import React, { useState, useEffect, useRef, useCallback } from 'react';
import JSZip from 'jszip';
import Papa from 'papaparse';
import AceEditor from 'react-ace';

// Import ace modes and themes
import 'ace-builds/src-noconflict/mode-json';
import 'ace-builds/src-noconflict/theme-monokai';
import 'ace-builds/src-noconflict/ext-language_tools';
import ace from 'ace-builds/src-noconflict/ace';

import '../styles/SeoAudit.css';

// Configure ace to prevent worker loading issues
ace.config.set('useWorker', false);
ace.config.set('loadWorkerFromBlob', false);

// Static industry requirements - moved outside component to prevent re-renders
const industryRequirements = {
  'Law Firms': [
    'Homepage', 'About Us', 'Contact Us', 'Case Results/Testimonials/Reviews',
    'Privacy Policy & Disclaimer', 'Terms of Service', 'Blog Archive (Landing/Category Page)',
    'Blog Single (Individual Posts)', 'Practice Areas Landing (All Practice Areas)',
    'Practice Area Pages (Individual)', 'Attorney Profiles Directory',
    'Attorney Profile Pages (Individual)', 'Service Areas Landing (All Locations)',
    'Location Pages (City/Neighborhood Specific)', 'FAQs (General)',
    'FAQ Pages (Practice Area Specific)', 'Free Consultation Landing Page',
    'Resources/Legal Guides'
  ],
  'Plumbing': [
    'Homepage', 'About Us', 'Contact Us', 'Testimonials/Reviews',
    'Privacy Policy & Disclaimer', 'Terms of Service', 'Blog Archive (Landing/Category Page)',
    'Blog Single (Individual Posts)', 'Services Landing (All Services)',
    'Service Pages (Individual)', 'Service Areas Landing (All Locations)',
    'Location Pages (City/Neighborhood Specific)', 'Emergency Services',
    'Technicians Directory', 'Individual Technician Bio Pages', 'Warranty Information',
    'Financing Options', 'Insurance/Claims Assistance', 'Maintenance Plans',
    'Free Estimate Landing Page', 'Coupons/Promotions', 'FAQ Page'
  ],
  'Roofing': [
    'Homepage', 'About Us', 'Contact Us', 'Testimonials/Reviews',
    'Privacy Policy & Disclaimer', 'Terms of Service', 'Blog Archive (Landing/Category Page)',
    'Blog Single (Individual Posts)', 'Services Landing (All Services)',
    'Service Pages (Individual)', 'Service Areas Landing (All Locations)',
    'Location Pages (City/Neighborhood Specific)', 'Emergency/Storm Damage Services',
    'Roofing Materials Guide', 'Gallery/Portfolio', 'Technicians Directory',
    'Individual Technician Bio Pages', 'Insurance Claims Assistance', 'Financing Options',
    'Warranty Information', 'Free Inspection Landing Page', 'Commercial Services',
    'Residential Services', 'FAQ Page'
  ],
  'HVAC': [
    'Homepage', 'About Us', 'Contact Us', 'Testimonials/Reviews',
    'Privacy Policy & Disclaimer', 'Terms of Service', 'Blog Archive (Landing/Category Page)',
    'Blog Single (Individual Posts)', 'Services Landing (All Services)',
    'Service Pages (Individual)', 'Service Areas Landing (All Locations)',
    'Location Pages (City/Neighborhood Specific)', 'Emergency Services',
    'Maintenance Plans/Service Agreements', 'Indoor Air Quality', 'Energy Efficiency/Savings',
    'Technicians Directory', 'Individual Technician Bio Pages', 'Financing Options',
    'Warranty Information', 'Rebates & Tax Credits', 'Free Estimate Landing Page',
    'Commercial Services', 'Residential Services', 'Brands We Service', 'FAQ Page'
  ],
  'Electricians': [
    'Homepage', 'About Us', 'Contact Us', 'Testimonials/Reviews',
    'Privacy Policy & Disclaimer', 'Terms of Service', 'Blog Archive (Landing/Category Page)',
    'Blog Single (Individual Posts)', 'Services Landing (All Services)',
    'Service Pages (Individual)', 'Service Areas Landing (All Locations)',
    'Location Pages (City/Neighborhood Specific)', 'Emergency Services',
    'Electrical Safety Inspections', 'EV Charger Installation', 'Generator Installation',
    'Smart Home Services', 'Technicians Directory', 'Individual Technician Bio Pages',
    'Maintenance Plans', 'Financing Options', 'Warranty Information',
    'Free Estimate Landing Page', 'Commercial Services', 'Residential Services',
    'Electrical Code Compliance', 'FAQ Page'
  ],
  'Pest Control': [
    'Homepage', 'About Us', 'Contact Us', 'Testimonials/Reviews',
    'Privacy Policy & Disclaimer', 'Terms of Service', 'Blog Archive (Landing/Category Page)',
    'Blog Single (Individual Posts)', 'Services Landing (All Services)',
    'Service Pages (Individual Pest Types)', 'Service Areas Landing (All Locations)',
    'Location Pages (City/Neighborhood Specific)', 'Emergency Services',
    'Pest Library/Identification Guide', 'Residential Services', 'Commercial Services',
    'Technicians Directory', 'Individual Technician Bio Pages', 'Treatment Plans/Packages',
    'Eco-Friendly Options', 'Warranty/Guarantee Information', 'Free Inspection Landing Page',
    'Seasonal Pest Guides', 'Safety & Preparation Guidelines', 'FAQ Page'
  ],
  'Landscaping': [
    'Homepage', 'About Us', 'Contact Us', 'Testimonials/Reviews',
    'Privacy Policy & Disclaimer', 'Terms of Service', 'Blog Archive (Landing/Category Page)',
    'Blog Single (Individual Posts)', 'Services Landing (All Services)',
    'Service Pages (Individual)', 'Service Areas Landing (All Locations)',
    'Location Pages (City/Neighborhood Specific)', 'Portfolio/Gallery', 'Design Process',
    'Maintenance Programs', 'Seasonal Services', 'Team Directory',
    'Individual Team Member Bio Pages', 'Plant Care Guides', 'Hardscaping Services',
    'Softscaping Services', 'Irrigation Services', 'Free Consultation Landing Page',
    'Commercial Services', 'Residential Services', 'FAQ Page'
  ],
  'Dentistry': [
    'Homepage', 'About Us', 'Contact Us', 'Patient Testimonials/Reviews',
    'Privacy Policy (HIPAA)', 'Terms of Service', 'Blog Archive (Landing/Category Page)',
    'Blog Single (Individual Posts)', 'Services Landing (All Services)',
    'Service Pages (Individual Procedures)', 'Location Pages (Multiple Offices)',
    'New Patients', 'Patient Forms', 'Insurance & Financing', 'Payment Options',
    'Dental Team Directory', 'Individual Dentist/Staff Bio Pages', 'Technology & Equipment',
    'Before & After Gallery', 'Emergency Dental Care', 'Appointment Request',
    'Patient Education/Resources', 'Smile Gallery', 'FAQ Page'
  ],
  'Beauty & Cosmetic School': [
    'Homepage', 'About Us', 'Contact Us', 'Testimonials/Reviews',
    'Privacy Policy & Disclaimer', 'Terms of Service', 'Blog Archive (Landing/Category Page)',
    'Blog Single (Individual Posts)', 'Programs Landing (All Programs)',
    'Program Pages (Individual)', 'Location Pages (Multiple Campuses)',
    'Admissions', 'Financial Aid & Scholarships', 'Tuition & Costs',
    'Faculty Directory', 'Individual Faculty Bio Pages', 'Student Services',
    'Career Services/Job Placement', 'Student Gallery/Portfolio', 'Campus Tours',
    'Request Information Landing Page', 'Apply Now Landing Page', 'Events & Workshops',
    'Continuing Education', 'Alumni Success Stories', 'FAQ Page'
  ],
  'Doors & Windows': [
    'Homepage', 'About Us', 'Contact Us', 'Testimonials/Reviews',
    'Privacy Policy & Disclaimer', 'Terms of Service', 'Blog Archive (Landing/Category Page)',
    'Blog Single (Individual Posts)', 'Services Landing (All Services)',
    'Service Pages (Individual)', 'Service Areas Landing (All Locations)',
    'Location Pages (City/Neighborhood Specific)', 'Products Landing (All Products)',
    'Product Pages (Individual)', 'Gallery/Portfolio', 'Residential Services',
    'Commercial Services', 'Emergency Services', 'Installation Services',
    'Repair & Maintenance Services', 'Financing Options', 'Warranty Information',
    'Free Estimate Landing Page', 'Energy Efficiency Information',
    'Brands We Carry', 'FAQ Page'
  ]
};

// Configuration object
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://admin-portal-api.presshive.cloud/api';

const config = {
  apiBaseUrl: `${API_BASE_URL}/screaming-frog`,
  defaultToken: '8d649d0c6924145cc5653590d7207c616947d2c9',
  clientId: '10',
  pollInterval: 5000,
  maxPollingAttempts: 120,
};

const SeoAudit = () => {
  // State management
  const [siteUrl, setSiteUrl] = useState('');
  const [industry, setIndustry] = useState('');
  const [selectedModel, setSelectedModel] = useState('claude-sonnet-4-20250514');
  const [availableModels, setAvailableModels] = useState([]);
  const [showModelModal, setShowModelModal] = useState(false);
  const [modalAction, setModalAction] = useState(null);
  const [status, setStatus] = useState({ type: '', message: 'Ready' });
  const [progress, setProgress] = useState({ show: false, percent: 0, message: '' });
  const [activeSection, setActiveSection] = useState('overview');
  const [lastUpdate, setLastUpdate] = useState('No scans yet');
  
  // Data states
  const [pages, setPages] = useState({});
  const [classification, setClassification] = useState({});
  const [fileStatus, setFileStatus] = useState({});
  const [currentExecution, setCurrentExecution] = useState(null);
  const [classificationMethod, setClassificationMethod] = useState('');
  const [crawlStats, setCrawlStats] = useState({});
  
  // Previous executions
  const [previousExecutions, setPreviousExecutions] = useState([]);
  const [selectedExecution, setSelectedExecution] = useState('');
  const [loadingExecutions, setLoadingExecutions] = useState(false);
  
  // Screenshots state
  const [screenshotStatus, setScreenshotStatus] = useState({});
  const [existingScreenshots, setExistingScreenshots] = useState([]);
  const [loadingScreenshots, setLoadingScreenshots] = useState(false);
  const [selectedUrls, setSelectedUrls] = useState({}); // For multi-page classifications
  
  // AI Analysis state
  const [isAiAnalyzing, setIsAiAnalyzing] = useState(false);
  
  // Industry field state
  const [industryLocked, setIndustryLocked] = useState(false);
  
  // Benchmark state
  const [benchmarkExecutions, setBenchmarkExecutions] = useState([]);
  const [selectedBenchmarkExecution, setSelectedBenchmarkExecution] = useState('');
  const [loadingBenchmarkExecutions, setLoadingBenchmarkExecutions] = useState(false);
  const [benchmarkClassifications, setBenchmarkClassifications] = useState({});
  const [loadingBenchmarkClassifications, setLoadingBenchmarkClassifications] = useState(false);
  const [benchmarkComparisons, setBenchmarkComparisons] = useState({});
  const [loadingBenchmarkComparisons, setLoadingBenchmarkComparisons] = useState(false);
  
  // URL Recommendations
  const [urlRecommendations, setUrlRecommendations] = useState([]);
  const [showUrlRecommendations, setShowUrlRecommendations] = useState(false);
  const [loadingUrlRecommendations, setLoadingUrlRecommendations] = useState(false);
  
  // Prompt Management
  const [promptTemplates, setPromptTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [loadingPrompts, setLoadingPrompts] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(false);
  const [editingConfig, setEditingConfig] = useState(false);
  
  // Schema Audit
  const [schemaAuditData, setSchemaAuditData] = useState([]);
  const [schemaAuditResults, setSchemaAuditResults] = useState({});
  const [loadingSchemaAudits, setLoadingSchemaAudits] = useState({});
  const [tempTemplateContent, setTempTemplateContent] = useState('');
  const [tempConfigData, setTempConfigData] = useState('');
  
  // Manual URL editing
  const [editingCard, setEditingCard] = useState(null);
  const [editUrlInputs, setEditUrlInputs] = useState({});
  const [editingUrl, setEditingUrl] = useState(null);
  const [deletingCard, setDeletingCard] = useState(null);
  
  // Refs for polling
  const pollingAttempts = useRef(0);
  const pollingTimer = useRef(null);
  
  const getAuthToken = useCallback(() => {
    return config.defaultToken;
  }, []);

  // Fetch available models from API
  const fetchAvailableModels = useCallback(async () => {
    // Don't fetch if models are already loaded
    if (availableModels.length > 0) {
      return;
    }

    try {
      console.log('Fetching available models from API...');
      const response = await fetch(`${API_BASE_URL}/claude/models/`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${getAuthToken()}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Successfully fetched models:', data);
        setAvailableModels(data.available_models || []);
        
        // Return the data so we can handle default model setting outside
        return data;
      } else {
        console.warn('❌ API response not OK:', response.status, response.statusText);
        console.warn('Using fallback hardcoded models');
        // Fallback to hardcoded models
        setAvailableModels([
          {
            id: "claude-sonnet-4-20250514",
            name: "Claude Sonnet 4",
            description: "Fast, reliable model for most tasks",
            recommended_for: ["classification", "analysis"],
            cost_tier: "standard"
          },
          {
            id: "claude-opus-4-1-20250805", 
            name: "Claude Opus 4",
            description: "Most capable model for complex reasoning",
            recommended_for: ["complex_analysis", "detailed_reasoning"],
            cost_tier: "premium"
          },
          {
            id: "claude-haiku-4-20250514",
            name: "Claude Haiku 4",
            description: "Fastest, most cost-effective model",
            recommended_for: ["quick_analysis", "batch_processing"],
            cost_tier: "budget"
          },
          {
            id: "claude-sonnet-4-5-20250514",
            name: "Claude Sonnet 4.5",
            description: "Enhanced version with improved capabilities",
            recommended_for: ["classification", "analysis", "content_generation"],
            cost_tier: "standard"
          }
        ]);
        return null; // No default model data from API
      }
    } catch (error) {
      console.error('❌ Error fetching available models:', error);
      console.warn('Using fallback hardcoded models');
      // Fallback to hardcoded models
      setAvailableModels([
        {
          id: "claude-sonnet-4-20250514",
          name: "Claude Sonnet 4", 
          description: "Fast, reliable model for most tasks",
          recommended_for: ["classification", "analysis"],
          cost_tier: "standard"
        },
        {
          id: "claude-opus-4-1-20250805",
          name: "Claude Opus 4", 
          description: "Most capable model for complex reasoning",
          recommended_for: ["complex_analysis", "detailed_reasoning"],
          cost_tier: "premium"
        },
        {
          id: "claude-haiku-4-20250514",
          name: "Claude Haiku 4",
          description: "Fastest, most cost-effective model",
          recommended_for: ["quick_analysis", "batch_processing"],
          cost_tier: "budget"
        },
        {
          id: "claude-sonnet-4-5-20250514",
          name: "Claude Sonnet 4.5",
          description: "Enhanced version with improved capabilities",
          recommended_for: ["classification", "analysis", "content_generation"],
          cost_tier: "standard"
        }
      ]);
      return null; // No default model data from API
    }
  }, [availableModels.length, getAuthToken]);

  // Load from localStorage on component mount
  useEffect(() => {
    loadFromLocalStorage();
    
    // Load saved model selection
    const savedModel = localStorage.getItem('selectedClaudeModel');
    if (savedModel) {
      setSelectedModel(savedModel);
    }
    

    // Check and clean localStorage data
    try {
      const saved = localStorage.getItem('seoAuditData');
      if (saved) {
        const parsed = JSON.parse(saved);
        // Since we no longer save large datasets to localStorage, 
        // just ensure basic structure is maintained
        console.log('✅ Found localStorage data, validating structure');
        
        // If we have legacy large data saved, clear it to prevent future quota issues
        if (parsed.pages || parsed.classification || parsed.benchmarkExecutions) {
          console.log('🧹 Clearing legacy large data from localStorage to prevent quota issues');
          const cleanedData = {
            siteUrl: parsed.siteUrl || '',
            industry: parsed.industry || '',
            industryLocked: parsed.industryLocked || false,
            selectedExecution: parsed.selectedExecution || '',
            classificationMethod: parsed.classificationMethod || '',
            currentExecution: parsed.currentExecution ? {
              executionId: parsed.currentExecution.executionId,
              url: parsed.currentExecution.url,
              status: parsed.currentExecution.status
            } : null
          };
          localStorage.setItem('seoAuditData', JSON.stringify(cleanedData));
          console.log('✅ Updated localStorage with cleaned structure');
        }
      }
    } catch (error) {
      console.error('❌ Error checking localStorage:', error.message);
      localStorage.removeItem('seoAuditData');
    }
  }, []);

  // Load models on component mount (only once)
  useEffect(() => {
    const loadModels = async () => {
      const data = await fetchAvailableModels();
      
      // Set default model if none selected and we got data from API
      const currentSelectedModel = localStorage.getItem('selectedClaudeModel');
      if (!currentSelectedModel && data && data.default_model) {
        setSelectedModel(data.default_model);
      }
    };
    
    loadModels();
  }, [fetchAvailableModels]); // Only depends on fetchAvailableModels

  // Fetch URL recommendations from previous executions
  const fetchUrlRecommendations = async () => {
    if (loadingUrlRecommendations) return;
    
    setLoadingUrlRecommendations(true);
    
    try {
      console.log('🔍 Fetching URL recommendations from previous executions');
      
      const response = await fetch(`${API_BASE_URL}/clients/10/`, {
        method: 'GET',
        headers: {
          'accept': '*/*',
          'authorization': 'Token 8d649d0c6924145cc5653590d7207c616947d2c9',
          'content-type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('📥 Client data received:', data);

      if (data.screen_frog_executions && data.screen_frog_executions.length > 0) {
        // Extract unique URLs from executions
        const uniqueUrls = [...new Set(
          data.screen_frog_executions
            .filter(execution => execution.url && execution.status === 'Completed')
            .map(execution => execution.url)
        )];
        
        console.log(`🌐 Found ${uniqueUrls.length} unique URLs from ${data.screen_frog_executions.length} executions`);
        setUrlRecommendations(uniqueUrls);
      } else {
        console.log('ℹ️ No executions found');
        setUrlRecommendations([]);
      }

    } catch (error) {
      console.error('❌ Error fetching URL recommendations:', error);
      setUrlRecommendations([]);
    } finally {
      setLoadingUrlRecommendations(false);
    }
  };

  // Clear stale data function
  const clearStaleData = () => {
    setPages({});
    setClassification({});
    setFileStatus({});
    setCurrentExecution(null);
    setClassificationMethod('');
    setCrawlStats({});
    setScreenshotStatus({});
    setExistingScreenshots([]);
    setSelectedUrls({});
    setIsAiAnalyzing(false);
    setIndustryLocked(false); // Unlock industry field when clearing data
    setBenchmarkExecutions([]);
    setSelectedBenchmarkExecution('');
    setBenchmarkClassifications({});
    setBenchmarkComparisons({});
    
    // URL recommendations
    setUrlRecommendations([]);
    setShowUrlRecommendations(false);
    
    // Prompt management
    setPromptTemplates([]);
    setSelectedTemplate(null);
    setSelectedConfig(null);
    setEditingTemplate(false);
    setEditingConfig(false);
    setTempTemplateContent('');
    setTempConfigData('');
    
    setLastUpdate('No scans yet');
  };

  // Close URL recommendations when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showUrlRecommendations && !event.target.closest('.seo-input-group')) {
        setShowUrlRecommendations(false);
      }
    };

    if (showUrlRecommendations) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showUrlRecommendations]);

  // Save to localStorage when relevant data changes (with quota management)
  useEffect(() => {
    const saveToLocalStorage = () => {
      try {
        // Only save essential data to prevent quota exceeded errors
        const saveData = {
          siteUrl,
          industry,
          industryLocked,
          currentExecution: currentExecution ? {
            executionId: currentExecution.executionId,
            url: currentExecution.url,
            status: currentExecution.status
          } : null,
          selectedExecution,
          classificationMethod,
          // Don't save large datasets like pages, classification, benchmarkExecutions, etc.
          // These will be refetched when needed
        };
        
        const dataString = JSON.stringify(saveData);
        
        // Check approximate size (rough estimate)
        if (dataString.length > 1000000) { // ~1MB limit as safety
          console.warn('⚠️ Data too large for localStorage, saving minimal data only');
          const minimalSaveData = {
            siteUrl,
            industry,
            industryLocked,
            selectedExecution
          };
          localStorage.setItem('seoAuditData', JSON.stringify(minimalSaveData));
        } else {
          localStorage.setItem('seoAuditData', dataString);
        }
        
      } catch (error) {
        console.error('❌ Failed to save to localStorage:', error.message);
        
        if (error.name === 'QuotaExceededError') {
          console.warn('🧹 localStorage quota exceeded, clearing and saving minimal data');
          
          // Clear existing data
          localStorage.removeItem('seoAuditData');
          
          // Save only critical data
          try {
            const criticalData = {
              siteUrl,
              industry,
              industryLocked,
              selectedExecution
            };
            localStorage.setItem('seoAuditData', JSON.stringify(criticalData));
            console.log('✅ Saved critical data to localStorage after quota exceeded');
          } catch (secondError) {
            console.error('❌ Failed to save even critical data:', secondError.message);
            // Clear localStorage completely if even critical data fails
            localStorage.clear();
          }
        }
      }
    };

    if (siteUrl || industry || currentExecution || Object.keys(pages).length > 0) {
      saveToLocalStorage();
    }
  }, [siteUrl, industry, currentExecution, pages, classification, classificationMethod, previousExecutions, selectedExecution, screenshotStatus, selectedUrls, industryLocked, benchmarkExecutions, selectedBenchmarkExecution, benchmarkClassifications, benchmarkComparisons]);

  // Save model selection effect
  useEffect(() => {
    if (selectedModel) {
      localStorage.setItem('selectedClaudeModel', selectedModel);
    }
  }, [selectedModel]);


  // Fetch previous executions when URL changes
  useEffect(() => {
    if (siteUrl && siteUrl.length > 10) { // Basic URL validation
      fetchPreviousExecutions(siteUrl);
    } else {
      setPreviousExecutions([]);
      setSelectedExecution('');
      setIndustryLocked(false); // Unlock when clearing executions
    }
  }, [siteUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle execution selection changes
  useEffect(() => {
    if (selectedExecution === 'new' || selectedExecution === '') {
      // Unlock industry field when selecting "Trigger New Scan" or clearing selection
      setIndustryLocked(false);
    } else if (selectedExecution && previousExecutions.length > 0) {
      // Set industry from selected execution and lock field
      const execution = previousExecutions.find(ex => ex.id.toString() === selectedExecution);
      if (execution && execution.industry_type) {
        console.log(`Setting industry from selected execution: ${execution.industry_type}`);
        setIndustry(execution.industry_type);
        setIndustryLocked(true);
      }
    }
  }, [selectedExecution, previousExecutions]);

  // Check screenshots when execution and pages are available
  useEffect(() => {
    if (currentExecution?.executionId && Object.keys(pages).length > 0 && Object.keys(classification).length > 0) {
      console.log('Auto-checking existing screenshots for execution:', currentExecution.executionId);
      checkExistingScreenshots();
    }
  }, [currentExecution?.executionId, pages, classification]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load benchmark executions when switching to benchmark tab and having industry + execution
  useEffect(() => {
    if (activeSection === 'benchmark' && industry && currentExecution?.executionId) {
      loadBenchmarkExecutions(industry);
    }
  }, [activeSection, industry, currentExecution?.executionId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load benchmark classifications when benchmark execution is selected
  useEffect(() => {
    if (selectedBenchmarkExecution) {
      loadBenchmarkClassifications(selectedBenchmarkExecution);
    }
  }, [selectedBenchmarkExecution]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateStatus = (type, message) => {
    setStatus({ type, message });
  };

  const showProgress = (show) => {
    setProgress(prev => ({ ...prev, show }));
  };

  const updateProgress = (percent, message) => {
    console.log(`Progress Update: ${percent}% - ${message}`);
    setProgress({ show: true, percent, message });
  };

  const fetchPreviousExecutions = async (url) => {
    setLoadingExecutions(true);
    try {
      const response = await fetch(`${API_BASE_URL}/clients/10/`, {
        method: 'GET',
        headers: {
          'Authorization': `Token ${getAuthToken()}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      // Filter executions that match the URL and are completed
      const matchingExecutions = data.screen_frog_executions?.filter(execution => {
        // Normalize URLs for comparison (remove trailing slashes, etc.)
        const normalizeUrl = (u) => u.replace(/\/+$/, '').toLowerCase();
        return normalizeUrl(execution.url) === normalizeUrl(url) && 
               execution.status === 'Completed';
      }) || [];

      // Sort by execution date (newest first)
      matchingExecutions.sort((a, b) => new Date(b.execution_date) - new Date(a.execution_date));
      
      setPreviousExecutions(matchingExecutions);
      setSelectedExecution(''); // Reset selection when URL changes
      
      console.log(`Found ${matchingExecutions.length} previous executions for ${url}`);
      
    } catch (error) {
      console.error('Error fetching previous executions:', error);
      setPreviousExecutions([]);
      setSelectedExecution('');
    } finally {
      setLoadingExecutions(false);
    }
  };

  const triggerScan = async () => {
    if (!siteUrl || !industry) {
      alert('Please fill in all required fields: Website URL and Industry');
      return;
    }

    console.log('=== TRIGGER SCAN ===');
    console.log('Site URL:', siteUrl);
    console.log('Industry:', industry);
    console.log('Industry Locked:', industryLocked);
    console.log('Selected Execution:', selectedExecution);

    // If a previous execution is selected, use that instead of triggering new scan
    if (selectedExecution && selectedExecution !== 'new') {
      const execution = previousExecutions.find(ex => ex.id.toString() === selectedExecution);
      if (execution) {
        console.log('Using previous execution:', execution);
        
        // Lock industry field when loading previous scan
        setIndustryLocked(true);
        
        updateStatus('processing', 'Loading previous scan results...');
        showProgress(true);
        updateProgress(10, 'Loading previous execution...');
        
        // Set industry from execution if available
        if (execution.industry_type) {
          console.log(`Setting industry from execution: ${execution.industry_type}`);
          setIndustry(execution.industry_type);
          setIndustryLocked(true);
        }

        // Set current execution data IMMEDIATELY
        const executionData = {
          executionId: execution.id.toString(),
          executionArn: '', // Not available for previous executions
          siteUrl: siteUrl,
          industry: execution.industry_type || industry,
          startTime: execution.execution_date,
          s3Path: execution.s3_path,
          completedAt: execution.execution_date
        };
        
        console.log('Setting current execution for previous scan:', executionData);
        setCurrentExecution(executionData);
        updateProgress(50, 'Downloading previous results...');
        
        // Directly download and process the results with execution data
        downloadResultsWithExecution(execution.s3_path, executionData);
        return;
      }
    }

    console.log('Starting new scan for:', siteUrl, 'Industry:', industry);
    console.log('Sending industry_type to backend:', industry);
    
    // Clear any previous execution state for new scan
    setCurrentExecution(null);
    setSelectedExecution('new');
    setPages({});
    setClassification({});
    setFileStatus({});
    setScreenshotStatus({});
    setExistingScreenshots([]);
    
    // Lock industry field during scan
    setIndustryLocked(true);
    
    updateStatus('processing', 'Triggering scan...');
    showProgress(true);
    updateProgress(5, 'Initiating scan...');

    try {
      const response = await fetch(`${config.apiBaseUrl}/trigger_scan/`, {
        method: 'POST',
        headers: {
          'Authorization': `Token ${getAuthToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          client_id: config.clientId,
          url: siteUrl,
          industry_type: industry // Send industry type to backend
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      const execution = {
        executionId: data.execution_id.toString(), // Ensure string format for consistency
        executionArn: data.step_functions_execution_arn,
        siteUrl: siteUrl,
        industry: industry,
        startTime: new Date().toISOString()
      };

      console.log('NEW SCAN EXECUTION CREATED:', execution);
      setCurrentExecution(execution);
      updateProgress(10, 'Scan initiated successfully');
      
      // Start polling immediately with execution data
      startPollingWithExecution(execution);

    } catch (error) {
      console.error('Error triggering scan:', error);
      updateStatus('error', `Failed to trigger scan: ${error.message}`);
      showProgress(false);
      setIndustryLocked(false); // Unlock on error
    }
  };


  const startPollingWithExecution = (execution) => {
    console.log('Starting polling with execution:', execution);
    pollingAttempts.current = 0;
    pollStatusWithExecution(execution);
  };


  const pollStatusWithExecution = async (execution) => {
    if (!execution) {
      console.log('No execution data, stopping polling');
      return;
    }

    pollingAttempts.current++;
    console.log(`Polling attempt ${pollingAttempts.current} for execution:`, execution.executionId);
    
    const progressPercent = Math.min(10 + (pollingAttempts.current * 2), 90);
    updateProgress(progressPercent, `Checking status... (Attempt ${pollingAttempts.current})`);

    if (pollingAttempts.current > config.maxPollingAttempts) {
      updateStatus('error', 'Scan timeout - please check status manually');
      showProgress(false);
      return;
    }

    try {
      const response = await fetch(`${config.apiBaseUrl}/check_status/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${getAuthToken()}`
        },
        body: JSON.stringify({
          execution_id: execution.executionId,
          step_functions_execution_arn: execution.executionArn
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Poll status response:', data);
      
      if (data.is_complete === true || data.step_functions_status === 'SUCCEEDED') {
        updateProgress(95, 'Scan complete! Downloading results...');
        const updatedExecution = {
          ...execution,
          s3Path: data.s3_path,
          completedAt: data.stopped_at
        };
        console.log('SCAN COMPLETED - UPDATED EXECUTION:', updatedExecution);
        setCurrentExecution(updatedExecution);
        downloadResultsWithExecution(data.s3_path, updatedExecution);
      } else if (data.step_functions_status === 'FAILED') {
        updateStatus('error', 'Scan failed');
        showProgress(false);
      } else {
        console.log(`Scan still running (status: ${data.step_functions_status}), continuing to poll...`);
        pollingTimer.current = setTimeout(() => pollStatusWithExecution(execution), config.pollInterval);
      }

    } catch (error) {
      console.error('Error checking status:', error);
      console.log('Retrying in 5 seconds...');
      pollingTimer.current = setTimeout(() => pollStatusWithExecution(execution), config.pollInterval);
    }
  };


  const downloadResultsWithExecution = async (s3Path, executionData) => {
    console.log('downloadResultsWithExecution called with execution:', executionData);
    
    try {
      const encodedS3Path = encodeURIComponent(s3Path);
      
      const response = await fetch(
        `${API_BASE_URL}/screaming-frog-executions/download_results/?s3_path=${encodedS3Path}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Token ${getAuthToken()}`
          }
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.download_url) {
        updateProgress(98, 'Downloading results.zip...');
        
        const updatedExecution = {
          ...executionData,
          downloadUrl: data.download_url
        };
        console.log('DOWNLOAD EXECUTION UPDATE:', updatedExecution);
        setCurrentExecution(updatedExecution);
        
        try {
          const downloadResponse = await fetch(data.download_url, { mode: 'cors' });
          
          if (downloadResponse.ok) {
            const blob = await downloadResponse.blob();
            await processZipFileWithExecution(blob, updatedExecution);
            offerDownload(blob, 'screaming_frog_results.zip');
          } else {
            throw new Error('Direct download failed');
          }
        } catch (corsError) {
          console.warn('Direct S3 download failed due to CORS, offering manual download...');
          handleManualDownload(data.download_url);
        }
      } else {
        throw new Error('No download URL in response');
      }
    } catch (error) {
      console.error('Error downloading results:', error);
      updateStatus('error', `Download failed - manual download may be required`);
      showProgress(false);
    }
  };

  const handleManualDownload = (downloadUrl) => {
    updateStatus('warning', 'Automatic download blocked by CORS - Please download manually');
    showProgress(false);
    
    // Store download URL for manual access
    const updatedExecution = {
      ...currentExecution,
      downloadUrl: downloadUrl
    };
    setCurrentExecution(updatedExecution);
  };


  const processZipFileWithExecution = async (blob, executionData) => {
    console.log('processZipFileWithExecution called with execution:', executionData);
    console.log('Current execution state at ZIP processing:', currentExecution);
    updateProgress(99, 'Extracting CSV files from ZIP...');
    
    try {
      // Reset file status and data
      const newFileStatus = {};
      
      // Load ZIP file using JSZip
      const zip = await JSZip.loadAsync(blob);
      
      // Expected CSV files
      const expectedFiles = [
        'page_titles_all.csv',
        'meta_description_all.csv',
        'custom_extraction_all.csv',
        'structured_data_all.csv',
        'crawl_overview.csv'
      ];
      
      let extractedCount = 0;
      const totalFiles = expectedFiles.length;
      const newPages = {};
      
      // Extract and parse each CSV file
      for (const filename of expectedFiles) {
        try {
          const file = zip.file(filename);
          if (file) {
            const content = await file.async('string');
            newFileStatus[filename] = 'success';
            extractedCount++;
            
            // Parse and process the CSV data
            await parseCSVFile(filename, content, newPages);
          } else {
            newFileStatus[filename] = 'missing';
            console.warn(`File not found in ZIP: ${filename}`);
            
            // Special handling for custom_extraction_all.csv
            if (filename === 'custom_extraction_all.csv') {
              console.warn('⚠️ custom_extraction_all.csv missing - Schema Audit will not be available');
            }
          }
        } catch (error) {
          newFileStatus[filename] = 'error';
          console.error(`Error extracting ${filename}:`, error);
        }
        
        const progressPercent = 99 + (extractedCount / totalFiles);
        updateProgress(progressPercent, `Processing ${filename}...`);
      }
      
      // Update state
      setFileStatus(newFileStatus);
      
      // Check if we have minimum required files
      if (newFileStatus['page_titles_all.csv'] === 'success') {
        // Check if we actually have page data
        if (Object.keys(newPages).length === 0) {
          console.warn('CSV files parsed successfully but no page data found');
          updateStatus('warning', 'No pages found in scan results');
          setPages({});
          setClassification({});
          setTimeout(() => showProgress(false), 2000);
          return;
        }
        
        console.log(`Found ${Object.keys(newPages).length} pages in scan results`);
        
        // Proceed with analysis
        setPages(newPages);
        
        // For previous executions, try to load existing classifications first
        if (executionData?.executionId && executionData.completedAt) {
          console.log('Attempting to load existing classifications for execution:', executionData.executionId);
          const existingClassifications = await loadExistingClassifications(executionData.executionId);
          
          if (existingClassifications) {
            console.log('Using existing classifications from API');
            updateStatus('processing', 'Loading existing AI classifications...');
            
            // Apply existing classifications to pages
            applyExistingClassifications(newPages, existingClassifications, executionData);
            
            // Skip the auto-trigger since we have existing data
            console.log('Skipping auto-trigger for existing classifications');
            updateStatus('active', 'Existing AI classifications loaded successfully');
          } else {
            console.log('No existing classifications found, showing model selection for new analysis');
            // Show model selection modal before performing analysis
            showModelSelectionModal(async (modelToUse) => {
              console.log('🤖 Using selected model for new analysis:', modelToUse);
              await performFullAnalysisWithPages(newPages, executionData, modelToUse);
              setSelectedModel(modelToUse);
            });
            return; // Exit early to prevent automatic analysis
          }
        } else {
          // New scan - show model selection before analysis
          console.log('New scan - showing model selection for analysis');
          showModelSelectionModal(async (modelToUse) => {
            console.log('🤖 Using selected model for new scan:', modelToUse);
            await performFullAnalysisWithPages(newPages, executionData, modelToUse);
            setSelectedModel(modelToUse);
          });
          return; // Exit early to prevent automatic analysis
        }
        
        updateProgress(100, 'Analysis complete!');
        updateStatus('active', 'Analysis complete - Results processed');
        
        // Unlock industry field after successful analysis
        setIndustryLocked(false);
        
        // Show file status display
        displayFileStatusInUI(newFileStatus);
        
        // Only check screenshots if we didn't already load them from classifications
        if (!executionData?.completedAt) {
          setTimeout(() => {
            console.log('Checking screenshots with execution data:', executionData);
            checkExistingScreenshots(executionData);
          }, 1000);
        } else {
          console.log('Skipping screenshot check - already loaded from classifications endpoint');
        }
      } else {
        throw new Error('Critical file missing: page_titles_all.csv');
      }
      
      setLastUpdate(`Last scan: ${new Date().toLocaleString()}`);
      
      // Add download button
      addDownloadButtonToUI();
      
      setTimeout(() => showProgress(false), 2000);
      
    } catch (error) {
      console.error('Error processing ZIP file:', error);
      updateStatus('error', `Failed to process ZIP: ${error.message}`);
      setLastUpdate(`Last scan: ${new Date().toLocaleString()}`);
      setTimeout(() => showProgress(false), 2000);
    }
  };

  const parseCSVFile = async (filename, content, pagesData) => {
    const parsed = Papa.parse(content, {
      header: true,
      dynamicTyping: true,
      skipEmptyLines: true,
      delimitersToGuess: [',', '\t', '|', ';']
    });

    if (parsed.errors.length > 0) {
      console.warn(`Parse warnings for ${filename}:`, parsed.errors);
    }

    switch(filename) {
      case 'page_titles_all.csv':
        processTitlesData(parsed.data, pagesData);
        break;
      case 'meta_description_all.csv':
        processMetaData(parsed.data, pagesData);
        break;
      case 'custom_extraction_all.csv':
        processCustomData(parsed.data, pagesData);
        break;
      case 'structured_data_all.csv':
        processStructuredData(parsed.data, pagesData);
        break;
      case 'crawl_overview.csv':
        processCrawlOverviewData(parsed.data);
        break;
      default:
        console.warn(`Unknown CSV file: ${filename}`);
        break;
    }
  };

  const processTitlesData = (data, pagesData) => {
    data.forEach(row => {
      if (row.Address) {
        if (!pagesData[row.Address]) {
          pagesData[row.Address] = {};
        }
        pagesData[row.Address].url = row.Address;
        pagesData[row.Address].title = row['Title 1'] || row.Title || '';
        pagesData[row.Address].titleLength = row['Title 1 Length'] || row['Title Length'] || 0;
        pagesData[row.Address].titlePixelWidth = row['Title 1 Pixel Width'] || 0;
      }
    });
  };

  const processMetaData = (data, pagesData) => {
    data.forEach(row => {
      if (row.Address && pagesData[row.Address]) {
        pagesData[row.Address].metaDescription = row['Meta Description 1'] || row['Meta Description'] || '';
        pagesData[row.Address].metaLength = row['Meta Description 1 Length'] || row['Meta Description Length'] || 0;
        pagesData[row.Address].metaPixelWidth = row['Meta Description 1 Pixel Width'] || 0;
        pagesData[row.Address].indexability = row.Indexability;
        pagesData[row.Address].indexabilityStatus = row['Indexability Status'];
      }
    });
  };

  const processCustomData = (data, pagesData) => {
    const schemaAuditEntries = [];
    
    data.forEach(row => {
      if (row.Address && pagesData[row.Address]) {
        // Get body class - handle various possible column names
        const bodyClass = row['/html/body/@class 1'] || 
                        row['/html/body/@class'] || 
                        row['body class'] || 
                        row['Body Class'] || '';
        
        pagesData[row.Address].bodyClass = bodyClass;
        pagesData[row.Address].statusCode = row['Status Code'] || row.Status;
        
        // Parse LD+JSON data
        const ldJson = [];
        for (let i = 1; i <= 3; i++) {
          const jsonField = row[`application/ld+json ${i}`] || row[`ld+json ${i}`];
          if (jsonField) {
            try {
              ldJson.push(JSON.parse(jsonField));
            } catch(e) {
              ldJson.push(jsonField);
            }
          }
        }
        pagesData[row.Address].ldJson = ldJson;

        // Schema Audit Processing - Filter for Status Code 200 and Status OK
        const statusCode = row['Status Code'] || row.Status;
        const statusText = row['Status'] || '';
        
        if ((statusCode === 200 || statusCode === '200') && 
            (statusText === 'OK' || statusText === '' || !statusText)) {
          
          // Extract all application/ld+json columns dynamically
          const schemaOrgData = [];
          const columnNames = Object.keys(row);
          
          columnNames.forEach(columnName => {
            if (columnName.includes('application/ld+json') || columnName.includes('ld+json')) {
              const jsonContent = row[columnName];
              if (jsonContent && jsonContent.trim()) {
                try {
                  const parsed = JSON.parse(jsonContent);
                  // Check if it contains schema.org context (handle multiple formats)
                  let hasSchemaOrgContext = false;
                  
                  if (parsed['@context']) {
                    const context = parsed['@context'];
                    
                    // String format: "https://schema.org" or "https://schema.org/"
                    if (typeof context === 'string') {
                      hasSchemaOrgContext = context.includes('schema.org');
                    }
                    // Array format: ["https://schema.org", "other-context"]
                    else if (Array.isArray(context)) {
                      hasSchemaOrgContext = context.some(ctx => 
                        typeof ctx === 'string' && ctx.includes('schema.org')
                      );
                    }
                    // Object format: {"@vocab": "https://schema.org/"}
                    else if (typeof context === 'object') {
                      hasSchemaOrgContext = Object.values(context).some(value => 
                        typeof value === 'string' && value.includes('schema.org')
                      );
                    }
                  }
                  
                  if (hasSchemaOrgContext) {
                    schemaOrgData.push(parsed);
                  }
                } catch (e) {
                  console.warn(`Failed to parse JSON-LD for ${row.Address}:`, jsonContent);
                }
              }
            }
          });

          // If we found schema.org data, add to schema audit entries
          if (schemaOrgData.length > 0) {
            schemaAuditEntries.push({
              address: row.Address,
              schema_org: schemaOrgData
            });
          }
        }
        
        // Detect CMS from body class
        if (bodyClass) {
          if (bodyClass.includes('wordpress') || bodyClass.includes('wp-')) {
            pagesData[row.Address].cms = 'WordPress';
          } else if (bodyClass.includes('drupal')) {
            pagesData[row.Address].cms = 'Drupal';
          } else if (bodyClass.includes('joomla')) {
            pagesData[row.Address].cms = 'Joomla';
          } else if (bodyClass.includes('shopify')) {
            pagesData[row.Address].cms = 'Shopify';
          } else if (bodyClass.includes('wix')) {
            pagesData[row.Address].cms = 'Wix';
          }
        }
      }
    });
    
    // Set schema audit data after processing all rows
    console.log(`Found ${schemaAuditEntries.length} pages with schema.org JSON-LD data`);
    setSchemaAuditData(schemaAuditEntries);
  };

  const processStructuredData = (data, pagesData) => {
    data.forEach(row => {
      if (row.Address && pagesData[row.Address]) {
        const schemaTypes = [];
        const richFeatures = [];
        
        // Collect all schema types
        for (let i = 1; i <= 25; i++) {
          if (row[`Type-${i}`]) {
            schemaTypes.push(row[`Type-${i}`]);
          }
        }
        
        // Collect rich result features
        for (let i = 1; i <= 6; i++) {
          if (row[`Feature-${i}`]) {
            richFeatures.push(row[`Feature-${i}`]);
          }
        }
        
        pagesData[row.Address].schemaTypes = schemaTypes;
        pagesData[row.Address].richFeatures = richFeatures;
        pagesData[row.Address].schemaErrors = row.Errors || 0;
        pagesData[row.Address].schemaWarnings = row.Warnings || 0;
        pagesData[row.Address].richResultErrors = row['Rich Result Errors'] || 0;
        pagesData[row.Address].richResultWarnings = row['Rich Result Warnings'] || 0;
      }
    });
  };

  const processCrawlOverviewData = (data) => {
    // Extract summary statistics from crawl overview
    const siteUrl = Object.keys(data[0] || {}).find(key => key.startsWith('http'));
    if (siteUrl) {
      const newCrawlStats = {
        siteUrl: siteUrl,
        crawlDate: data.find(r => r['Site Crawled'] === 'Date')?.[siteUrl] || '',
        crawlTime: data.find(r => r['Site Crawled'] === 'Time')?.[siteUrl] || '',
        totalUrls: data.find(r => r['Site Crawled'] === 'Total URLs')?.[siteUrl] || 0,
        totalHtml: data.find(r => r['Site Crawled'] === 'HTML')?.[siteUrl] || 0,
        totalIndexable: data.find(r => r['Site Crawled'] === 'Indexable')?.[siteUrl] || 0,
        totalNonIndexable: data.find(r => r['Site Crawled'] === 'Non-Indexable')?.[siteUrl] || 0
      };
      setCrawlStats(newCrawlStats);
    }
  };

  const performFullAnalysisWithPages = async (pagesData, executionData = null, modelToUse = null) => {
    console.log('performFullAnalysisWithPages called with execution:', executionData);
    console.log('performFullAnalysisWithPages called with pages data:', pagesData);
    console.log('Pages data keys:', Object.keys(pagesData));
    console.log('Pages data type:', typeof pagesData);
    console.log('Pages data length:', Object.keys(pagesData).length);
    
    // Clear previous classification
    let newClassification = {};
    
    // Use AI classification
      setIsAiAnalyzing(true);
      updateStatus('processing', 'Classifying pages with AI...');
      try {
      newClassification = await classifyPagesWithClaude(pagesData, executionData, modelToUse);
        setClassificationMethod('AI-Powered (Claude)');
        updateStatus('active', 'AI classification complete');
      } catch (error) {
        console.warn('Claude API classification failed, using rule-based fallback:', error);
        newClassification = performRuleBasedClassification(pagesData);
      setClassificationMethod('Rule-based (Fallback)');
      updateStatus('warning', 'Using rule-based classification (AI failed)');
      } finally {
        setIsAiAnalyzing(false);
    }
    
    // Update state
    setClassification(newClassification);
    
    // Auto-trigger screenshots for single-page classifications only for new classifications
    // (Skip if we loaded existing classifications that already have screenshots)
    setTimeout(() => {
      const execToUse = executionData || currentExecution;
      console.log('Auto-triggering screenshots with execution:', execToUse);
      
      // Only auto-trigger for truly new classifications (not when loading existing ones)
      autoTriggerSinglePageScreenshots(newClassification, execToUse);
    }, 2000);
  };

  const refreshClassifications = async () => {
    console.log('=== MANUAL REFRESH CLASSIFICATIONS CLICKED ===');
    
    // Determine which execution to use
    let executionToUse = currentExecution;
    if (!executionToUse?.executionId && selectedExecution && selectedExecution !== 'new') {
      const execution = previousExecutions.find(ex => ex.id.toString() === selectedExecution);
      if (execution) {
        executionToUse = {
          executionId: execution.id.toString(),
          executionArn: '',
          siteUrl: siteUrl,
          industry: industry,
          startTime: execution.execution_date,
          s3Path: execution.s3_path,
          completedAt: execution.execution_date
        };
      }
    }

    if (!executionToUse?.executionId) {
      alert('No execution ID available. Please run a scan first.');
      return;
    }

    console.log('Refreshing classifications for execution:', executionToUse.executionId);
    
    try {
      updateStatus('processing', 'Refreshing classifications...');
      
      // Load existing classifications from API
      const existingClassifications = await loadExistingClassifications(executionToUse.executionId);
      
      if (existingClassifications) {
        console.log('Successfully loaded classifications, applying to pages...');
        // Apply existing classifications to current pages
        applyExistingClassifications(pages, existingClassifications, executionToUse);
        updateStatus('active', 'Classifications refreshed successfully');
      } else {
        console.log('No classifications found for this execution, showing model selection');
        updateStatus('warning', 'No classifications found - running new classification');
        
        // Show model selection modal for new classification
        showModelSelectionModal(async (modelToUse) => {
          console.log('🤖 Using selected model for refresh classification:', modelToUse);
          await performFullAnalysisWithPages(pages, executionToUse, modelToUse);
          setSelectedModel(modelToUse);
        });
        return; // Exit early to prevent further processing
      }
      
    } catch (error) {
      console.error('Error refreshing classifications:', error);
      updateStatus('error', `Failed to refresh classifications: ${error.message}`);
      alert('Failed to refresh classifications. Please try again.');
    }
  };

  const updateUrlClassification = async (oldUrl, newUrl, classificationType) => {
    console.log('=== UPDATE URL CLASSIFICATION ===');
    console.log('Old URL:', oldUrl);
    console.log('New URL:', newUrl);
    console.log('Classification Type:', classificationType);

    if (!newUrl.trim()) {
      alert('Please enter a valid URL');
      return;
    }

    // Determine which execution to use
    let executionToUse = currentExecution;
    if (!executionToUse?.executionId && selectedExecution && selectedExecution !== 'new') {
      const execution = previousExecutions.find(ex => ex.id.toString() === selectedExecution);
      if (execution) {
        executionToUse = {
          executionId: execution.id.toString(),
          executionArn: '',
          siteUrl: siteUrl,
          industry: industry,
          startTime: execution.execution_date,
          s3Path: execution.s3_path,
          completedAt: execution.execution_date
        };
      }
    }

    if (!executionToUse?.executionId) {
      alert('No execution ID available. Please run a scan first.');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/classifications/single/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${getAuthToken()}`
        },
        body: JSON.stringify({
          execution_id: executionToUse.executionId,
          url: newUrl.trim(),
          classification_type: classificationType,
          confidence: 1.1,
          reasoning: "Human feedback"
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to update URL: ${response.status}`);
      }

      const data = await response.json();
      console.log('URL updated successfully:', data);

      // Update local state
      const updatedPages = { ...pages };
      const updatedClassification = { ...classification };

      // Remove old page
      delete updatedPages[oldUrl];

      // Create updated page object
      const updatedPage = {
        url: newUrl.trim(),
        title: `Page from ${newUrl.trim()}`,
        classification: classificationType,
        confidence: 1.1,
        reasoning: "Human feedback",
        classificationMethod: 'Manual'
      };

      // Add updated page
      updatedPages[newUrl.trim()] = updatedPage;

      // Update classification array
      if (updatedClassification[classificationType]) {
        updatedClassification[classificationType] = updatedClassification[classificationType].map(page => 
          page.url === oldUrl ? updatedPage : page
        );
      }

      // Update state
      setPages(updatedPages);
      setClassification(updatedClassification);

      updateStatus('active', `URL updated in ${classificationType}: ${oldUrl} → ${newUrl.trim()}`);
      
      // Close editing
      setEditingUrl(null);
      setEditUrlInputs(prev => {
        const updated = { ...prev };
        delete updated[oldUrl];
        return updated;
      });

    } catch (error) {
      console.error('Error updating URL:', error);
      alert(`Failed to update URL: ${error.message}`);
    }
  };

  const addUrlToClassification = async (classificationType, url) => {
    console.log('=== ADD URL TO CLASSIFICATION ===');
    console.log('Classification Type:', classificationType);
    console.log('URL:', url);

    if (!url.trim()) {
      alert('Please enter a valid URL');
      return;
    }

    // Determine which execution to use
    let executionToUse = currentExecution;
    if (!executionToUse?.executionId && selectedExecution && selectedExecution !== 'new') {
      const execution = previousExecutions.find(ex => ex.id.toString() === selectedExecution);
      if (execution) {
        executionToUse = {
          executionId: execution.id.toString(),
          executionArn: '',
          siteUrl: siteUrl,
          industry: industry,
          startTime: execution.execution_date,
          s3Path: execution.s3_path,
          completedAt: execution.execution_date
        };
      }
    }

    if (!executionToUse?.executionId) {
      alert('No execution ID available. Please run a scan first.');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/classifications/single/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${getAuthToken()}`
        },
        body: JSON.stringify({
          execution_id: executionToUse.executionId,
          url: url.trim(),
          classification_type: classificationType,
          confidence: 1.1,
          reasoning: "Human feedback"
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to add URL: ${response.status}`);
      }

      const data = await response.json();
      console.log('URL added successfully:', data);

      // Create new page object
      const newPage = {
        url: url.trim(),
        title: `Page from ${url.trim()}`,
        classification: classificationType,
        confidence: 1.1,
        reasoning: "Human feedback",
        classificationMethod: 'Manual'
      };

      // Update local state
      const updatedPages = { ...pages };
      const updatedClassification = { ...classification };

      // Add to pages
      updatedPages[url.trim()] = newPage;

      // Add to classification
      if (!updatedClassification[classificationType]) {
        updatedClassification[classificationType] = [];
      }
      updatedClassification[classificationType].push(newPage);

      // Update state
      setPages(updatedPages);
      setClassification(updatedClassification);

      updateStatus('active', `URL added to ${classificationType}: ${url.trim()}`);
      
      // Close editing
      setEditingCard(null);
      setEditUrlInputs(prev => {
        const updated = { ...prev };
        delete updated[classificationType];
        return updated;
      });

    } catch (error) {
      console.error('Error adding URL:', error);
      alert(`Failed to add URL: ${error.message}`);
    }
  };

  const deleteClassificationType = async (classificationType) => {
    console.log('=== DELETE CLASSIFICATION TYPE ===');
    console.log('Classification Type:', classificationType);

    // Determine which execution to use
    let executionToUse = currentExecution;
    if (!executionToUse?.executionId && selectedExecution && selectedExecution !== 'new') {
      const execution = previousExecutions.find(ex => ex.id.toString() === selectedExecution);
      if (execution) {
        executionToUse = {
          executionId: execution.id.toString(),
          executionArn: '',
          siteUrl: siteUrl,
          industry: industry,
          startTime: execution.execution_date,
          s3Path: execution.s3_path,
          completedAt: execution.execution_date
        };
      }
    }

    if (!executionToUse?.executionId) {
      alert('No execution ID available. Please run a scan first.');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/classifications/delete-by-type/`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${getAuthToken()}`
        },
        body: JSON.stringify({
          execution_id: executionToUse.executionId,
          classification_type: classificationType
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to delete classification: ${response.status}`);
      }

      console.log('Classification deleted successfully');

      // Update local state
      const updatedPages = { ...pages };
      const updatedClassification = { ...classification };

      // Remove all pages from this classification type
      if (updatedClassification[classificationType]) {
        updatedClassification[classificationType].forEach(page => {
          delete updatedPages[page.url];
        });
        delete updatedClassification[classificationType];
      }

      // Update state
      setPages(updatedPages);
      setClassification(updatedClassification);

      updateStatus('active', `Classification "${classificationType}" deleted`);
      
      // Close editing
      setEditingUrl(null);
      setDeletingCard(null);
      setEditUrlInputs(prev => {
        const updated = { ...prev };
        Object.keys(updated).forEach(key => {
          if (key.startsWith(classificationType)) {
            delete updated[key];
          }
        });
        return updated;
      });

    } catch (error) {
      console.error('Error deleting classification:', error);
      alert(`Failed to delete classification: ${error.message}`);
    }
  };

  const classifyPagesWithClaude = async (pagesData, executionData = null, modelToUse = null) => {
    try {
      console.log('classifyPagesWithClaude input pagesData:', pagesData);
      console.log('classifyPagesWithClaude input executionData:', executionData);
      console.log('classifyPagesWithClaude pagesData type:', typeof pagesData);
      console.log('classifyPagesWithClaude pagesData keys:', Object.keys(pagesData));
      
      // Prepare pages data for Claude API (limit to max 440 pages)
      const preparedPages = Object.values(pagesData).map(page => ({
        url: page.url,
        title: page.title,
        metaDescription: page.metaDescription,
        bodyClass: page.bodyClass,
        cms: page.cms,
        schemaTypes: page.schemaTypes || []
      })).slice(0, 440);

      console.log('preparedPages for Claude API:', preparedPages);
      console.log('preparedPages length:', preparedPages.length);
      
      if (Object.values(pagesData).length > 440) {
        console.log(`⚠️ Limited classification to first 440 pages (total pages: ${Object.values(pagesData).length})`);
        setStatus({ 
          type: 'warning', 
          message: `Classification limited to first 440 pages (${Object.values(pagesData).length} total pages found)` 
        });
      }

      // Validate that we have pages to classify
      if (preparedPages.length === 0) {
        console.error('No pages to classify - preparedPages is empty');
        console.error('Original pagesData:', pagesData);
        console.error('Object.values(pagesData):', Object.values(pagesData));
        throw new Error('No pages available for classification - pages data is empty');
      }

      // Get execution ID for the request
      const execToUse = executionData || currentExecution;
      const executionId = execToUse?.executionId;
      
      console.log('Using execution ID for Claude classify:', executionId);

      const requestBody = {
        pages: preparedPages,
        industry: industry,
        execution_id: executionId,
        model_used: modelToUse || selectedModel
      };

      console.log('=== CLAUDE CLASSIFY API INPUT ===');
      console.log('Request URL:', `${API_BASE_URL}/claude/classify/`);
      console.log('Request Headers:', {
        'Content-Type': 'application/json',
        'Authorization': `Token ${getAuthToken()}`
      });
      console.log('Request Body:', JSON.stringify(requestBody, null, 2));
      console.log('Pages count:', preparedPages.length, 'Industry:', industry);

      // Call backend proxy for Claude API
      const response = await fetch(`${API_BASE_URL}/claude/classify/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${getAuthToken()}`
        },
        body: JSON.stringify(requestBody)
      });

      console.log('=== CLAUDE CLASSIFY API RESPONSE ===');
      console.log('Response status:', response.status, response.statusText);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        const errorText = await response.text();
        console.log('Error response body:', errorText);
        throw new Error(`Claude API proxy failed: ${response.status} - ${errorText}`);
      }

      const classificationsResponse = await response.json();
      console.log('Response body (parsed JSON):', JSON.stringify(classificationsResponse, null, 2));
      console.log('Classifications object:', classificationsResponse);
      
      // Process the response and build classification structure
      const newClassification = {};
      
      // Handle different response formats
      let classifications;
      
      // Check if response has 'classification' field with markdown string (current backend format)
      if (classificationsResponse.classification && typeof classificationsResponse.classification === 'string') {
        console.log('Backend returned classification as markdown string, parsing...');
        
        // Extract JSON from markdown code block
        const markdownString = classificationsResponse.classification;
        const jsonMatch = markdownString.match(/```json\n([\s\S]*?)\n```/);
        
        if (jsonMatch && jsonMatch[1]) {
          try {
            const parsedData = JSON.parse(jsonMatch[1]);
            classifications = parsedData.classifications;
            console.log('Successfully parsed classifications from markdown:', classifications);
          } catch (parseError) {
            console.error('Failed to parse JSON from markdown:', parseError);
            throw new Error('Failed to parse classification JSON from backend response');
          }
        } else {
          console.error('No JSON found in markdown string:', markdownString);
          throw new Error('No valid JSON found in classification response');
        }
      }
      // Check if response has direct 'classifications' field 
      else if (classificationsResponse.classifications) {
        classifications = classificationsResponse.classifications;
      } 
      // Check if response is direct classifications object
      else if (typeof classificationsResponse === 'object') {
        classifications = classificationsResponse;
      } 
      else {
        console.error('Invalid Claude API response format:', classificationsResponse);
        throw new Error('Invalid Claude API response format');
      }
      
      console.log('Applying classifications to pages...');
      console.log('Classifications to apply:', classifications);
      console.log('Available pages:', Object.keys(pagesData));
      
      // Apply classifications to pages
      Object.entries(classifications).forEach(([url, classificationData]) => {
        const page = pagesData[url];
        console.log(`Processing classification for ${url}:`, classificationData);
        
        if (page) {
          // Handle both simple string classification and detailed object
          const classification = typeof classificationData === 'string' 
            ? classificationData 
            : classificationData.classification;
          
          console.log(`Setting classification for ${url}: ${classification}`);
          
          page.classification = classification;
          page.confidence = classificationData.confidence || null;
          page.reasoning = classificationData.reasoning || null;
          page.classificationMethod = 'AI'; // Mark as AI-classified
          
          if (!newClassification[classification]) {
            newClassification[classification] = [];
          }
          newClassification[classification].push(page);
        } else {
          console.warn(`Page not found for URL: ${url}`);
        }
      });
      
      console.log('Final classification structure:', newClassification);
      
      return newClassification;
      
    } catch (error) {
      console.error('Claude classification error:', error);
      throw error; // Re-throw to trigger fallback
    }
  };

  const performRuleBasedClassification = (pagesData) => {
    const newClassification = {};
    
    Object.values(pagesData).forEach(page => {
      const pageClassification = classifyPageFallback(page);
      page.classification = pageClassification;
      page.classificationMethod = 'Rules'; // Mark as rule-based
      page.confidence = null; // No confidence for rules-based
      page.reasoning = null; // No reasoning for rules-based
      
      if (!newClassification[pageClassification]) {
        newClassification[pageClassification] = [];
      }
      newClassification[pageClassification].push(page);
    });
    
    return newClassification;
  };

  const loadExistingClassifications = async (executionId) => {
    try {
      console.log(`Loading existing classifications for execution: ${executionId}`);
      
      console.log('=== LOADING EXISTING CLASSIFICATIONS ===');
      console.log('Request URL:', `${API_BASE_URL}/claude/classifications/${executionId}/`);
      
      const response = await fetch(
        `${API_BASE_URL}/claude/classifications/${executionId}/`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Token ${getAuthToken()}`
          }
        }
      );

      console.log('Classifications response status:', response.status);

      if (!response.ok) {
        console.log(`No existing classifications found (${response.status})`);
        return null;
      }

      const data = await response.json();
      console.log('Existing classifications loaded:', data);
      
      return data;
      
    } catch (error) {
      console.error('Error loading existing classifications:', error);
      return null;
    }
  };

  const applyExistingClassifications = (pagesData, classificationsData, executionData = null) => {
    console.log('Applying existing classifications to pages...');
    
    // Set industry from the response
    if (classificationsData.industry_type) {
      console.log(`Setting industry from classifications API: ${classificationsData.industry_type}`);
      setIndustry(classificationsData.industry_type);
      setIndustryLocked(true);
    }
    
    // Build classification structure
    const newClassification = {};
    const allScreenshots = [];
    
    // Process each classification
    classificationsData.classifications.forEach(classItem => {
      const page = pagesData[classItem.url];
      if (page) {
        // Apply classification data to page
        page.classification = classItem.classification_type;
        page.confidence = classItem.confidence;
        page.reasoning = classItem.reasoning;
        page.classificationMethod = 'AI'; // Mark as AI-classified
        
        // Add to classification groups
        if (!newClassification[classItem.classification_type]) {
          newClassification[classItem.classification_type] = [];
        }
        newClassification[classItem.classification_type].push(page);
        
        // Collect screenshots
        if (classItem.screenshots && classItem.screenshots.length > 0) {
          classItem.screenshots.forEach(screenshot => {
            allScreenshots.push({
              screenshot_id: screenshot.screenshot_id,
              tag: classItem.classification_type,
              url: classItem.url,
              device: screenshot.device,
              status: screenshot.status,
              screenshot_url: screenshot.screenshot_url,
              created_at: screenshot.created_at,
              updated_at: screenshot.updated_at,
              error_message: screenshot.error_message
            });
          });
        }
      } else {
        console.warn(`Page not found for classification URL: ${classItem.url}`);
        // Still create a minimal page object for the classification
        const minimalPage = {
          url: classItem.url,
          title: `Page from ${classItem.url}`,
          classification: classItem.classification_type,
          confidence: classItem.confidence,
          reasoning: classItem.reasoning,
          classificationMethod: 'AI'
        };
        
        // Add to pages data and classification groups
        pagesData[classItem.url] = minimalPage;
        
        if (!newClassification[classItem.classification_type]) {
          newClassification[classItem.classification_type] = [];
        }
        newClassification[classItem.classification_type].push(minimalPage);
        
        // Still collect screenshots
        if (classItem.screenshots && classItem.screenshots.length > 0) {
          classItem.screenshots.forEach(screenshot => {
            allScreenshots.push({
              screenshot_id: screenshot.screenshot_id,
              tag: classItem.classification_type,
              url: classItem.url,
              device: screenshot.device,
              status: screenshot.status,
              screenshot_url: screenshot.screenshot_url,
              created_at: screenshot.created_at,
              updated_at: screenshot.updated_at,
              error_message: screenshot.error_message
            });
          });
        }
      }
    });
    
    console.log(`Applied ${classificationsData.classifications.length} existing classifications`);
    console.log(`Found ${allScreenshots.length} existing screenshots`);
    
    // Update state
    setPages(pagesData); // Update pages with merged classification data
    setClassification(newClassification);
    setClassificationMethod('AI-Powered (Claude)');
    setExistingScreenshots(allScreenshots);
    
    // Update screenshot status ONLY for screenshots that actually exist
    const screenshotStatusUpdates = {};
    if (allScreenshots.length > 0) {
      allScreenshots.forEach(screenshot => {
        if (screenshot.device === 'desktop') {
          screenshotStatusUpdates[screenshot.tag] = screenshot.status === 'completed' ? 'success' : 
                                                     screenshot.status === 'failed' ? 'error' : 'pending';
        }
      });
      setScreenshotStatus(screenshotStatusUpdates);
      console.log('Set screenshot status for existing screenshots:', screenshotStatusUpdates);
    } else {
      // Don't set any screenshot status if there are no screenshots
      // This allows auto-triggering to work properly
      console.log('No existing screenshots - leaving screenshot status empty for auto-triggering');
    }
    
    console.log('Applied existing classifications and screenshots');
    
    // Check which single-page classifications are missing screenshots
    const singlePageClassifications = Object.entries(newClassification).filter(
      ([tag, pages]) => pages.length === 1
    );
    
    const existingScreenshotTags = new Set(allScreenshots.map(s => s.tag));
    const missingScreenshotTags = singlePageClassifications
      .map(([tag]) => tag)
      .filter(tag => !existingScreenshotTags.has(tag));
    
    console.log(`Single-page classifications: ${singlePageClassifications.length}`);
    console.log(`Classifications with existing screenshots: ${existingScreenshotTags.size}`);
    console.log(`Classifications missing screenshots: ${missingScreenshotTags.length}`);
    console.log(`Missing screenshot tags:`, missingScreenshotTags);
    
    if (missingScreenshotTags.length > 0) {
      console.log(`Found ${missingScreenshotTags.length} single-page classifications without screenshots, will auto-trigger`);
      console.log('Current execution for auto-trigger:', currentExecution);
      console.log('Passed execution data for auto-trigger:', executionData);
      
      // Use passed execution data if available, otherwise fall back to currentExecution
      const execForAutoTrigger = executionData || currentExecution;
      
      // Need to call auto-trigger after state is updated
      setTimeout(() => {
        console.log('About to call autoTriggerSinglePageScreenshots with execution:', execForAutoTrigger);
        autoTriggerSinglePageScreenshots(newClassification, execForAutoTrigger);
      }, 1000);
    } else {
      console.log('All single-page classifications already have screenshots, no auto-trigger needed');
    }
  };

  // Prompt Management functions
  const fetchPromptTemplates = async () => {
    setLoadingPrompts(true);
    try {
      console.log('🔍 Fetching prompt templates');
      
      const response = await fetch(`${API_BASE_URL}/prompts/templates/`, {
        method: 'GET',
        headers: {
          'Authorization': `Token ${getAuthToken()}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('📥 Prompt templates loaded:', data);
      setPromptTemplates(data);
      
      return data;
    } catch (error) {
      console.error('❌ Error fetching prompt templates:', error);
      setPromptTemplates([]);
    } finally {
      setLoadingPrompts(false);
    }
  };

  const fetchTemplateWithConfigs = async (templateSlug) => {
    try {
      console.log(`🔍 Fetching template with configs: ${templateSlug}`);
      
      const response = await fetch(`${API_BASE_URL}/prompts/templates/${templateSlug}/`, {
        method: 'GET',
        headers: {
          'Authorization': `Token ${getAuthToken()}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('📥 Template with configs loaded:', data);
      setSelectedTemplate(data);
      setTempTemplateContent(data.content);
      
      return data;
    } catch (error) {
      console.error('❌ Error fetching template with configs:', error);
      setSelectedTemplate(null);
    }
  };

  const updatePromptTemplate = async (templateSlug, updates) => {
    try {
      console.log(`💾 Updating template: ${templateSlug}`);
      
      const response = await fetch(`${API_BASE_URL}/prompts/templates/${templateSlug}/`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${getAuthToken()}`
        },
        body: JSON.stringify(updates)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('✅ Template updated successfully:', data);
      
      // Refresh the selected template
      setSelectedTemplate(data);
      setEditingTemplate(false);
      
      return data;
    } catch (error) {
      console.error('❌ Error updating template:', error);
      alert('Failed to update template: ' + error.message);
    }
  };

  const validatePromptTemplate = (templateContent) => {
    const issues = [];
    
    // Find SINGLE brace variables {variable} but exclude DOUBLE brace examples {{example}}
    // More precise regex: matches {word} but not {{anything}} and not {}
    const templateVariables = [];
    const lines = templateContent.split('\n');
    
    lines.forEach((line, lineNum) => {
      // Skip lines that contain double braces (JSON examples)
      if (line.includes('{{') || line.includes('}}')) {
        return;
      }
      
      // Find single brace variables in non-JSON lines
      const matches = [...line.matchAll(/\{([a-zA-Z_][a-zA-Z0-9_]*)\}/g)];
      matches.forEach(match => {
        templateVariables.push(match[1]);
      });
    });
    
    const uniqueTemplateVars = [...new Set(templateVariables)];
    
    console.log('🔍 Template variables found (single braces only):', uniqueTemplateVars);
    
    // Check for malformed variables (unclosed braces)
    const malformedLines = lines.filter((line, index) => {
      // Skip JSON example lines
      if (line.includes('{{') || line.includes('}}')) return false;
      
      // Check for unclosed single braces
      const openBraces = (line.match(/\{/g) || []).length;
      const closeBraces = (line.match(/\}/g) || []).length;
      return openBraces !== closeBraces;
    });
    
    if (malformedLines.length > 0) {
      issues.push({
        type: 'error',
        message: `Found ${malformedLines.length} lines with unclosed braces`,
        line: 'Multiple locations'
      });
    }
    
    // Check for empty variables (but only in non-JSON lines)
    const emptyVarLines = lines.filter(line => {
      if (line.includes('{{') || line.includes('}}')) return false;
      return line.includes('{}');
    });
    
    if (emptyVarLines.length > 0) {
      issues.push({
        type: 'warning',
        message: 'Empty variable placeholders found: {}',
        line: 'Multiple locations'
      });
    }
    
    return {
      variables: uniqueTemplateVars,
      issues: issues
    };
  };

  // Valid industry types and classifications (moved to component level)
  const validIndustries = ['Law Firms', 'Plumbing', 'Roofing', 'HVAC', 'Electricians', 'Pest Control', 'Landscaping', 'Dentistry', 'Beauty & Cosmetic School', 'Doors & Windows'];
  const validClassificationTypes = React.useMemo(() => {
    const types = new Set();
    Object.values(industryRequirements).forEach(industryReqs => {
      industryReqs.forEach(classification => types.add(classification));
    });
    return types;
  }, []); // industryRequirements is static, no dependencies needed

  const validateConfiguration = (configData) => {
    const issues = [];
    
    try {
      const parsed = JSON.parse(configData);
      
      // Check if it's a valid object
      if (typeof parsed !== 'object' || parsed === null) {
        issues.push({
          type: 'error',
          message: 'Configuration must be a valid JSON object',
          line: '1'
        });
        return { parsed: null, issues };
      }
      
      // Check if this is a schema audit config (has "industries" key)
      const isSchemaAuditConfig = parsed.industries && typeof parsed.industries === 'object';
      
      if (isSchemaAuditConfig) {
        // Validate schema audit configuration structure
        const industries = Object.keys(parsed.industries);
        
        if (industries.length === 0) {
          issues.push({
            type: 'error',
            message: 'Schema audit configuration must contain at least one industry in "industries" object',
            line: 'N/A'
          });
        }

        // Validate each industry in schema audit config
        industries.forEach(industry => {
          if (!validIndustries.includes(industry)) {
            issues.push({
              type: 'error',
              message: `Invalid industry "${industry}". Must be one of: ${validIndustries.join(', ')}`,
              line: 'N/A'
            });
          }

          const industryData = parsed.industries[industry];
          
          // Check for schema_requirements
          if (!industryData.schema_requirements || typeof industryData.schema_requirements !== 'object') {
            issues.push({
              type: 'error',
              message: `Industry "${industry}" missing "schema_requirements" object`,
              line: 'N/A'
            });
            return;
          }

          // Validate page types within schema_requirements
          const pageTypes = Object.keys(industryData.schema_requirements);
          pageTypes.forEach(pageType => {
            const pageData = industryData.schema_requirements[pageType];
            
            // Check for required schemas array
            if (!pageData.schemas || !Array.isArray(pageData.schemas)) {
              issues.push({
                type: 'error',
                message: `Page type "${pageType}" in industry "${industry}" missing "schemas" array`,
                line: 'N/A'
              });
            }

            // Validate schema types are strings
            if (pageData.schemas && Array.isArray(pageData.schemas)) {
              pageData.schemas.forEach((schema, index) => {
                if (typeof schema !== 'string') {
                  issues.push({
                    type: 'error',
                    message: `Invalid schema type at index ${index} in "${pageType}" for industry "${industry}". Must be string.`,
                    line: 'N/A'
                  });
                }
              });
            }

            // Validate specific_prompt if present
            if (pageData.specific_prompt && typeof pageData.specific_prompt !== 'string') {
              issues.push({
                type: 'warning',
                message: `Page type "${pageType}" in industry "${industry}" has invalid specific_prompt (must be string)`,
                line: 'N/A'
              });
            }

            // Validate page type matches industry requirements
            const industryPageTypes = industryRequirements[industry] || [];
            if (!industryPageTypes.includes(pageType)) {
              issues.push({
                type: 'warning',
                message: `Page type "${pageType}" not found in industry "${industry}" requirements. Available: ${industryPageTypes.join(', ')}`,
                line: 'N/A'
              });
            }
          });
        });

        return { issues, parsed };
      }
      
      // Check for required structure (industry-based config)
      const industries = Object.keys(parsed);
      if (industries.length === 0) {
        issues.push({
          type: 'warning',
          message: 'No industries defined in configuration',
          line: '1'
        });
      }
      
      // Validate industry names match frontend values
      industries.forEach(industry => {
        if (!validIndustries.includes(industry)) {
          issues.push({
            type: 'error',
            message: `Invalid industry "${industry}". Must be one of: ${validIndustries.join(', ')}`,
            line: 'N/A'
          });
        }
      });
      
      // Validate each industry structure
      industries.forEach(industry => {
        const industryData = parsed[industry];
        
        // Check if this is a classification config (has classifications array)
        const isClassificationConfig = industryData.classifications && Array.isArray(industryData.classifications);
        
        // Check if this is a benchmark config (has page-type-specific objects)
        const pageTypeKeys = Object.keys(industryData).filter(key => 
          typeof industryData[key] === 'object' && 
          industryData[key] !== null &&
          !Array.isArray(industryData[key]) &&
          key !== 'guidelines'
        );
        const isBenchmarkConfig = pageTypeKeys.length > 0;
        
        if (!isClassificationConfig && !isBenchmarkConfig) {
          issues.push({
            type: 'error',
            message: `Industry "${industry}" has invalid structure. Expected either classifications array or page-type objects`,
            line: 'N/A'
          });
        }
        
        // Validate classification config structure
        if (isClassificationConfig) {
          if (!industryData.guidelines || typeof industryData.guidelines !== 'object') {
            issues.push({
              type: 'warning',
              message: `Industry "${industry}" (classification config) missing guidelines object`,
              line: 'N/A'
            });
          }
        }
        
        // Validate benchmark config structure
        if (isBenchmarkConfig) {
          pageTypeKeys.forEach(pageType => {
            const pageConfig = industryData[pageType];
            
            // Validate page type name matches frontend classification types
            if (!validClassificationTypes.has(pageType)) {
              const industryClassifications = industryRequirements[industry] || [];
              issues.push({
                type: 'error',
                message: `Invalid classification type "${pageType}" in industry "${industry}". Must match Gap Analysis classifications for ${industry}: ${industryClassifications.join(', ')}`,
                line: 'N/A'
              });
            }
            
            // Validate if this page type is actually required for this industry
            if (validIndustries.includes(industry) && industryRequirements[industry]) {
              if (!industryRequirements[industry].includes(pageType)) {
                issues.push({
                  type: 'warning',
                  message: `Page type "${pageType}" not required for "${industry}" industry (but allowed)`,
                  line: 'N/A'
                });
              }
            }
            
            if (!pageConfig.focus_areas || !Array.isArray(pageConfig.focus_areas)) {
              issues.push({
                type: 'warning',
                message: `Industry "${industry}" page type "${pageType}" missing focus_areas array`,
                line: 'N/A'
              });
            }
            
            if (!pageConfig.key_elements || !Array.isArray(pageConfig.key_elements)) {
              issues.push({
                type: 'warning',
                message: `Industry "${industry}" page type "${pageType}" missing key_elements array`,
                line: 'N/A'
              });
            }
            
            if (!pageConfig.specific_prompt || typeof pageConfig.specific_prompt !== 'string') {
              issues.push({
                type: 'warning',
                message: `Industry "${industry}" page type "${pageType}" missing specific_prompt string`,
                line: 'N/A'
              });
            }
            
            if (!pageConfig.cro_priorities || !Array.isArray(pageConfig.cro_priorities)) {
              issues.push({
                type: 'warning',
                message: `Industry "${industry}" page type "${pageType}" missing cro_priorities array`,
                line: 'N/A'
              });
            }
          });
        }
        
        // Validate classification config structure  
        if (isClassificationConfig) {
          // Check if classifications match valid types
          industryData.classifications.forEach(classification => {
            if (!validClassificationTypes.has(classification)) {
              const industryClassifications = industryRequirements[industry] || [];
              issues.push({
                type: 'error',
                message: `Invalid classification "${classification}" in industry "${industry}". Must match Gap Analysis classifications for ${industry}: ${industryClassifications.join(', ')}`,
                line: 'N/A'
              });
            }
          });
          
          // Check if all required classifications for this industry are present
          if (validIndustries.includes(industry) && industryRequirements[industry]) {
            const requiredForIndustry = industryRequirements[industry];
            const missingClassifications = requiredForIndustry.filter(req => 
              !industryData.classifications.includes(req)
            );
            
            if (missingClassifications.length > 0) {
              issues.push({
                type: 'warning',
                message: `Industry "${industry}" missing required classifications: ${missingClassifications.join(', ')}`,
                line: 'N/A'
              });
            }
          }
          
          // Validate guidelines structure
          if (industryData.guidelines) {
            // Check that all guideline keys exist in classifications
            const guidelineKeys = Object.keys(industryData.guidelines);
            const classifications = industryData.classifications || [];
            
            guidelineKeys.forEach(key => {
              if (!classifications.includes(key)) {
                issues.push({
                  type: 'error',
                  message: `Guideline key "${key}" does not exist in classifications array. All guideline keys must match classification names exactly.`,
                  line: 'N/A'
                });
              }
            });
            
            // Check that all classifications have guidelines
            classifications.forEach(classification => {
              if (!guidelineKeys.includes(classification)) {
                issues.push({
                  type: 'error',  
                  message: `Classification "${classification}" is missing its guideline. Each classification must have a corresponding guideline entry in the guidelines object.`,
                  line: 'N/A'
                });
              }
            });
          } else if (industryData.classifications && industryData.classifications.length > 0) {
            issues.push({
              type: 'warning',
              message: `No guidelines found for classification config. Guidelines help improve classification accuracy and should be provided for each classification.`,
              line: 'N/A'
            });
          }
        }
      });
      
      return { parsed, issues };
      
    } catch (error) {
      issues.push({
        type: 'error',
        message: `Invalid JSON: ${error.message}`,
        line: 'Parse error'
      });
      return { parsed: null, issues };
    }
  };

  const highlightTemplateVariables = (content) => {
    // Highlight SINGLE brace variables {variable} but leave DOUBLE brace examples {{example}} unchanged
    const lines = content.split('\n');
    
    return lines.map(line => {
      // Skip highlighting lines that contain double braces (JSON examples)
      if (line.includes('{{') || line.includes('}}')) {
        return line;
      }
      
      // Highlight single brace variables in non-JSON lines (same regex as validation)
      return line.replace(/\{([a-zA-Z_][a-zA-Z0-9_]*)\}/g, (match, variable) => {
        return `<span class="template-variable">{${variable}}</span>`;
      });
    }).join('\n');
  };


  // Load prompt data when switching to prompts tab
  useEffect(() => {
    if (activeSection === 'prompts') {
      fetchPromptTemplates();
    }
  }, [activeSection]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-load linked configuration when template is selected
  useEffect(() => {
    if (selectedTemplate && selectedTemplate.configurations && selectedTemplate.configurations.length > 0 && !selectedConfig) {
      const firstConfig = selectedTemplate.configurations[0];
      console.log('Auto-loading linked configuration:', firstConfig.name);
      setSelectedConfig(firstConfig);
      setTempConfigData(JSON.stringify(firstConfig.config_data, null, 2));
    }
  }, [selectedTemplate, selectedConfig]);

  // Benchmark functions
  const loadBenchmarkExecutions = async (industryType) => {
    if (!industryType) return;
    
    setLoadingBenchmarkExecutions(true);
    try {
      console.log(`Loading benchmark executions for industry: ${industryType}`);
      
      const response = await fetch(
        `${API_BASE_URL}/executions/by-industry/${encodeURIComponent(industryType)}/`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Token ${getAuthToken()}`
          }
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Benchmark executions loaded:', data);
      
      // Filter out current execution
      const currentExecId = currentExecution?.executionId;
      const filteredExecutions = data.executions.filter(exec => 
        exec.execution_id.toString() !== currentExecId
      );
      
      setBenchmarkExecutions(filteredExecutions);
      setSelectedBenchmarkExecution(''); // Reset selection
      
    } catch (error) {
      console.error('Error loading benchmark executions:', error);
      setBenchmarkExecutions([]);
    } finally {
      setLoadingBenchmarkExecutions(false);
    }
  };

  const loadBenchmarkClassifications = async (executionId) => {
    if (!executionId) return;
    
    setLoadingBenchmarkClassifications(true);
    try {
      console.log(`Loading benchmark classifications for execution: ${executionId}`);
      
      const response = await fetch(
        `${API_BASE_URL}/claude/classifications/${executionId}/`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Token ${getAuthToken()}`
          }
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Benchmark classifications loaded:', data);
      
      setBenchmarkClassifications(data);
      
    } catch (error) {
      console.error('Error loading benchmark classifications:', error);
      setBenchmarkClassifications({});
    } finally {
      setLoadingBenchmarkClassifications(false);
    }
  };

  const triggerBenchmarkComparison = async (classificationType, device = 'desktop', modelToUse = null) => {
    if (!currentExecution?.executionId || !selectedBenchmarkExecution) {
      console.error('Missing execution IDs for comparison');
      return;
    }

    const currentScreenshot = existingScreenshots.find(s => 
      s.tag === classificationType && s.device === device
    );
    
    const benchmarkScreenshot = benchmarkClassifications.classifications?.find(c => 
      c.classification_type === classificationType && 
      c.screenshots?.some(s => s.device === device)
    )?.screenshots?.find(s => s.device === device);

    const comparisonKey = `${classificationType}_${device}`;
    
    // Handle missing screenshots scenarios
    if (!currentScreenshot && !benchmarkScreenshot) {
      setBenchmarkComparisons(prev => ({
        ...prev,
        [comparisonKey]: {
          loading: false,
          error: 'industry_gap',
          message: `Neither site has ${device} screenshots for ${classificationType} - this indicates an industry-wide gap`,
          recommendation: `Consider creating a standout ${classificationType} ${device} page to gain competitive advantage`,
          opportunity_level: 'high'
        }
      }));
      return;
    }

    if (!currentScreenshot) {
      setBenchmarkComparisons(prev => ({
        ...prev,
        [comparisonKey]: {
          loading: false,
          error: 'client_screenshot_missing',
          message: `Your site missing ${device} screenshot for ${classificationType}`,
          recommendation: `Capture a ${device} screenshot of your ${classificationType} page to enable comparison`,
          action_required: 'trigger_client_screenshot'
        }
      }));
      return;
    }

    if (!benchmarkScreenshot) {
      setBenchmarkComparisons(prev => ({
        ...prev,
        [comparisonKey]: {
          loading: false,
          analysis: 'competitive_advantage',
          message: `Your competitor doesn't have a ${classificationType} ${device} page - this is a potential advantage`,
          recommendation: `Optimize your ${classificationType} ${device} page as competitors lack this page type`,
          advantage_level: 'high'
        }
      }));
      return;
    }

    console.log(`Triggering ${device} comparison for ${classificationType}`);
    
    // Set loading state for this comparison
    setBenchmarkComparisons(prev => ({
      ...prev,
      [comparisonKey]: { loading: true }
    }));

    try {
      // Prepare request for comparison endpoint
      const requestBody = {
        client_execution_id: currentExecution.executionId,
        benchmark_execution_id: selectedBenchmarkExecution,
        classification_type: classificationType,
        industry_type: industry,
        client_screenshot_url: currentScreenshot.screenshot_url,
        benchmark_screenshot_url: benchmarkScreenshot.screenshot_url,
        client_url: currentScreenshot.url || siteUrl,
        benchmark_url: benchmarkScreenshot.url || benchmarkClassifications.summary?.execution_url,
        device: device === 'phone' ? 'phone' : 'desktop',
        model_used: modelToUse || selectedModel
      };

      console.log(`=== ${device.toUpperCase()} BENCHMARK COMPARISON API INPUT ===`);
      console.log('Request Body:', JSON.stringify(requestBody, null, 2));

      // Call comparison endpoint
      const response = await fetch(`${API_BASE_URL}/claude/benchmark-comparison/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${getAuthToken()}`
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(`Comparison API failed: ${response.status}`);
      }

      const comparisonResult = await response.json();
      console.log(`=== ${device.toUpperCase()} BENCHMARK COMPARISON API RESPONSE ===`);
      console.log('Response:', comparisonResult);

      setBenchmarkComparisons(prev => ({
        ...prev,
        [comparisonKey]: {
          loading: false,
          ...comparisonResult
        }
      }));
      
    } catch (error) {
      console.error(`Error in ${device} benchmark comparison for ${classificationType}:`, error);
      setBenchmarkComparisons(prev => ({
        ...prev,
        [comparisonKey]: { 
          loading: false, 
          error: error.message,
          recommendation: 'Try again or contact support if the issue persists'
        }
      }));
    }
  };

  const getExistingScreenshotsData = async (executionData) => {
    if (!executionData?.executionId) {
      console.log('No execution ID available for getting screenshots data');
      return null;
    }

    console.log('=== GET EXISTING SCREENSHOTS DATA DEBUG ===');
    console.log('Getting existing screenshots data for execution ID:', executionData.executionId);
    console.log('Full execution data:', executionData);
    console.log('API URL will be:', `${API_BASE_URL}/screaming-frog/screenshots/${executionData.executionId}/`);
    
    try {
      const response = await fetch(
        `${API_BASE_URL}/screaming-frog/screenshots/${executionData.executionId}/`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Token ${getAuthToken()}`
          }
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log(`API returned ${data.screenshots?.length || 0} existing screenshots`);
      console.log('Screenshots API response:', data);
      
      // Extract and set industry type from API response if available
      if (data.industry_type) {
        console.log(`Industry from screenshots API: ${data.industry_type} (current: ${industry})`);
        if (data.industry_type !== industry) {
          console.log(`Setting industry from API: ${data.industry_type}`);
          setIndustry(data.industry_type);
        }
        setIndustryLocked(true); // Lock industry field since it comes from DB
      }
      
      return data.screenshots || [];
      
    } catch (error) {
      console.error('Error getting existing screenshots data:', error);
      return null;
    }
  };

  const checkExistingScreenshots = async (executionData = null) => {
    const execution = executionData || currentExecution;
    console.log('checkExistingScreenshots called with execution:', execution);
    
    if (!execution?.executionId) {
      console.log('No execution ID available for checking screenshots - execution:', execution);
      return;
    }

    setLoadingScreenshots(true);
    
    try {
      const screenshots = await getExistingScreenshotsData(execution);
      if (screenshots) {
        setExistingScreenshots(screenshots);
        
        console.log(`Found ${screenshots.length} existing screenshots`);
        
        // Update screenshot status based on existing screenshots
        const statusUpdates = {};
        screenshots.forEach(screenshot => {
          if (screenshot.device === 'desktop') {
            statusUpdates[screenshot.tag] = screenshot.status === 'completed' ? 'success' : 
                                           screenshot.status === 'failed' ? 'error' : 'pending';
          }
        });
        setScreenshotStatus(statusUpdates);
      }
    } catch (error) {
      console.error('Error in checkExistingScreenshots:', error);
      setExistingScreenshots([]);
    } finally {
      setLoadingScreenshots(false);
    }
  };

  const autoTriggerSinglePageScreenshots = async (classificationData, executionData = null) => {
    let executionToUse = executionData || currentExecution;
    
    console.log('=== AUTO TRIGGER SINGLE PAGE SCREENSHOTS DEBUG ===');
    console.log('autoTriggerSinglePageScreenshots called with executionData:', executionData);
    console.log('currentExecution state:', currentExecution);
    console.log('autoTriggerSinglePageScreenshots using execution:', executionToUse);
    console.log('selectedExecution:', selectedExecution);
    console.log('previousExecutions length:', previousExecutions.length);
    
    // If no execution data provided, try to get from selected execution
    if (!executionToUse?.executionId && selectedExecution && selectedExecution !== 'new') {
      const execution = previousExecutions.find(ex => ex.id.toString() === selectedExecution);
      if (execution) {
        executionToUse = {
          executionId: execution.id.toString(),
          executionArn: '',
          siteUrl: siteUrl,
          industry: industry,
          startTime: execution.execution_date,
          s3Path: execution.s3_path,
          completedAt: execution.execution_date
        };
      }
    }

    if (!executionToUse?.executionId) {
      console.log('No execution ID available for auto-triggering screenshots');
      return;
    }

    // Get existing screenshots data directly from API
    const existingScreenshotsData = await getExistingScreenshotsData(executionToUse);
    
    if (!existingScreenshotsData) {
      console.log('Failed to get existing screenshots data');
      return;
    }

    // Find single-page classifications
    const singlePageClassifications = Object.entries(classificationData).filter(
      ([tag, pages]) => pages.length === 1
    );

    if (singlePageClassifications.length === 0) {
      console.log('No single-page classifications for auto-triggering');
      return;
    }

    // Check which ones don't have screenshots using fresh API data
    const existingDesktopScreenshots = existingScreenshotsData.filter(s => s.device === 'desktop');
    const existingTags = new Set(existingDesktopScreenshots.map(s => `${s.tag}|${s.url}`));

    const missingScreenshots = singlePageClassifications.filter(([tag, pages]) => {
      const page = pages[0];
      const key = `${tag}|${page.url}`;
      return !existingTags.has(key);
    });

    console.log(`Single-page classifications: ${singlePageClassifications.length}`);
    console.log(`Existing screenshots: ${existingDesktopScreenshots.length}`);
    console.log(`Missing screenshots: ${missingScreenshots.length}`);
    console.log(`Current screenshot status keys: ${Object.keys(screenshotStatus).length}`);

    // Skip auto-triggering if there are no missing screenshots (all needed ones already exist)
    if (missingScreenshots.length === 0) {
      console.log('All single-page classifications already have screenshots, skipping auto-trigger');
      return;
    }

    if (missingScreenshots.length > 0) {
      console.log(`Auto-triggering ${missingScreenshots.length} missing screenshots for execution ${executionToUse.executionId}`);
      
      // Set status to processing for both desktop and phone
      const statusUpdates = {};
      missingScreenshots.forEach(([tag]) => {
        statusUpdates[`${tag}_desktop`] = 'processing';
      });
      singlePageClassifications.forEach(([tag]) => {
        statusUpdates[`${tag}_phone`] = 'processing';
      });
      setScreenshotStatus(prev => ({ ...prev, ...statusUpdates }));

      // Trigger desktop screenshots for missing ones
      const desktopPromises = missingScreenshots.map(([tag, pages]) => {
        const page = pages[0];
        console.log(`Triggering desktop screenshot: ${tag} -> ${page.url} (exec: ${executionToUse.executionId})`);
        
        return fetch(`${API_BASE_URL}/screaming-frog/create_screenshot/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Token ${getAuthToken()}`
          },
          body: JSON.stringify({
            tag: tag,
            execution_id: executionToUse.executionId,
            url: page.url,
            device: 'desktop'
          })
        })
        .then(response => {
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }
          return response.json();
        })
        .then((data) => {
          console.log(`Desktop screenshot API success for ${tag}:`, data);
          setScreenshotStatus(prev => ({ ...prev, [`${tag}_desktop`]: 'success' }));
          return { tag, device: 'desktop', success: true };
        })
        .catch(error => {
          console.error(`Desktop screenshot API error for ${tag}:`, error);
          setScreenshotStatus(prev => ({ ...prev, [`${tag}_desktop`]: 'error' }));
          return { tag, device: 'desktop', success: false, error };
        });
      });

      // Trigger phone screenshots for all single-page classifications
      const phonePromises = singlePageClassifications.map(([tag, pages]) => {
        const page = pages[0];
        console.log(`Triggering phone screenshot: ${tag} -> ${page.url} (exec: ${executionToUse.executionId})`);
        
        return fetch(`${API_BASE_URL}/screaming-frog/create_screenshot/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Token ${getAuthToken()}`
          },
          body: JSON.stringify({
            tag: tag,
            execution_id: executionToUse.executionId,
            url: page.url,
            device: 'phone'
          })
        })
        .then(response => {
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }
          return response.json();
        })
        .then((data) => {
          console.log(`Phone screenshot API success for ${tag}:`, data);
          setScreenshotStatus(prev => ({ ...prev, [`${tag}_phone`]: 'success' }));
          return { tag, device: 'phone', success: true };
        })
        .catch(error => {
          console.error(`Phone screenshot API error for ${tag}:`, error);
          setScreenshotStatus(prev => ({ ...prev, [`${tag}_phone`]: 'error' }));
          return { tag, device: 'phone', success: false, error };
        });
      });

      const screenshotPromises = [...desktopPromises, ...phonePromises];

      await Promise.all(screenshotPromises);
      
      // Refresh screenshot status after a delay
      setTimeout(() => checkExistingScreenshots(executionToUse), 5000);
    } else {
      console.log('All single-page screenshots already exist');
    }
  };

  const triggerScreenshotForUrl = async (tag, url, device = 'desktop', forceUpdate = true) => {
    let executionToUse = currentExecution;
    
    // If no current execution, try to get from selected execution
    if (!executionToUse?.executionId && selectedExecution && selectedExecution !== 'new') {
      const execution = previousExecutions.find(ex => ex.id.toString() === selectedExecution);
      if (execution) {
        executionToUse = {
          executionId: execution.id.toString(),
          executionArn: '',
          siteUrl: siteUrl,
          industry: industry,
          startTime: execution.execution_date,
          s3Path: execution.s3_path,
          completedAt: execution.execution_date
        };
      }
    }
    
    if (!executionToUse?.executionId) {
      console.error('No execution ID available for screenshots');
      return;
    }

    console.log(`Triggering ${device} screenshot for ${tag}: ${url} (force_update: ${forceUpdate})`);
    
    // Set processing status for both devices
    const statusKey = `${tag}_${device}`;
    setScreenshotStatus(prev => ({ ...prev, [statusKey]: 'processing' }));

    try {
      const response = await fetch(`${API_BASE_URL}/screaming-frog/create_screenshot/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${getAuthToken()}`
        },
        body: JSON.stringify({
          tag: tag,
          execution_id: executionToUse.executionId,
          url: url,
          device: device === 'phone' ? 'phone' : 'desktop',
          force_update: forceUpdate
        })
      });

      if (!response.ok) {
        throw new Error(`Screenshot failed: ${response.status}`);
      }

      const data = await response.json();
      console.log(`${device} screenshot triggered successfully for ${tag}:`, data);
      
      // Keep processing status and refresh after delay
      setTimeout(() => {
        checkExistingScreenshots(executionToUse);
      }, 3000);
      
    } catch (error) {
      console.error(`${device} screenshot error for ${tag}:`, error);
      setScreenshotStatus(prev => ({ ...prev, [statusKey]: 'error' }));
    }
  };

  const classifyPageFallback = (page) => {
    // Complete implementation from original file
    const url = page.url || '';
    const title = (page.title || '').toLowerCase();
    const bodyClass = (page.bodyClass || '').toLowerCase();
    const metaDesc = (page.metaDescription || '').toLowerCase();
    
    // Homepage detection
    if (url === '/' || url.endsWith('.com/') || url.endsWith('.org/') || 
        url.endsWith('.net/') || bodyClass.includes('home')) {
      return 'Homepage';
    }
    
    // Contact page
    if (url.includes('/contact') || title.includes('contact') || 
        bodyClass.includes('contact')) {
      return 'Contact';
    }
    
    // About page - IMPROVED
    if (url.includes('/about') || url.includes('/our-firm') || 
        url.includes('/our-team') || title.includes('about') ||
        title.includes('meet ') || title.includes('our firm')) {
      return 'About';
    }
    
    // Privacy/Legal pages
    if (url.includes('/privacy') || title.includes('privacy')) return 'Privacy Policy';
    if (url.includes('/terms') || title.includes('terms')) return 'Terms';
    if (url.includes('/disclaimer') || title.includes('disclaimer')) return 'Disclaimer';
    
    // Blog detection - IMPROVED
    if (url.includes('/blog')) {
      if (url.endsWith('/blog/') || url.endsWith('/blog') ||
          url === '/blog' ||
          (title.includes('blog') && !title.includes('|')) ||
          title.includes('articles') || title.includes('posts') ||
          title.includes('news') || title.includes('insights') ||
          (bodyClass.includes('blog') && bodyClass.includes('archive')) ||
          bodyClass.includes('post-type-archive')) {
        return 'Blog Archive';
      }
      
      if (url.split('/').filter(s => s).length > 2 ||
          title.includes('|') ||
          title.length > 60 ||
          bodyClass.includes('single-post') ||
          bodyClass.includes('postid-') ||
          bodyClass.includes('post-template') ||
          metaDesc.length > 100) {
        return 'Blog Single';
      }
      
      return 'Blog Archive';
    }
    
    // WordPress-specific blog detection
    if (bodyClass.includes('single-post') || 
        bodyClass.includes('postid-') || 
        bodyClass.includes('post-template-default')) {
      return 'Blog Single';
    }
    
    if (bodyClass.includes('blog') || 
        bodyClass.includes('archive') || 
        bodyClass.includes('category') ||
        bodyClass.includes('tag')) {
      return 'Blog Archive';
    }
    
    // Location/Service Area pages - CHECK FIRST
    if (url.includes('/service-area') || url.includes('/service-areas/')) {
      const locationKeywords = ['county', 'city', 'athens', 'clarke', 'oconee', 'watkinsville', 
                              'atlanta', 'georgia', 'ga'];
      const hasLocationKeyword = locationKeywords.some(keyword => 
        url.toLowerCase().includes(keyword) || title.includes(keyword)
      );
      
      if (hasLocationKeyword || url.split('/').filter(s => s).length > 2) {
        return 'Location Single';
      }
      return 'Locations Landing';
    }
    
    // Practice/Service pages - CHECK SECOND
    if (url.includes('/areas-of-expertise') || url.includes('/practice-area') || 
        url.includes('/service/') || url.includes('/practices/')) {
      const serviceKeywords = ['accident', 'injury', 'lawyer', 'attorney', 'case', 
                             'bicycle', 'car', 'dui', 'pedestrian', 'motorcycle'];
      const hasServiceKeyword = serviceKeywords.some(keyword => 
        url.toLowerCase().includes(keyword) || title.includes(keyword)
      );
      
      if (hasServiceKeyword || url.split('/').filter(s => s).length > 2) {
        return 'Service Single';
      }
      return 'Services Landing';
    }
    
    // Additional location detection by title
    if (title.includes('county') || title.includes(' in ') || 
        (title.includes('lawyer') && (title.includes(', ga') || title.includes('georgia')))) {
      return 'Location Single';
    }
    
    // Team/Profile pages
    if (url.includes('/attorney') || url.includes('/lawyer') || 
        url.includes('/team') || title.includes('attorney') || 
        title.includes('lawyer') || bodyClass.includes('team')) {
      if (!title.includes('county') && !title.includes(' in ')) {
        return 'Team/Profile';
      }
    }
    
    // Testimonials/Reviews
    if (url.includes('/testimonial') || url.includes('/review') || 
        url.includes('/case-result') || title.includes('testimonial') || 
        title.includes('review')) {
      return 'Testimonials';
    }
    
    // FAQ page
    if (url.includes('/faq') || title.includes('faq') || 
        title.includes('frequently asked')) {
      return 'FAQ';
    }
    
    // Financing/Payment
    if (url.includes('/financing') || url.includes('/payment') || 
        title.includes('financing') || title.includes('payment')) {
      return 'Financing';
    }
    
    // Warranty/Guarantee
    if (url.includes('/warranty') || url.includes('/guarantee') || 
        title.includes('warranty')) {
      return 'Warranty';
    }
    
    // Insurance
    if (url.includes('/insurance') || title.includes('insurance')) {
      return 'Insurance';
    }
    
    // 404 page
    if (title.includes('404') || title.includes('not found')) {
      return '404 Error Page';
    }
    
    return 'Other';
  };

  const displayFileStatusInUI = (statusData) => {
    // This will be rendered in the overview section
    setFileStatus(statusData);
  };

  const addDownloadButtonToUI = () => {
    // This is handled by the currentExecution state and rendered in the UI
    // No need for manual DOM manipulation in React
  };

  // Handle modal actions
  const handleModelSelection = (selectedModelId) => {
    setSelectedModel(selectedModelId);
    // Don't close modal or execute action yet - wait for Continue button
  };

  const handleContinueWithModel = async () => {
    setShowModelModal(false);
    
    if (modalAction) {
      // Pass the current selectedModel to the action
      await modalAction(selectedModel);
      setModalAction(null);
    }
  };

  const showModelSelectionModal = async (action) => {
    setModalAction(() => action);
    setShowModelModal(true);
    // Fetch models when modal is opened
    const data = await fetchAvailableModels();
    
    // Set default model if none selected and we got data from API
    const currentSelectedModel = localStorage.getItem('selectedClaudeModel');
    if (!currentSelectedModel && data && data.default_model) {
      setSelectedModel(data.default_model);
    }
  };

  const triggerBenchmarkComparisonWithModal = (classificationType, device = 'desktop') => {
    showModelSelectionModal(async (modelToUse) => {
      console.log('🤖 Using selected model for benchmark:', modelToUse);
      
      await triggerBenchmarkComparison(classificationType, device, modelToUse);
      
      // Update the persisted selected model after successful operation
      setSelectedModel(modelToUse);
    });
  };

  const runSchemaAudit = async (address, pageType, schemaOrgData, modelToUse) => {
    const auditKey = `${address}_${pageType}`;
    
    try {
      setLoadingSchemaAudits(prev => ({ ...prev, [auditKey]: true }));
      
      // Extract business name from schema data if available
      let businessName = 'Business';
      
      // Look through all schema objects for business name
      for (const schema of schemaOrgData) {
        if (schema['@graph']) {
          // Handle @graph structure
          for (const item of schema['@graph']) {
            if (item.name && (item['@type'] === 'Organization' || item['@type'] === 'LocalBusiness' || item['@type'] === 'LegalService')) {
              businessName = item.name;
              break;
            }
          }
        } else if (schema.name && (schema['@type'] === 'Organization' || schema['@type'] === 'LocalBusiness' || schema['@type'] === 'LegalService')) {
          businessName = schema.name;
          break;
        }
      }

      // Prepare schema data for API - combine all schemas into single structure
      let combinedSchemaData = {};
      
      if (schemaOrgData.length === 1) {
        // Single schema object
        combinedSchemaData = schemaOrgData[0];
      } else if (schemaOrgData.length > 1) {
        // Multiple schemas - create @graph structure
        const allGraphItems = [];
        
        schemaOrgData.forEach(schema => {
          if (schema['@graph']) {
            // Already has @graph, add all items
            allGraphItems.push(...schema['@graph']);
          } else {
            // Single schema, add to graph
            allGraphItems.push(schema);
          }
        });
        
        combinedSchemaData = {
          '@context': 'https://schema.org',
          '@graph': allGraphItems
        };
      }

      const requestBody = {
        industry: industry,
        url: address,
        classification_type: pageType,
        business_name: businessName,
        schema_org: combinedSchemaData,
        model_used: modelToUse
      };

      console.log('=== SCHEMA AUDIT API INPUT ===');
      console.log('Request Body:', JSON.stringify(requestBody, null, 2));

      const response = await fetch(`${API_BASE_URL}/claude/schema-audit/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${getAuthToken()}`
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      console.log('✅ Schema audit completed:', result);

      // Store the result
      setSchemaAuditResults(prev => ({
        ...prev,
        [auditKey]: result
      }));

      // Log success - results are displayed in the UI
      console.log(`✅ Schema Audit Complete - Compliance: ${result.compliance_score}%, Critical Issues: ${result.critical_issues?.length || 0}, Recommendations: ${result.recommended_improvements?.length || 0}`);

    } catch (error) {
      console.error('❌ Schema audit failed:', error);
      alert(`❌ Schema Audit Failed\n\n${error.message}`);
    } finally {
      setLoadingSchemaAudits(prev => ({ ...prev, [auditKey]: false }));
    }
  };

  const runSchemaAuditWithModal = (address, pageType, schemaOrgData) => {
    showModelSelectionModal(async (modelToUse) => {
      console.log('🤖 Using selected model for schema audit:', modelToUse);
      
      // Clear previous result for this specific page
      const auditKey = `${address}_${pageType}`;
      setSchemaAuditResults(prev => {
        const updated = { ...prev };
        delete updated[auditKey];
        return updated;
      });
      
      await runSchemaAudit(address, pageType, schemaOrgData, modelToUse);
      setSelectedModel(modelToUse);
    });
  };

  const runAllSchemaAudits = async (modelToUse) => {
    // Get all pages with valid classification and schema data
    const validPages = schemaAuditData.filter(item => {
      const page = pages[item.address];
      const pageType = page?.classification;
      return pageType && pageType !== 'Unknown' && pageType.trim() !== '';
    });

    if (validPages.length === 0) {
      alert('No classified pages with schema data found to audit.');
      return;
    }

    console.log(`🚀 Running schema audits for ${validPages.length} pages with model:`, modelToUse);
    
    // Clear all previous results
    setSchemaAuditResults({});
    
    try {
      // Run all audits in parallel
      const auditPromises = validPages.map(item => {
        const page = pages[item.address];
        const pageType = page?.classification;
        return runSchemaAudit(item.address, pageType, item.schema_org, modelToUse);
      });
      
      await Promise.all(auditPromises);
      console.log('✅ All schema audits completed successfully');
      
    } catch (error) {
      console.error('❌ Error running bulk schema audits:', error);
    }
  };

  const runAllSchemaAuditsWithModal = () => {
    const validPages = schemaAuditData.filter(item => {
      const page = pages[item.address];
      const pageType = page?.classification;
      return pageType && pageType !== 'Unknown' && pageType.trim() !== '';
    });

    if (validPages.length === 0) {
      alert('No classified pages with schema data found to audit.');
      return;
    }

    showModelSelectionModal(async (modelToUse) => {
      console.log('🤖 Using selected model for bulk schema audit:', modelToUse);
      await runAllSchemaAudits(modelToUse);
      setSelectedModel(modelToUse);
    });
  };

  const generateAIRecommendations = async () => {

    if (!industry) {
      alert('Please select an industry first');
      return;
    }

    if (Object.keys(pages).length === 0) {
      alert('No analysis data available. Please run a scan first.');
      return;
    }

    updateStatus('processing', 'Generating AI recommendations...');

    try {
      // Prepare data for Claude API recommendations
      const analysisData = {
        siteUrl: siteUrl,
        industry: industry,
        currentStructure: Object.entries(classification).map(([type, pagesList]) => ({
          type,
          count: pagesList.length,
          pages: pagesList.map(p => ({
            url: p.url,
            title: p.title,
            confidence: p.confidence
          }))
        })),
        missingPages: industryRequirements[industry]?.filter(req => 
          !Object.keys(classification).some(ex => 
            ex.toLowerCase().includes(req.toLowerCase().split('/')[0])
          )
        ),
        totalPages: Object.keys(pages).length,
        classificationMethod: classificationMethod,
        model_used: selectedModel
      };

      console.log('Generating AI recommendations with data:', analysisData);

      // Call backend endpoint for AI recommendations (placeholder for now)
      // In production, you'd implement /api/claude/recommendations/ endpoint
      
      // For now, simulate the response
      setTimeout(() => {
        updateStatus('active', 'AI recommendations generated');
        console.log('AI Recommendations would be generated with this data:', analysisData);
        alert(`AI Recommendations ready!\n\nAnalyzed: ${analysisData.totalPages} pages\nMissing: ${analysisData.missingPages?.length || 0} required pages\nMethod: ${analysisData.classificationMethod}\n\nImplement /api/claude/recommendations/ endpoint for full functionality.`);
      }, 2000);

    } catch (error) {
      console.error('Error generating AI recommendations:', error);
      updateStatus('error', 'Failed to generate recommendations');
      alert('Failed to generate AI recommendations: ' + error.message);
    }
  };


  const offerDownload = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };


  const loadFromLocalStorage = () => {
    try {
      const saved = localStorage.getItem('seoAuditData');
      if (saved) {
        const parsed = JSON.parse(saved);
        
        // Load basic settings (these are still saved)
        setSiteUrl(parsed.siteUrl || '');
        setIndustry(parsed.industry || '');
        
        if (parsed.industryLocked !== undefined) {
          setIndustryLocked(parsed.industryLocked);
        }
        
        if (parsed.selectedExecution) {
          setSelectedExecution(parsed.selectedExecution);
        }
        
        if (parsed.classificationMethod) {
          setClassificationMethod(parsed.classificationMethod);
        }
        
        // Only set current execution if it exists (minimal data structure)
        if (parsed.currentExecution) {
          setCurrentExecution(parsed.currentExecution);
        }
        
        // Large datasets (pages, classification, benchmarks, etc.) are no longer saved 
        // to localStorage to prevent quota issues. They will be refetched when needed.
        
        console.log('✅ Loaded essential data from localStorage:', {
          siteUrl: parsed.siteUrl,
          industry: parsed.industry,
          industryLocked: parsed.industryLocked,
          selectedExecution: parsed.selectedExecution,
          hasCurrentExecution: !!parsed.currentExecution
        });
        
      } else {
        console.log('ℹ️ No saved data found in localStorage');
      }
    } catch (error) {
      console.error('❌ Error loading from localStorage:', error.message);
      console.warn('🧹 Clearing corrupted localStorage data');
      localStorage.removeItem('seoAuditData');
    }
  };

  // Calculate metrics
  const totalPages = Object.keys(pages).length;
  const pageTypes = Object.keys(classification).length;
  const cms = totalPages > 0 ? (Object.values(pages).find(p => p.cms)?.cms || 'Unknown') : '-';
  const schemaTypes = totalPages > 0 ? [...new Set(Object.values(pages).flatMap(p => p.schemaTypes || []))].length : 0;

  let missingPages = 0;
  if (totalPages > 0 && industry && industryRequirements[industry]) {
    const required = industryRequirements[industry];
    const existing = Object.keys(classification);
    missingPages = required.filter(req => 
      !existing.some(ex => ex.toLowerCase().includes(req.toLowerCase().split('/')[0]))
    ).length;
  }

  const seoScore = totalPages > 0 ? Math.round(((totalPages - missingPages) / totalPages) * 100) : null;

  const renderOverview = () => {
    if (Object.keys(pages).length === 0) {
      return (
        <div className="seo-alert seo-alert-info">
          No analysis data available. Click "Trigger Scan" to begin.
          {fileStatus && Object.keys(fileStatus).length > 0 && (
            <div style={{ marginTop: '10px', fontSize: '0.9rem', color: '#94a3b8' }}>
              <strong>Note:</strong> If you recently ran a scan but see no data, the scan may have failed to crawl pages. 
              Check that the website URL is accessible and not blocking crawlers.
            </div>
          )}
        </div>
      );
    }

    return (
      <>
        <h3>Site Summary</h3>
        <table className="seo-data-table">
          <thead>
            <tr>
              <th>Metric</th>
              <th>Value</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Website URL</td>
              <td>{siteUrl || '-'}</td>
              <td><span className="seo-badge seo-badge-info">Scanned</span></td>
            </tr>
            <tr>
              <td>Industry</td>
              <td>{industry || '-'}</td>
              <td><span className="seo-badge seo-badge-info">Selected</span></td>
            </tr>
            <tr>
              <td>Total Pages Analyzed</td>
              <td>{totalPages}</td>
              <td><span className="seo-badge seo-badge-success">Complete</span></td>
            </tr>
            <tr>
              <td>CMS Platform</td>
              <td>{cms}</td>
              <td><span className="seo-badge seo-badge-info">Detected</span></td>
            </tr>
          </tbody>
        </table>
        
        {/* File Processing Status */}
        {Object.keys(fileStatus).length > 0 && (
          <>
            <h3>File Processing Status</h3>
            <div className="seo-file-status-grid">
              {[
                'page_titles_all.csv',
                'meta_description_all.csv',
                'custom_extraction_all.csv',
                'structured_data_all.csv',
                'crawl_overview.csv'
              ].map(filename => {
                const status = fileStatus[filename] || 'pending';
                const iconClass = status === 'success' ? 'success' : 
                                 status === 'error' ? 'error' : 
                                 status === 'missing' ? 'warning' : 'pending';
                
                return (
                  <div key={filename} className="seo-file-status-item">
                    <div className={`seo-file-status-icon ${iconClass}`}></div>
                    <span>{filename.replace('_', ' ').replace('.csv', '')}</span>
                  </div>
                );
              })}
            </div>
          </>
        )}
        
        {/* Crawl Statistics */}
        {Object.keys(crawlStats).length > 0 && (
          <>
            <h3>Crawl Statistics</h3>
            <table className="seo-data-table">
              <tbody>
                <tr>
                  <td>Crawl Date</td>
                  <td>{crawlStats.crawlDate || '-'}</td>
                </tr>
                <tr>
                  <td>Total URLs</td>
                  <td>{crawlStats.totalUrls || '-'}</td>
                </tr>
                <tr>
                  <td>HTML Pages</td>
                  <td>{crawlStats.totalHtml || '-'}</td>
                </tr>
                <tr>
                  <td>Indexable Pages</td>
                  <td>{crawlStats.totalIndexable || '-'}</td>
                </tr>
                <tr>
                  <td>Non-Indexable Pages</td>
                  <td>{crawlStats.totalNonIndexable || '-'}</td>
                </tr>
              </tbody>
            </table>
          </>
        )}
      </>
    );
  };

  const renderStructure = () => {
    if (isAiAnalyzing) {
      return (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div className="seo-loading-spinner"></div>
          <p style={{ marginTop: '20px', color: '#10b981' }}>
            🤖 AI analysis in progress...
          </p>
          <p style={{ color: '#94a3b8', fontSize: '0.9rem', marginTop: '10px' }}>
            Site structure will update once classification is complete
          </p>
        </div>
      );
    }

    if (Object.keys(pages).length === 0) {
      // Check if scan was run but no pages found
      if (Object.keys(fileStatus).length > 0) {
        return (
          <div className="seo-alert seo-alert-warning">
            <h4>No Site Structure Data</h4>
            <p>The scan completed but no pages were found to analyze. This usually indicates:</p>
            <ul style={{ marginTop: '10px', paddingLeft: '20px' }}>
              <li>Website is blocking crawlers</li>
              <li>URL is inaccessible or incorrect</li>
              <li>Site requires authentication to view content</li>
            </ul>
          </div>
        );
      }
      return <p>No data available</p>;
    }

    return (
      <table className="seo-data-table">
        <thead>
          <tr>
            <th>URL</th>
            <th>Title</th>
            <th>Type</th>
            <th>Schema</th>
            <th>CMS</th>
          </tr>
        </thead>
        <tbody>
          {Object.values(pages).map((page, index) => (
            <tr key={index}>
              <td>{page.url}</td>
              <td>{page.title || 'No title'}</td>
              <td>
                <span className="seo-badge seo-badge-info">{page.classification || 'Unknown'}</span>
                {page.confidence && (
                  <div style={{ fontSize: '0.75rem', color: '#10b981', marginTop: '2px' }}>
                    🤖 {Math.round(page.confidence * 100)}%
                  </div>
                )}
              </td>
              <td>{page.schemaTypes?.length || 0} types</td>
              <td>{page.cms || '-'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const renderClassification = () => {
    if (isAiAnalyzing) {
      return (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div className="seo-loading-spinner"></div>
          <p style={{ marginTop: '20px', color: '#10b981' }}>
            🤖 AI is analyzing {Object.keys(pages).length} pages...
          </p>
          <p style={{ color: '#94a3b8', fontSize: '0.9rem', marginTop: '10px' }}>
            This may take 30-60 seconds depending on the number of pages
          </p>
        </div>
      );
    }

    if (Object.keys(classification).length === 0) {
      // Check if scan was run but no pages found
      if (Object.keys(fileStatus).length > 0) {
        return (
          <div className="seo-alert seo-alert-warning">
            <h4>Scan Completed But No Pages Found</h4>
            <p>The scan finished successfully but didn't find any crawlable pages. This could happen if:</p>
            <ul style={{ marginTop: '10px', paddingLeft: '20px' }}>
              <li>The website is blocking crawlers (robots.txt, user-agent blocking)</li>
              <li>The URL is incorrect or inaccessible</li>
              <li>The site requires authentication</li>
              <li>JavaScript-heavy site that doesn't render content for crawlers</li>
            </ul>
            <p style={{ marginTop: '10px' }}>
              <strong>Recommendation:</strong> Verify the URL is accessible and try running a new scan.
            </p>
          </div>
        );
      }
      return <p>No data available</p>;
    }

    // Calculate AI vs Rules breakdown
    const allPages = Object.values(pages);
    const aiPages = allPages.filter(p => p.classificationMethod === 'AI').length;
    const rulesPages = allPages.filter(p => p.classificationMethod === 'Rules').length;

    return (
      <>
        {/* Classification Method Summary */}
        {(aiPages > 0 || rulesPages > 0) && (
          <div style={{ 
            background: '#1e293b', 
            padding: '12px', 
            borderRadius: '8px', 
            marginBottom: '20px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div style={{ fontSize: '0.9rem', color: '#e2e8f0' }}>
              <strong>Classification Methods:</strong>
            </div>
            <div style={{ display: 'flex', gap: '15px' }}>
              {aiPages > 0 && (
                <span style={{ 
                  fontSize: '0.8rem', 
                  color: '#10b981', 
                  background: 'rgba(16, 185, 129, 0.1)',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  fontWeight: '600'
                }}>
                  🤖 AI: {aiPages} pages
                </span>
              )}
              {rulesPages > 0 && (
                <span style={{ 
                  fontSize: '0.8rem', 
                  color: '#f59e0b', 
                  background: 'rgba(245, 158, 11, 0.1)',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  fontWeight: '600'
                }}>
                  ⚙️ Rules: {rulesPages} pages
                </span>
              )}
            </div>
          </div>
        )}

        {/* AI Classification Management */}
        {Object.keys(classification).length > 0 && (
          <div style={{ 
            background: '#0f172a', 
            padding: '15px', 
            borderRadius: '8px',
            marginBottom: '20px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div>
              <div style={{ fontSize: '0.9rem', color: '#e2e8f0', marginBottom: '5px' }}>
                <strong>🤖 AI Classification Management</strong>
              </div>
              <div style={{ fontSize: '0.8rem', color: '#94a3b8' }}>
                Re-run AI classification to update page categorizations with latest analysis
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <button 
                className="seo-btn-secondary"
                onClick={() => {
                  checkExistingScreenshots();
                }}
                title="Refresh screenshot statuses"
              >
                🔄 Refresh Screenshots
              </button>
              <button 
                className="seo-btn-secondary"
                onClick={refreshClassifications}
                title="Refresh classification data from server"
              >
                📥 Refresh Classifications
              </button>
              <button 
                className="seo-btn-secondary"
                disabled={isAiAnalyzing}
                onClick={async () => {
                  console.log('=== RE-CLASSIFY BUTTON CLICKED (Top Section) ===');
                  console.log('Pages state:', pages);
                  console.log('Pages keys:', Object.keys(pages));
                  console.log('Pages length:', Object.keys(pages).length);
                  console.log('Current execution:', currentExecution);
                  
                  if (Object.keys(pages).length > 0) {
                    // Show model selection modal
                    showModelSelectionModal(async (modelToUse) => {
                      console.log('🤖 Using selected model:', modelToUse);
                      
                    // Find the execution to use for re-classification
                    let execForClaude = null;
                    
                    if (currentExecution?.executionId) {
                      execForClaude = currentExecution;
                      console.log('Using current execution for re-classify:', execForClaude);
                    } else if (selectedExecution?.executionId) {
                      execForClaude = selectedExecution;
                      console.log('Using selected execution for re-classify:', execForClaude);
                    } else {
                      console.warn('No execution found for re-classification');
                    }
                    
                    console.log('Re-classify using execution:', execForClaude);
                    
                    // For re-classification, always call Claude API (don't load existing)
                      await performFullAnalysisWithPages(pages, execForClaude, modelToUse);
                      
                      // Update the persisted selected model after successful operation
                      setSelectedModel(modelToUse);
                    });
                  } else {
                    alert('No pages to classify. Please run a scan first.');
                  }
                }}
              >
                {isAiAnalyzing ? '🤖 Analyzing...' : 'Re-run AI Classification'}
              </button>
            </div>
          </div>
        )}


        <div className="seo-classification-grid">
        {(!industry ? Object.keys(classification) : industryRequirements[industry] || []).map((type) => {
          // Get actual pages for this classification type (if any)
          const pagesList = classification[type] || [];
          const isEmpty = pagesList.length === 0;
          const isSinglePage = pagesList.length === 1;
          
          // Find screenshots for this classification
          const relevantUrl = isSinglePage ? pagesList[0].url : selectedUrls[type];
          const desktopScreenshot = existingScreenshots.find(s => 
            s.tag === type && s.device === 'desktop' && s.url === relevantUrl
          );
          const phoneScreenshot = existingScreenshots.find(s => 
            s.tag === type && s.device === 'phone' && s.url === relevantUrl
          );
          
          return (
            <div key={type} className="seo-classification-card">
              <div className="seo-classification-header">
                <span className="seo-classification-title">
                  {type}
                  {/* Show classification method indicator */}
                  {!isEmpty && pagesList[0]?.classificationMethod === 'AI' && pagesList[0]?.confidence !== 1.1 && (
                    <span style={{ 
                      fontSize: '0.75rem', 
                      color: '#10b981', 
                      marginLeft: '8px',
                      fontWeight: 'normal',
                      background: 'rgba(16, 185, 129, 0.1)',
                      padding: '2px 6px',
                      borderRadius: '4px'
                    }}>
                      🤖 AI {pagesList[0]?.confidence ? `${Math.round(pagesList[0].confidence * 100)}%` : ''}
                    </span>
                  )}
                  {!isEmpty && pagesList[0]?.classificationMethod === 'Rules' && (
                    <span style={{ 
                      fontSize: '0.75rem', 
                      color: '#f59e0b', 
                      marginLeft: '8px',
                      fontWeight: 'normal',
                      background: 'rgba(245, 158, 11, 0.1)',
                      padding: '2px 6px',
                      borderRadius: '4px'
                    }}>
                      ⚙️ Rules
                    </span>
                  )}
                  {!isEmpty && pagesList[0]?.confidence === 1.1 && (
                    <span style={{ 
                      fontSize: '0.75rem', 
                      color: '#8b5cf6', 
                      marginLeft: '8px',
                      fontWeight: 'normal',
                      background: 'rgba(139, 92, 246, 0.1)',
                      padding: '2px 6px',
                      borderRadius: '4px'
                    }}>
                      👤 Manual
                    </span>
                  )}
                  {isEmpty && (
                    <span style={{ 
                      fontSize: '0.75rem', 
                      color: '#ef4444', 
                      marginLeft: '8px',
                      fontWeight: 'normal',
                      background: 'rgba(239, 68, 68, 0.1)',
                      padding: '2px 6px',
                      borderRadius: '4px'
                    }}>
                      ❌ Missing
                    </span>
                  )}
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <button
                    onClick={() => {
                      if (isEmpty) {
                        // For empty cards, show add URL form
                        setEditingCard(type);
                        setEditUrlInputs(prev => ({ ...prev, [type]: '' }));
                      } else {
                        // For cards with content, make first URL editable
                        const firstUrl = pagesList[0]?.url;
                        if (firstUrl) {
                          setEditingUrl(firstUrl);
                          setEditUrlInputs(prev => ({ ...prev, [firstUrl]: firstUrl }));
                        }
                      }
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#94a3b8',
                      cursor: 'pointer',
                      fontSize: '1rem',
                      padding: '4px',
                      borderRadius: '4px',
                      opacity: (editingCard === type || editingUrl === pagesList[0]?.url) ? '1' : '0.7'
                    }}
                    title={isEmpty ? "Add URL to this classification" : "Edit URL"}
                  >
                    ✏️
                  </button>
                  <span className="seo-classification-count">
                    {pagesList.length}
                  </span>
                </div>
              </div>
              
              {/* URL List with selection for multi-page */}
              <div className="seo-url-list">
                {isEmpty ? (
                  editingCard === type ? (
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      padding: '8px 0'
                    }}>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flex: 1 }}>
                        <input
                          type="text"
                          value={editUrlInputs[type] || ''}
                          onChange={(e) => setEditUrlInputs(prev => ({ ...prev, [type]: e.target.value }))}
                          placeholder="https://example.com/page"
                          style={{
                            flex: 1,
                            padding: '4px 8px',
                            background: '#0f172a',
                            border: '1px solid #8b5cf6',
                            borderRadius: '4px',
                            color: '#e2e8f0',
                            fontSize: '0.9rem'
                          }}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              addUrlToClassification(type, editUrlInputs[type] || '');
                            }
                          }}
                        />
                        <button
                          onClick={() => addUrlToClassification(type, editUrlInputs[type] || '')}
                          style={{
                            background: '#8b5cf6',
                            border: 'none',
                            color: 'white',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '0.7rem',
                            cursor: 'pointer'
                          }}
                        >
                          Save
                        </button>
                        <button
                          onClick={() => {
                            setEditingCard(null);
                            setEditUrlInputs(prev => {
                              const updated = { ...prev };
                              delete updated[type];
                              return updated;
                            });
                          }}
                          style={{
                            background: '#475569',
                            border: 'none',
                            color: 'white',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '0.7rem',
                            cursor: 'pointer'
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ 
                      fontSize: '0.8rem', 
                      color: '#94a3b8', 
                      padding: '20px',
                      textAlign: 'center',
                      fontStyle: 'italic'
                    }}>
                      No pages found for this classification
                    </div>
                  )
                ) : (
                  <>
                    {!isSinglePage && (
                      <div style={{ 
                        fontSize: '0.8rem', 
                        color: '#94a3b8', 
                        marginBottom: '8px',
                        fontStyle: 'italic'
                      }}>
                        Select one URL for screenshot capture:
                      </div>
                    )}
                    {pagesList.map((page, index) => (
                      <div key={index} className="seo-url-item" style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        padding: '8px 0'
                      }}>
                        <div style={{ flex: 1 }}>
                          {editingUrl === page.url ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                              <input
                                type="text"
                                value={editUrlInputs[page.url] || ''}
                                onChange={(e) => setEditUrlInputs(prev => ({ ...prev, [page.url]: e.target.value }))}
                                style={{
                                  width: '100%',
                                  padding: '4px 8px',
                                  background: '#0f172a',
                                  border: '1px solid #8b5cf6',
                                  borderRadius: '4px',
                                  color: '#e2e8f0',
                                  fontSize: '0.9rem'
                                }}
                                onKeyPress={(e) => {
                                  if (e.key === 'Enter') {
                                    updateUrlClassification(page.url, editUrlInputs[page.url] || '', type);
                                  }
                                }}
                              />
                              {deletingCard === type ? (
                                <div style={{ 
                                  background: '#450a0a', 
                                  padding: '8px', 
                                  borderRadius: '4px',
                                  border: '1px solid #991b1b'
                                }}>
                                  <div style={{ color: '#fecaca', fontSize: '0.8rem', marginBottom: '8px' }}>
                                    Delete URL in "{type}" classification?
                                  </div>
                                  <div style={{ display: 'flex', gap: '4px', justifyContent: 'flex-end' }}>
                                    <button
                                      onClick={() => {
                                        deleteClassificationType(type);
                                      }}
                                      style={{
                                        background: '#991b1b',
                                        border: 'none',
                                        color: 'white',
                                        padding: '4px 8px',
                                        borderRadius: '4px',
                                        fontSize: '0.7rem',
                                        cursor: 'pointer'
                                      }}
                                    >
                                      Delete
                                    </button>
                                    <button
                                      onClick={() => setDeletingCard(null)}
                                      style={{
                                        background: '#475569',
                                        border: 'none',
                                        color: 'white',
                                        padding: '4px 8px',
                                        borderRadius: '4px',
                                        fontSize: '0.7rem',
                                        cursor: 'pointer'
                                      }}
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <div style={{ display: 'flex', gap: '4px', justifyContent: 'flex-end' }}>
                                  <button
                                    onClick={() => updateUrlClassification(page.url, editUrlInputs[page.url] || '', type)}
                                    style={{
                                      background: '#8b5cf6',
                                      border: 'none',
                                      color: 'white',
                                      padding: '4px 8px',
                                      borderRadius: '4px',
                                      fontSize: '0.7rem',
                                      cursor: 'pointer'
                                    }}
                                  >
                                    Save
                                  </button>
                                  <button
                                    onClick={() => setDeletingCard(type)}
                                    style={{
                                      background: '#991b1b',
                                      border: 'none',
                                      color: 'white',
                                      padding: '4px 8px',
                                      borderRadius: '4px',
                                      fontSize: '0.7rem',
                                      cursor: 'pointer'
                                    }}
                                  >
                                    Delete
                                  </button>
                                  <button
                                    onClick={() => {
                                      setEditingUrl(null);
                                      setEditUrlInputs(prev => {
                                        const updated = { ...prev };
                                        delete updated[page.url];
                                        return updated;
                                      });
                                    }}
                                    style={{
                                      background: '#475569',
                                      border: 'none',
                                      color: 'white',
                                      padding: '4px 8px',
                                      borderRadius: '4px',
                                      fontSize: '0.7rem',
                                      cursor: 'pointer'
                                    }}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              )}
                            </div>
                          ) : (
                            <div style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{page.url}</div>
                          )}
                          {page.reasoning && editingUrl !== page.url && (
                            <div style={{ 
                              fontSize: '0.75rem', 
                              color: page.confidence === 1.1 ? '#8b5cf6' : '#64748b', 
                              marginTop: '2px',
                              fontStyle: 'italic'
                            }}>
                              {page.confidence === 1.1 ? 'Human feedback' : page.reasoning}
                            </div>
                          )}
                        </div>
                        {!isSinglePage && editingUrl !== page.url && (
                          <input
                            type="radio"
                            name={`url-${type}`}
                            value={page.url}
                            checked={selectedUrls[type] === page.url}
                            onChange={(e) => setSelectedUrls(prev => ({ ...prev, [type]: e.target.value }))}
                            style={{ marginLeft: '10px' }}
                          />
                        )}
                      </div>
                    ))}
                  </>
                )}
              </div>


              {/* Screenshot status and controls */}
              {!isEmpty && (isSinglePage || selectedUrls[type]) && (
                <div style={{ marginTop: '15px', padding: '10px', background: '#1e293b', borderRadius: '6px' }}>
                  {/* Screenshot status */}
                  {(screenshotStatus[`${type}_desktop`] === 'processing' || screenshotStatus[`${type}_phone`] === 'processing') && (
                    <div style={{ color: '#f59e0b', fontSize: '0.85rem', marginBottom: '8px' }}>
                      📸 Processing screenshots...
                    </div>
                  )}
                  
                  {loadingScreenshots && !desktopScreenshot && !phoneScreenshot && (
                    <div style={{ color: '#94a3b8', fontSize: '0.85rem', marginBottom: '8px' }}>
                      🔄 Checking for existing screenshots...
                    </div>
                  )}
                  
                  {/* Desktop Screenshots */}
                  <div style={{ marginBottom: '10px' }}>
                    <div style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#e2e8f0', marginBottom: '6px' }}>
                      💻 Desktop
                    </div>
                    {desktopScreenshot ? (
                      <div style={{ fontSize: '0.85rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                          <span style={{ color: desktopScreenshot.status === 'completed' ? '#10b981' : '#ef4444' }}>
                            📸 {desktopScreenshot.status === 'completed' ? 'Screenshot Available' : 'Screenshot Failed'}
                          </span>
                        </div>
                        {desktopScreenshot.screenshot_url && (
                          <div>
                            <a 
                              href={desktopScreenshot.screenshot_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              style={{ color: '#3b82f6', textDecoration: 'none', fontSize: '0.8rem' }}
                            >
                              🔗 View Desktop Screenshot
                            </a>
                            <span style={{ color: '#94a3b8', marginLeft: '10px', fontSize: '0.8rem' }}>
                              {new Date(desktopScreenshot.updated_at).toLocaleDateString()} {new Date(desktopScreenshot.updated_at).toLocaleTimeString()}
                            </span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <button
                        className="seo-btn-primary"
                        style={{ fontSize: '0.8rem', padding: '6px 12px', marginRight: '8px' }}
                        onClick={() => triggerScreenshotForUrl(type, relevantUrl, 'desktop')}
                        disabled={screenshotStatus[`${type}_desktop`] === 'processing'}
                      >
                        📸 Capture Desktop
                      </button>
                    )}
                  </div>

                  {/* Phone Screenshots */}
                  <div>
                    <div style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#e2e8f0', marginBottom: '6px' }}>
                      📱 Mobile
                    </div>
                    {phoneScreenshot ? (
                      <div style={{ fontSize: '0.85rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                          <span style={{ color: phoneScreenshot.status === 'completed' ? '#10b981' : '#ef4444' }}>
                            📸 {phoneScreenshot.status === 'completed' ? 'Screenshot Available' : 'Screenshot Failed'}
                          </span>
                        </div>
                        {phoneScreenshot.screenshot_url && (
                          <div>
                            <a 
                              href={phoneScreenshot.screenshot_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              style={{ color: '#3b82f6', textDecoration: 'none', fontSize: '0.8rem' }}
                            >
                              🔗 View Mobile Screenshot
                            </a>
                            <span style={{ color: '#94a3b8', marginLeft: '10px', fontSize: '0.8rem' }}>
                              {new Date(phoneScreenshot.updated_at).toLocaleDateString()} {new Date(phoneScreenshot.updated_at).toLocaleTimeString()}
                            </span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <button
                        className="seo-btn-primary"
                        style={{ fontSize: '0.8rem', padding: '6px 12px' }}
                        onClick={() => triggerScreenshotForUrl(type, relevantUrl, 'phone')}
                        disabled={screenshotStatus[`${type}_phone`] === 'processing'}
                      >
                        📱 Capture Mobile
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
        </div>
      </>
    );
  };

  const renderGapAnalysis = () => {
    if (isAiAnalyzing) {
      return (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div className="seo-loading-spinner"></div>
          <p style={{ marginTop: '20px', color: '#10b981' }}>
            🤖 AI analysis in progress...
          </p>
          <p style={{ color: '#94a3b8', fontSize: '0.9rem', marginTop: '10px' }}>
            Gap analysis will update once classification is complete
          </p>
        </div>
      );
    }

    if (!industry) {
      return <p>Select an industry to see gap analysis</p>;
    }
    
    if (Object.keys(pages).length === 0) {
      // Check if scan was run but no pages found
      if (Object.keys(fileStatus).length > 0) {
        return (
          <div className="seo-alert seo-alert-warning">
            <h4>Cannot Perform Gap Analysis</h4>
            <p>No pages were found in the scan results, so gap analysis cannot be performed.</p>
            <p style={{ marginTop: '10px' }}>
              <strong>All required page types are missing</strong> because the scan didn't find any pages to analyze.
              Please ensure the website is accessible and run a new scan.
            </p>
          </div>
        );
      }
      return <p>Run analysis to see gap analysis</p>;
    }

    const required = industryRequirements[industry];
    const existing = Object.keys(classification);

    return (
      <>
        <h3>Industry: {industry}</h3>
        {required.map((req, index) => {
          const found = existing.some(ex => 
            ex.toLowerCase().includes(req.toLowerCase().split('/')[0]) ||
            req.toLowerCase().includes(ex.toLowerCase())
          );
          
          return (
            <div key={index} className={`seo-gap-item ${found ? 'present' : 'missing'}`}>
              <div className="seo-gap-header">
                <span className="seo-gap-status">{found ? '✓' : '✗'}</span>
                <span className="seo-gap-title">{req}</span>
              </div>
              <div className="seo-gap-description">
                {found ? 'Page type detected on site' : 'Missing - recommended to add this page type'}
              </div>
            </div>
          );
        })}
      </>
    );
  };

  const renderSchemaAudit = () => {
    // Check if custom_extraction_all.csv was missing
    const customExtractionMissing = fileStatus['custom_extraction_all.csv'] === 'missing';
    
    if (customExtractionMissing) {
      return (
        <div className="seo-alert seo-alert-warning">
          <h4>⚠️ Custom Extraction File Missing</h4>
          <p><strong>custom_extraction_all.csv</strong> is not present in the uploaded ZIP file.</p>
          <p>This file is required for Schema Audit functionality. Please ensure your Screaming Frog export includes custom extraction data.</p>
        </div>
      );
    }

    if (Object.keys(classification).length === 0) {
      return (
        <div className="seo-alert seo-alert-info">
          <p>Schema Audit will be available after page classification is completed.</p>
        </div>
      );
    }

    if (schemaAuditData.length === 0) {
      return (
        <div className="seo-alert seo-alert-info">
          <h4>No Schema.org Data Found</h4>
          <p>No pages with valid schema.org JSON-LD markup were found in the scan results.</p>
          <p>This could mean:</p>
          <ul style={{ marginTop: '10px', paddingLeft: '20px' }}>
            <li>Website doesn't use schema.org markup</li>
            <li>Schema markup is not in JSON-LD format</li>
            <li>Pages have non-200 status codes</li>
          </ul>
        </div>
      );
    }

    return (
      <>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '20px' 
        }}>
          <div style={{ fontSize: '0.9rem', color: '#94a3b8' }}>
            Found <strong>{schemaAuditData.filter(item => {
              const page = pages[item.address];
              const pageType = page?.classification;
              return pageType && pageType !== 'Unknown' && pageType.trim() !== '';
            }).length}</strong> classified pages with schema.org JSON-LD markup
            {schemaAuditData.length > 0 && (
              <span style={{ color: '#94a3b8', marginLeft: '10px' }}>
                (Total pages with schema: {schemaAuditData.length})
              </span>
            )}
          </div>
          
          {/* Run All Button */}
          {schemaAuditData.filter(item => {
            const page = pages[item.address];
            const pageType = page?.classification;
            return pageType && pageType !== 'Unknown' && pageType.trim() !== '';
          }).length > 1 && (
            <button
              className="seo-btn-primary"
              onClick={runAllSchemaAuditsWithModal}
              disabled={Object.values(loadingSchemaAudits).some(loading => loading)}
              style={{ 
                fontSize: '0.9rem',
                padding: '8px 16px'
              }}
            >
              {Object.values(loadingSchemaAudits).some(loading => loading) ? 
                '🤖 Running Audits...' : 
                '🚀 Run All Schema Audits'
              }
            </button>
          )}
        </div>
        
        <div className="schema-audit-container">
          {schemaAuditData
            .filter(item => {
              // Only show pages with valid classification
              const page = pages[item.address];
              const pageType = page?.classification;
              return pageType && pageType !== 'Unknown' && pageType.trim() !== '';
            })
            .map((item, index) => {
            // Find the page type from classification
            const page = pages[item.address];
            const pageType = page?.classification || 'Unknown';
            const auditKey = `${item.address}_${pageType}`;
            const auditResult = schemaAuditResults[auditKey];
            const isLoading = loadingSchemaAudits[auditKey];
            
            return (
              <div key={index} style={{
                marginBottom: '25px',
                border: '1px solid #334155',
                borderRadius: '12px',
                overflow: 'hidden',
                background: '#1e293b'
              }}>
                {/* Header Row */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '300px 1fr 150px',
                  gap: '20px',
                  padding: '20px',
                  borderBottom: auditResult ? '1px solid #334155' : 'none'
                }}>
                  {/* Page Info */}
                  <div>
                    <div style={{ marginBottom: '8px' }}>
                      <span className="seo-badge seo-badge-info">{pageType}</span>
                    </div>
                    <div>
                      <a 
                        href={item.address} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{ 
                          color: '#3b82f6', 
                          textDecoration: 'none',
                          fontSize: '0.85rem',
                          wordBreak: 'break-all'
                        }}
                      >
                        {item.address}
                      </a>
                    </div>
                  </div>

                  {/* Schema Data */}
                  <div style={{ width: '100%' }}>
                    {item.schema_org.map((schema, schemaIndex) => {
                      // Determine schema types - handle @graph structure
                      let schemaTypes = [];
                      if (schema['@graph'] && Array.isArray(schema['@graph'])) {
                        // Extract types from @graph array
                        schemaTypes = schema['@graph']
                          .map(item => item['@type'])
                          .filter(type => type)
                          .join(', ');
                      } else if (schema['@type']) {
                        schemaTypes = Array.isArray(schema['@type']) 
                          ? schema['@type'].join(', ')
                          : schema['@type'];
                      } else {
                        schemaTypes = 'Schema.org Data';
                      }

                      return (
                        <div key={schemaIndex} style={{ 
                          marginBottom: '15px',
                          border: '1px solid #334155',
                          borderRadius: '8px',
                          overflow: 'hidden'
                        }}>
                          <div style={{ 
                            background: '#0f172a',
                            padding: '8px 12px',
                            borderBottom: '1px solid #334155'
                          }}>
                            <span style={{ fontWeight: '600', color: '#10b981' }}>
                              {schemaTypes}
                            </span>
                          </div>
                          <div style={{ height: '200px' }}>
                            <AceEditor
                              mode="json"
                              theme="monokai"
                              value={JSON.stringify(schema, null, 2)}
                              readOnly={true}
                              showPrintMargin={false}
                              showGutter={true}
                              highlightActiveLine={false}
                              width="100%"
                              height="200px"
                              fontSize={11}
                              setOptions={{
                                enableBasicAutocompletion: false,
                                enableLiveAutocompletion: false,
                                enableSnippets: false,
                                showLineNumbers: true,
                                tabSize: 2,
                                wrap: false,
                                foldStyle: 'markbegin',
                                useWorker: false
                              }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Actions */}
                  <div>
                    <button
                      className="seo-btn-secondary"
                      onClick={() => runSchemaAuditWithModal(item.address, pageType, item.schema_org)}
                      disabled={isLoading}
                      style={{ 
                        fontSize: '0.8rem', 
                        padding: '6px 12px',
                        whiteSpace: 'nowrap',
                        width: '100%'
                      }}
                    >
                      {isLoading ? '🤖 Analyzing...' : 'Run Schema Audit'}
                    </button>
                  </div>
                </div>

                {/* Audit Results Section */}
                {auditResult && (
                  <div style={{ 
                    background: '#0f172a',
                    padding: '20px'
                  }}>
                    {/* Compliance Score Header */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '20px',
                      padding: '15px',
                      background: '#1e293b',
                      borderRadius: '8px'
                    }}>
                      <h4 style={{ margin: 0, color: '#f8fafc' }}>
                        🔍 Schema Audit Results
                      </h4>
                      <div style={{
                        padding: '6px 12px',
                        borderRadius: '8px',
                        fontSize: '0.85rem',
                        fontWeight: '600',
                        backgroundColor: auditResult.compliance_score >= 80 ? '#059669' :
                                        auditResult.compliance_score >= 60 ? '#ca8a04' : '#b91c1c',
                        color: 'white'
                      }}>
                        {auditResult.compliance_score}% Compliance
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '25px' }}>
                      {/* Critical Issues */}
                      <div style={{
                        background: '#1e293b',
                        padding: '15px',
                        borderRadius: '8px',
                        border: '1px solid #374151'
                      }}>
                        <h5 style={{ color: '#ef4444', margin: '0 0 12px 0', fontSize: '1rem' }}>
                          🚨 Critical Issues ({auditResult.critical_issues?.length || 0})
                        </h5>
                        {auditResult.critical_issues?.length > 0 ? (
                          <ul style={{ margin: 0, paddingLeft: '18px', color: '#f8fafc' }}>
                            {auditResult.critical_issues.map((issue, issueIndex) => (
                              <li key={issueIndex} style={{ 
                                marginBottom: '8px', 
                                fontSize: '0.9rem',
                                lineHeight: '1.4'
                              }}>
                                {issue}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p style={{ color: '#94a3b8', fontSize: '0.9rem', margin: 0 }}>
                            🎉 No critical issues found
                          </p>
                        )}
                      </div>
                      
                      {/* Recommendations */}
                      <div style={{
                        background: '#1e293b',
                        padding: '15px',
                        borderRadius: '8px',
                        border: '1px solid #374151'
                      }}>
                        <h5 style={{ color: '#10b981', margin: '0 0 12px 0', fontSize: '1rem' }}>
                          💡 Recommendations ({auditResult.recommended_improvements?.length || 0})
                        </h5>
                        {auditResult.recommended_improvements?.length > 0 ? (
                          <ul style={{ margin: 0, paddingLeft: '18px', color: '#f8fafc' }}>
                            {auditResult.recommended_improvements.map((rec, recIndex) => (
                              <li key={recIndex} style={{ 
                                marginBottom: '8px', 
                                fontSize: '0.9rem',
                                lineHeight: '1.4'
                              }}>
                                {rec}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p style={{ color: '#94a3b8', fontSize: '0.9rem', margin: 0 }}>
                            No additional recommendations
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Schema Analysis Summary */}
                    <div style={{
                      background: '#1e293b',
                      padding: '15px',
                      borderRadius: '8px',
                      border: '1px solid #374151',
                      marginBottom: '20px'
                    }}>
                      <h5 style={{ color: '#3b82f6', margin: '0 0 12px 0', fontSize: '1rem' }}>
                        📊 Schema Analysis Summary
                      </h5>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px', fontSize: '0.85rem' }}>
                        <div>
                          <div style={{ color: '#10b981', fontWeight: '600', marginBottom: '4px' }}>
                            ✅ Current Schemas ({auditResult.current_schemas?.length || 0})
                          </div>
                          <div style={{ color: '#94a3b8' }}>
                            {auditResult.current_schemas?.join(', ') || 'None'}
                          </div>
                        </div>
                        <div>
                          <div style={{ color: '#ef4444', fontWeight: '600', marginBottom: '4px' }}>
                            ❌ Missing Schemas ({auditResult.missing_schemas?.length || 0})
                          </div>
                          <div style={{ color: '#94a3b8' }}>
                            {auditResult.missing_schemas?.join(', ') || 'None'}
                          </div>
                        </div>
                        <div>
                          <div style={{ color: '#3b82f6', fontWeight: '600', marginBottom: '4px' }}>
                            🎯 Expected Schemas ({auditResult.expected_schemas?.length || 0})
                          </div>
                          <div style={{ color: '#94a3b8' }}>
                            {auditResult.expected_schemas?.join(', ') || 'None'}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Optimized Schema */}
                    {auditResult.optimized_schema && (
                      <div>
                        <h5 style={{ color: '#a855f7', margin: '0 0 15px 0', fontSize: '1rem' }}>
                          🛠️ Optimized Schema JSON-LD (Ready to Implement)
                        </h5>
                        <div style={{ 
                          height: '400px',
                          border: '1px solid #334155',
                          borderRadius: '8px',
                          overflow: 'hidden'
                        }}>
                          <AceEditor
                            mode="json"
                            theme="monokai"
                            value={JSON.stringify(auditResult.optimized_schema, null, 2)}
                            readOnly={true}
                            showPrintMargin={false}
                            showGutter={true}
                            highlightActiveLine={false}
                            width="100%"
                            height="400px"
                            fontSize={12}
                            setOptions={{
                              enableBasicAutocompletion: false,
                              enableLiveAutocompletion: false,
                              enableSnippets: false,
                              showLineNumbers: true,
                              tabSize: 2,
                              wrap: false,
                              foldStyle: 'markbegin',
                              useWorker: false
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

      </>
    );
  };

  const renderApiData = () => {
    const apiData = {
      execution: currentExecution,
      analysis: {
        siteUrl,
        industry,
        pages: Object.values(pages).map(p => ({
          url: p.url,
          title: p.title,
          classification: p.classification,
          cms: p.cms,
          schemaTypes: p.schemaTypes
        })),
        classification: Object.entries(classification).map(([type, pagesList]) => ({
          type,
          count: pagesList.length,
          urls: pagesList.map(p => p.url)
        })),
        gapAnalysis: industryRequirements[industry]?.map(req => ({
          requirement: req,
          status: Object.keys(classification).some(ex => 
            ex.toLowerCase().includes(req.toLowerCase().split('/')[0])
          ) ? 'present' : 'missing'
        }))
      }
    };

    return (
      <div className="seo-api-output">
        <pre>{JSON.stringify(apiData, null, 2)}</pre>
      </div>
    );
  };

  const renderBenchmark = () => {
    console.log('🔍 BENCHMARK RENDER DEBUG:');
    console.log('industry:', industry);
    console.log('currentExecution:', currentExecution);
    console.log('classification keys:', Object.keys(classification));
    console.log('activeSection:', activeSection);
    console.log('benchmarkExecutions length:', benchmarkExecutions.length);
    console.log('selectedBenchmarkExecution:', selectedBenchmarkExecution);
    
    if (!industry) {
      console.log('❌ Benchmark blocked: No industry');
      return (
        <div className="seo-alert seo-alert-info">
          <p>Please select an industry first to access benchmark analysis.</p>
        </div>
      );
    }

    if (!currentExecution?.executionId || Object.keys(classification).length === 0) {
      console.log('❌ Benchmark blocked: No execution or classification data');
      console.log('currentExecution?.executionId:', currentExecution?.executionId);
      console.log('classification length:', Object.keys(classification).length);
      return (
        <div className="seo-alert seo-alert-info">
          <p>Please run a scan first to access benchmark analysis.</p>
        </div>
      );
    }

    return (
      <>
        {/* Benchmark Control Panel */}
        <div style={{ 
          background: '#1e293b', 
          padding: '20px', 
          borderRadius: '12px', 
          marginBottom: '30px' 
        }}>
          <h3 style={{ marginTop: 0, marginBottom: '15px', color: '#f1f5f9' }}>
            Compare Against Industry Competitors
          </h3>
          
          <div style={{ display: 'flex', alignItems: 'end', gap: '20px', flexWrap: 'wrap' }}>
            <div className="seo-input-group" style={{ flex: 1, minWidth: '300px' }}>
              <label>Select Competitor to Compare ({industry})</label>
              <select
                value={selectedBenchmarkExecution}
                onChange={(e) => setSelectedBenchmarkExecution(e.target.value)}
                disabled={loadingBenchmarkExecutions}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: '#0f172a',
                  border: '2px solid #334155',
                  borderRadius: '8px',
                  color: '#e2e8f0',
                  fontSize: '1rem'
                }}
              >
                <option value="">-- Select Competitor --</option>
                {benchmarkExecutions.map(exec => (
                  <option key={exec.execution_id} value={exec.execution_id.toString()}>
                    {exec.url} - {new Date(exec.execution_date).toLocaleDateString()}
                  </option>
                ))}
              </select>
              {loadingBenchmarkExecutions && (
                <div style={{ fontSize: '0.8rem', color: '#f59e0b', marginTop: '5px' }}>
                  🔄 Loading {industry} competitors...
                </div>
              )}
              {benchmarkExecutions.length === 0 && !loadingBenchmarkExecutions && (
                <div style={{ fontSize: '0.8rem', color: '#94a3b8', marginTop: '5px' }}>
                  No other {industry} executions available for comparison
                </div>
              )}
            </div>
            
            {selectedBenchmarkExecution && (
              <button
                className="seo-btn-primary"
                onClick={async () => {
                  // Show model selection modal
                  showModelSelectionModal(async (modelToUse) => {
                    console.log('🤖 Using selected model for bulk benchmark:', modelToUse);
                    
                  try {
                      setLoadingBenchmarkComparisons(true);
                    // Trigger comparisons for all available classification types in gap analysis order
                    const orderedClassifications = industryRequirements[industry]?.filter(req => 
                      Object.keys(classification).includes(req)
                    ) || Object.keys(classification);
                    
                    const comparisonPromises = [];
                    
                    // Trigger desktop comparisons
                    orderedClassifications.forEach(classificationType => {
                        comparisonPromises.push(triggerBenchmarkComparison(classificationType, 'desktop', modelToUse));
                    });
                    
                    // Trigger phone comparisons
                    orderedClassifications.forEach(classificationType => {
                        comparisonPromises.push(triggerBenchmarkComparison(classificationType, 'phone', modelToUse));
                    });
                    await Promise.all(comparisonPromises);
                  } finally {
                    setLoadingBenchmarkComparisons(false);
                  }
                    
                    // Update the persisted selected model after successful operation
                    setSelectedModel(modelToUse);
                  });
                }}
                disabled={loadingBenchmarkClassifications || loadingBenchmarkComparisons}
              >
                {loadingBenchmarkComparisons ? '🤖 Analyzing...' : '📊 Generate Benchmark Analysis'}
              </button>
            )}
          </div>
        </div>

        {/* Benchmark Content */}
        {selectedBenchmarkExecution ? (
          <>
            {/* Benchmark Summary */}
            {Object.keys(benchmarkComparisons).length > 0 && (
              <div style={{ 
                background: '#1e293b', 
                padding: '20px', 
                borderRadius: '12px', 
                marginBottom: '30px' 
              }}>
                <h3 style={{ marginTop: 0, color: '#f1f5f9', marginBottom: '15px' }}>
                  📊 Benchmark Summary
                </h3>
                
                {(() => {
                  const completedComparisons = Object.values(benchmarkComparisons).filter(comp => 
                    !comp.loading && comp.analysis?.overall_score
                  );
                  
                  if (completedComparisons.length === 0) return null;
                  
                  const avgClientScore = completedComparisons.reduce((sum, comp) => 
                    sum + comp.analysis.overall_score.client_score, 0) / completedComparisons.length;
                  const avgBenchmarkScore = completedComparisons.reduce((sum, comp) => 
                    sum + comp.analysis.overall_score.benchmark_score, 0) / completedComparisons.length;
                  const clientWins = completedComparisons.filter(comp => 
                    comp.analysis.overall_score.winner === 'client').length;
                  
                  return (
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                      gap: '15px' 
                    }}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ color: '#3b82f6', fontSize: '2rem', fontWeight: 'bold' }}>
                          {avgClientScore.toFixed(1)}/10
                        </div>
                        <div style={{ color: '#94a3b8' }}>Your Average Score</div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ color: '#10b981', fontSize: '2rem', fontWeight: 'bold' }}>
                          {avgBenchmarkScore.toFixed(1)}/10
                        </div>
                        <div style={{ color: '#94a3b8' }}>Competitor Average</div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ color: '#f59e0b', fontSize: '2rem', fontWeight: 'bold' }}>
                          {clientWins}/{completedComparisons.length}
                        </div>
                        <div style={{ color: '#94a3b8' }}>Pages You Win</div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ 
                          color: avgClientScore > avgBenchmarkScore ? '#10b981' : '#f59e0b', 
                          fontSize: '2rem' 
                        }}>
                          {avgClientScore > avgBenchmarkScore ? '🏆' : '📈'}
                        </div>
                        <div style={{ color: '#94a3b8' }}>
                          {avgClientScore > avgBenchmarkScore ? 'Leading' : 'Improving'}
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* CRO & UI/UX Recommendations Section */}
            <div style={{ marginBottom: '30px' }}>
              <h3>CRO & UI/UX Recommendations</h3>
              
              {loadingBenchmarkClassifications ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <div className="seo-loading-spinner"></div>
                  <p style={{ marginTop: '20px', color: '#10b981' }}>
                    Loading competitor data...
                  </p>
                </div>
              ) : (
                <>
                  {/* Desktop vs Mobile Tabs */}
                  <div style={{ marginTop: '20px', marginBottom: '20px' }}>
                    <div style={{ 
                      display: 'flex', 
                      gap: '10px',
                      borderBottom: '2px solid #334155',
                      marginBottom: '20px'
                    }}>
                      <button
                        onClick={() => {
                          console.log('🖱️ Desktop tab clicked!');
                          setActiveSection('benchmark-desktop');
                        }}
                        style={{
                          background: (activeSection === 'benchmark-desktop' || activeSection === 'benchmark') ? '#3b82f6' : 'transparent',
                          color: (activeSection === 'benchmark-desktop' || activeSection === 'benchmark') ? 'white' : '#94a3b8',
                          border: 'none',
                          padding: '10px 20px',
                          borderRadius: '8px 8px 0 0',
                          cursor: 'pointer',
                          fontSize: '0.9rem',
                          fontWeight: '600'
                        }}
                      >
                        💻 Desktop Comparison
                      </button>
                      <button
                        onClick={() => {
                          console.log('🖱️ Mobile tab clicked!');
                          setActiveSection('benchmark-mobile');
                        }}
                        style={{
                          background: activeSection === 'benchmark-mobile' ? '#3b82f6' : 'transparent',
                          color: activeSection === 'benchmark-mobile' ? 'white' : '#94a3b8',
                          border: 'none',
                          padding: '10px 20px',
                          borderRadius: '8px 8px 0 0',
                          cursor: 'pointer',
                          fontSize: '0.9rem',
                          fontWeight: '600'
                        }}
                      >
                        📱 Mobile Comparison
                      </button>
                    </div>

                    {/* Desktop Comparison Grid */}
                    {(activeSection === 'benchmark-desktop' || activeSection === 'benchmark') && (
                      <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: 'repeat(auto-fit, minmax(600px, 1fr))', 
                        gap: '20px'
                      }}>
                        {industryRequirements[industry]?.filter(req => 
                          Object.keys(classification).includes(req)
                        ).map(classificationType => {
                          const currentScreenshot = existingScreenshots.find(s => 
                            s.tag === classificationType && s.device === 'desktop'
                          );
                          
                          const benchmarkScreenshot = benchmarkClassifications.classifications?.find(c => 
                            c.classification_type === classificationType && 
                            c.screenshots?.some(s => s.device === 'desktop')
                          )?.screenshots?.find(s => s.device === 'desktop');

                          const comparisonData = benchmarkComparisons[`${classificationType}_desktop`];
                      
                      return (
                        <div key={classificationType} style={{
                          background: '#0f172a',
                          border: '2px solid #334155',
                          borderRadius: '12px',
                          padding: '20px'
                        }}>
                          <h4 style={{ marginTop: 0, color: '#f1f5f9', marginBottom: '15px' }}>
                            {classificationType}
                          </h4>
                          
                          {/* Screenshot Comparison */}
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '20px' }}>
                            {/* Current Site */}
                            <div>
                              <h5 style={{ color: '#3b82f6', marginBottom: '10px' }}>Your Site</h5>
                              {currentScreenshot ? (
                                <div>
                                  <div className="seo-benchmark-screenshot-container">
                                    <img 
                                      src={currentScreenshot.screenshot_url} 
                                      alt={`Current ${classificationType}`}
                                    />
                                  </div>
                                  <div style={{ fontSize: '0.8rem', color: '#94a3b8', marginTop: '5px' }}>
                                    {new Date(currentScreenshot.updated_at).toLocaleDateString()} • Scroll to view full page
                                  </div>
                                </div>
                              ) : (
                                <div style={{ 
                                  background: '#1e293b', 
                                  height: '400px',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  justifyContent: 'center',
                                  alignItems: 'center',
                                  borderRadius: '8px',
                                  border: '1px solid #334155',
                                  color: '#94a3b8'
                                }}>
                                  📸 No screenshot available
                                  <div style={{ fontSize: '0.8rem', marginTop: '5px' }}>
                                    Screenshot needed for comparison
                                  </div>
                                </div>
                              )}
                            </div>
                            
                            {/* Benchmark Site */}
                            <div>
                              <h5 style={{ color: '#10b981', marginBottom: '10px' }}>
                                Competitor ({benchmarkClassifications.industry_type})
                              </h5>
                              {benchmarkScreenshot ? (
                                <div>
                                  <div className="seo-benchmark-screenshot-container">
                                    <img 
                                      src={benchmarkScreenshot.screenshot_url} 
                                      alt={`Benchmark ${classificationType}`}
                                    />
                                  </div>
                                  <div style={{ fontSize: '0.8rem', color: '#94a3b8', marginTop: '5px' }}>
                                    {new Date(benchmarkScreenshot.updated_at).toLocaleDateString()} • Scroll to view full page
                                  </div>
                                </div>
                              ) : (
                                <div style={{ 
                                  background: '#1e293b', 
                                  height: '400px',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  justifyContent: 'center',
                                  alignItems: 'center',
                                  borderRadius: '8px',
                                  border: '1px solid #334155',
                                  color: '#94a3b8'
                                }}>
                                  📸 No screenshot available
                                  <div style={{ fontSize: '0.8rem', marginTop: '5px' }}>
                                    Competitor doesn't have this page type
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                          
                          {/* Comparison Analysis */}
                          {comparisonData?.loading && (
                            <div style={{ textAlign: 'center', padding: '20px' }}>
                              <div className="seo-loading-spinner" style={{ width: '30px', height: '30px' }}></div>
                              <p style={{ marginTop: '10px', color: '#10b981', fontSize: '0.9rem' }}>
                                🤖 Analyzing screenshots...
                              </p>
                            </div>
                          )}
                          
                          {/* Show analysis results */}
                          {comparisonData && !comparisonData.loading && comparisonData.analysis && (
                            <div style={{ 
                              background: '#1e293b', 
                              padding: '15px', 
                              borderRadius: '8px',
                              marginTop: '15px'
                            }}>
                              <h5 style={{ color: '#f1f5f9', marginBottom: '10px' }}>AI Analysis</h5>
                              
                              {/* Overall Score */}
                              {comparisonData.analysis.overall_score && (
                                <div style={{ 
                                  display: 'flex', 
                                  justifyContent: 'space-between', 
                                  marginBottom: '15px',
                                  padding: '10px',
                                  background: '#0f172a',
                                  borderRadius: '6px'
                                }}>
                                  <div style={{ textAlign: 'center' }}>
                                    <div style={{ color: '#3b82f6', fontWeight: 'bold', fontSize: '1.2rem' }}>
                                      {comparisonData.analysis.overall_score.client_score}/10
                                    </div>
                                    <div style={{ color: '#94a3b8', fontSize: '0.8rem' }}>Your Site</div>
                                  </div>
                                  <div style={{ 
                                    color: comparisonData.analysis.overall_score.winner === 'client' ? '#10b981' : '#f59e0b',
                                    fontSize: '1.5rem',
                                    alignSelf: 'center'
                                  }}>
                                    {comparisonData.analysis.overall_score.winner === 'client' ? '🏆' : '📈'}
                                  </div>
                                  <div style={{ textAlign: 'center' }}>
                                    <div style={{ color: '#10b981', fontWeight: 'bold', fontSize: '1.2rem' }}>
                                      {comparisonData.analysis.overall_score.benchmark_score}/10
                                    </div>
                                    <div style={{ color: '#94a3b8', fontSize: '0.8rem' }}>Competitor</div>
                                  </div>
                                </div>
                              )}
                              
                              {/* Detailed Analysis */}
                              {comparisonData.analysis.detailed_analysis && (
                                <div style={{ fontSize: '0.9rem', color: '#cbd5e1', marginBottom: '15px' }}>
                                  {comparisonData.analysis.detailed_analysis}
                                </div>
                              )}
                              
                              
                              {/* Key Differences */}
                              {comparisonData.analysis.key_differences && comparisonData.analysis.key_differences.length > 0 && (
                                <div style={{ marginBottom: '15px' }}>
                                  <h5 style={{ color: '#f1f5f9', marginBottom: '12px', fontSize: '1.1rem', fontWeight: '700' }}>📊 Detailed Comparison</h5>
                                  {comparisonData.analysis.key_differences.map((diff, idx) => (
                                    <div key={idx} style={{ 
                                      background: '#0f172a',
                                      padding: '12px',
                                      borderRadius: '6px',
                                      marginBottom: '8px'
                                    }}>
                                      <div style={{ 
                                        display: 'flex', 
                                        justifyContent: 'space-between', 
                                        alignItems: 'center',
                                        marginBottom: '8px'
                                      }}>
                                        <h6 style={{ color: '#f1f5f9', margin: 0, fontSize: '1.1rem', fontWeight: '700' }}>
                                          {diff.aspect}
                                        </h6>
                                        <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                                          <div style={{ textAlign: 'center' }}>
                                            <div style={{ 
                                              color: diff.client_rating >= diff.benchmark_rating ? '#10b981' : '#ef4444',
                                              fontWeight: 'bold',
                                              fontSize: '1rem'
                                            }}>
                                              {diff.client_rating}/10
                                            </div>
                                            <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>You</div>
                                          </div>
                                          <div style={{ color: '#64748b', fontSize: '0.8rem' }}>vs</div>
                                          <div style={{ textAlign: 'center' }}>
                                            <div style={{ 
                                              color: diff.benchmark_rating >= diff.client_rating ? '#10b981' : '#ef4444',
                                              fontWeight: 'bold',
                                              fontSize: '1rem'
                                            }}>
                                              {diff.benchmark_rating}/10
                                            </div>
                                            <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>Competitor</div>
                                          </div>
                                        </div>
                                      </div>
                                      <div style={{ color: '#cbd5e1', fontSize: '0.85rem', lineHeight: '1.4', marginTop: '4px' }}>
                                        {diff.explanation}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}

                              {/* Strengths */}
                              {comparisonData.strengths && (
                                <div style={{ marginBottom: '15px' }}>
                                  <h5 style={{ color: '#f1f5f9', marginBottom: '12px', fontSize: '1.1rem', fontWeight: '700' }}>💪 Competitive Strengths</h5>
                                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                                    {comparisonData.strengths.client_advantages && (
                                      <div>
                                        <div style={{ color: '#3b82f6', fontWeight: '700', fontSize: '0.9rem', marginBottom: '8px' }}>
                                          ✅ Your Advantages
                                        </div>
                                        {comparisonData.strengths.client_advantages.map((advantage, idx) => (
                                          <div key={idx} style={{ 
                                            background: 'rgba(59, 130, 246, 0.1)',
                                            padding: '6px 8px',
                                            borderRadius: '4px',
                                            marginBottom: '4px',
                                            fontSize: '0.75rem',
                                            color: '#e2e8f0',
                                            lineHeight: '1.3'
                                          }}>
                                            • {advantage}
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                    {comparisonData.strengths.benchmark_advantages && (
                                      <div>
                                        <div style={{ color: '#10b981', fontWeight: '700', fontSize: '0.9rem', marginBottom: '8px' }}>
                                          🎯 Learn From Competitor
                                        </div>
                                        {comparisonData.strengths.benchmark_advantages.map((advantage, idx) => (
                                          <div key={idx} style={{ 
                                            background: 'rgba(16, 185, 129, 0.1)',
                                            padding: '6px 8px',
                                            borderRadius: '4px',
                                            marginBottom: '4px',
                                            fontSize: '0.75rem',
                                            color: '#e2e8f0',
                                            lineHeight: '1.3'
                                          }}>
                                            • {advantage}
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}

                              {/* Critical Recommendations */}
                              {comparisonData.recommendations?.critical && comparisonData.recommendations.critical.length > 0 && (
                                <div style={{ marginBottom: '15px' }}>
                                  <h5 style={{ color: '#ef4444', marginBottom: '10px', fontSize: '1.1rem', fontWeight: '700' }}>🚨 Critical Recommendations</h5>
                                  {comparisonData.recommendations.critical.map((rec, idx) => (
                                    <div key={idx} style={{ 
                                      background: '#0f172a',
                                      padding: '10px',
                                      borderRadius: '6px',
                                      marginBottom: '8px',
                                      borderLeft: '3px solid #ef4444'
                                    }}>
                                      <div style={{ fontWeight: 'bold', color: '#f1f5f9', fontSize: '1rem' }}>
                                        #{rec.priority} - {rec.title}
                                      </div>
                                      <div style={{ color: '#cbd5e1', fontSize: '0.85rem', marginTop: '6px', lineHeight: '1.4' }}>
                                        {rec.description}
                                      </div>
                                      <div style={{ 
                                        display: 'flex', 
                                        gap: '12px', 
                                        marginTop: '8px',
                                        fontSize: '0.75rem'
                                      }}>
                                        <span style={{ color: '#ef4444', fontWeight: '600' }}>
                                          Impact: {rec.impact}
                                        </span>
                                        <span style={{ color: '#94a3b8', fontWeight: '600' }}>
                                          Effort: {rec.effort}
                                        </span>
                                        <span style={{ color: '#3b82f6', fontWeight: '600' }}>
                                          {rec.category}
                                        </span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}

                              {/* General Improvements */}
                              {comparisonData.recommendations?.improvements && comparisonData.recommendations.improvements.length > 0 && (
                                <div style={{ marginBottom: '15px' }}>
                                  <h5 style={{ color: '#f59e0b', marginBottom: '10px', fontSize: '1.1rem', fontWeight: '700' }}>💡 General Improvements</h5>
                                  {comparisonData.recommendations.improvements.map((rec, idx) => (
                                    <div key={idx} style={{ 
                                      background: '#0f172a',
                                      padding: '10px',
                                      borderRadius: '6px',
                                      marginBottom: '8px',
                                      borderLeft: '3px solid #f59e0b'
                                    }}>
                                      <div style={{ fontWeight: 'bold', color: '#f1f5f9', fontSize: '1rem' }}>
                                        {rec.title}
                                      </div>
                                      <div style={{ color: '#cbd5e1', fontSize: '0.85rem', marginTop: '6px', lineHeight: '1.4' }}>
                                        {rec.description}
                                      </div>
                                      <div style={{ 
                                        display: 'flex', 
                                        gap: '12px', 
                                        marginTop: '8px',
                                        fontSize: '0.75rem'
                                      }}>
                                        <span style={{ color: '#f59e0b', fontWeight: '600' }}>
                                          Impact: {rec.impact}
                                        </span>
                                        <span style={{ color: '#94a3b8', fontWeight: '600' }}>
                                          Effort: {rec.effort}
                                        </span>
                                        <span style={{ color: '#3b82f6', fontWeight: '600' }}>
                                          {rec.category}
                                        </span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}

                              {/* Summary */}
                              {comparisonData.summary && (
                                <div style={{ 
                                  background: '#0f172a',
                                  padding: '15px',
                                  borderRadius: '8px',
                                  marginTop: '15px',
                                  border: '1px solid #334155'
                                }}>
                                  <h5 style={{ color: '#f1f5f9', marginBottom: '12px', fontSize: '1.1rem', fontWeight: '700' }}>📋 Summary</h5>
                                  
                                  <div style={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-around', 
                                    marginBottom: '15px',
                                    padding: '10px',
                                    background: '#1e293b',
                                    borderRadius: '6px'
                                  }}>
                                    <div style={{ textAlign: 'center' }}>
                                      <div style={{ color: '#3b82f6', fontWeight: 'bold', fontSize: '1.5rem' }}>
                                        {comparisonData.summary.client_wins}
                                      </div>
                                      <div style={{ color: '#94a3b8', fontSize: '0.8rem' }}>You Win</div>
                                    </div>
                                    <div style={{ textAlign: 'center' }}>
                                      <div style={{ color: '#10b981', fontWeight: 'bold', fontSize: '1.5rem' }}>
                                        {comparisonData.summary.benchmark_wins}
                                      </div>
                                      <div style={{ color: '#94a3b8', fontSize: '0.8rem' }}>They Win</div>
                                    </div>
                                    <div style={{ textAlign: 'center' }}>
                                      <div style={{ color: '#f59e0b', fontWeight: 'bold', fontSize: '1.5rem' }}>
                                        {comparisonData.summary.ties}
                                      </div>
                                      <div style={{ color: '#94a3b8', fontSize: '0.8rem' }}>Ties</div>
                                    </div>
                                  </div>
                                  
                                  {comparisonData.summary.overall_recommendation && (
                                    <div>
                                      <div style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '1rem', marginBottom: '8px' }}>
                                        🎯 Overall Recommendation:
                                      </div>
                                      <div style={{ color: '#cbd5e1', fontSize: '0.9rem', lineHeight: '1.5', marginTop: '4px' }}>
                                        {comparisonData.summary.overall_recommendation}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                          
                          {/* Handle special scenarios */}
                          {comparisonData && !comparisonData.loading && (comparisonData.error || comparisonData.analysis === 'competitive_advantage') && (
                            <div style={{ 
                              background: comparisonData.error === 'industry_gap' ? '#1e3a8a' :
                                          comparisonData.analysis === 'competitive_advantage' ? '#14532d' : '#7f1d1d',
                              padding: '15px', 
                              borderRadius: '8px',
                              marginTop: '15px'
                            }}>
                              <div style={{ 
                                color: comparisonData.error === 'industry_gap' ? '#93c5fd' :
                                        comparisonData.analysis === 'competitive_advantage' ? '#86efac' : '#fca5a5',
                                fontWeight: '600',
                                marginBottom: '8px'
                              }}>
                                {comparisonData.error === 'industry_gap' && '🏢 Industry Gap Opportunity'}
                                {comparisonData.analysis === 'competitive_advantage' && '🎯 Competitive Advantage'}
                                {comparisonData.error === 'client_screenshot_missing' && '📸 Screenshot Required'}
                                {comparisonData.error && !comparisonData.error.includes('industry_gap') && !comparisonData.error.includes('missing') && !comparisonData.analysis && '❌ Comparison Error'}
                              </div>
                              <div style={{ 
                                color: comparisonData.error === 'industry_gap' ? '#bfdbfe' :
                                        comparisonData.analysis === 'competitive_advantage' ? '#bbf7d0' : '#fecaca',
                                fontSize: '0.9rem',
                                marginBottom: '8px'
                              }}>
                                {comparisonData.message}
                              </div>
                              <div style={{ 
                                color: comparisonData.error === 'industry_gap' ? '#dbeafe' :
                                        comparisonData.analysis === 'competitive_advantage' ? '#dcfce7' : '#fed7d7',
                                fontSize: '0.85rem',
                                fontStyle: 'italic',
                                marginBottom: '10px'
                              }}>
                                💡 {comparisonData.recommendation}
                              </div>
                              
                              {/* Retry button for actual errors */}
                              {comparisonData.error && 
                               !comparisonData.error.includes('industry_gap') && 
                               !comparisonData.error.includes('missing') && 
                               !comparisonData.analysis && (
                                <button
                                  className="seo-btn-secondary"
                                  onClick={() => triggerBenchmarkComparisonWithModal(classificationType, 'desktop')}
                                  style={{ 
                                    fontSize: '0.8rem', 
                                    padding: '6px 12px',
                                    background: '#475569',
                                    border: 'none'
                                  }}
                                >
                                  🔄 Try Again
                                </button>
                              )}
                            </div>
                          )}
                          
                          {/* Action Button */}
                          {currentScreenshot && benchmarkScreenshot && !comparisonData && (
                            <button
                              className="seo-btn-secondary"
                              onClick={() => triggerBenchmarkComparison(classificationType, 'desktop')}
                              style={{ width: '100%', marginTop: '15px' }}
                            >
                              🔍 Analyze This Comparison
                            </button>
                          )}
                          
                        </div>
                      );
                    })}
                      </div>
                    )}

                    {/* Mobile Comparison Grid */}
                    {activeSection === 'benchmark-mobile' && (
                      <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: 'repeat(auto-fit, minmax(600px, 1fr))', 
                        gap: '20px'
                      }}>
                        {(() => {
                          console.log('🔍 MOBILE TAB DEBUG:');
                          console.log('industry:', industry);
                          console.log('industryRequirements[industry]:', industryRequirements[industry]);
                          console.log('Object.keys(classification):', Object.keys(classification));
                          console.log('existingScreenshots length:', existingScreenshots.length);
                          console.log('benchmarkClassifications:', benchmarkClassifications);
                          
                          const availableClassifications = industryRequirements[industry]?.filter(req => 
                            Object.keys(classification).includes(req)
                          ) || [];
                          
                          console.log('availableClassifications for mobile:', availableClassifications);
                          
                          return availableClassifications;
                        })().map(classificationType => {
                          const currentScreenshot = existingScreenshots.find(s => 
                            s.tag === classificationType && s.device === 'phone'
                          );
                          
                          const benchmarkScreenshot = benchmarkClassifications.classifications?.find(c => 
                            c.classification_type === classificationType && 
                            c.screenshots?.some(s => s.device === 'phone')
                          )?.screenshots?.find(s => s.device === 'phone');

                          console.log(`📱 ${classificationType}:`, {
                            currentScreenshot: !!currentScreenshot,
                            benchmarkScreenshot: !!benchmarkScreenshot,
                            currentUrl: currentScreenshot?.url,
                            benchmarkUrl: benchmarkScreenshot?.url
                          });

                          const comparisonData = benchmarkComparisons[`${classificationType}_phone`];
                      
                      return (
                        <div key={classificationType} style={{
                          background: '#0f172a',
                          border: '2px solid #334155',
                          borderRadius: '12px',
                          padding: '20px'
                        }}>
                          <h4 style={{ marginTop: 0, color: '#f1f5f9', marginBottom: '15px' }}>
                            📱 {classificationType} (Mobile)
                          </h4>
                          
                          {/* Screenshot Comparison */}
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '20px' }}>
                            {/* Current Site */}
                            <div>
                              <h5 style={{ color: '#3b82f6', marginBottom: '10px' }}>Your Site</h5>
                              {currentScreenshot ? (
                                <div>
                                  <div className="seo-benchmark-screenshot-container">
                                    <img 
                                      src={currentScreenshot.screenshot_url} 
                                      alt={`Current ${classificationType} Mobile`}
                                    />
                                  </div>
                                  <div style={{ fontSize: '0.8rem', color: '#94a3b8', marginTop: '5px' }}>
                                    {new Date(currentScreenshot.updated_at).toLocaleDateString()} • Scroll to view full page
                                  </div>
                                </div>
                              ) : (
                                <div style={{ 
                                  background: '#1e293b', 
                                  height: '400px',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  justifyContent: 'center',
                                  alignItems: 'center',
                                  borderRadius: '8px',
                                  border: '1px solid #334155',
                                  color: '#94a3b8'
                                }}>
                                  📱 No mobile screenshot available
                                  <div style={{ fontSize: '0.8rem', marginTop: '5px' }}>
                                    Mobile screenshot needed for comparison
                                  </div>
                                </div>
                              )}
                            </div>
                            
                            {/* Benchmark Site */}
                            <div>
                              <h5 style={{ color: '#10b981', marginBottom: '10px' }}>
                                Competitor ({benchmarkClassifications.industry_type})
                              </h5>
                              {benchmarkScreenshot ? (
                                <div>
                                  <div className="seo-benchmark-screenshot-container">
                                    <img 
                                      src={benchmarkScreenshot.screenshot_url} 
                                      alt={`Benchmark ${classificationType} Mobile`}
                                    />
                                  </div>
                                  <div style={{ fontSize: '0.8rem', color: '#94a3b8', marginTop: '5px' }}>
                                    {new Date(benchmarkScreenshot.updated_at).toLocaleDateString()} • Scroll to view full page
                                  </div>
                                </div>
                              ) : (
                                <div style={{ 
                                  background: '#1e293b', 
                                  height: '400px',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  justifyContent: 'center',
                                  alignItems: 'center',
                                  borderRadius: '8px',
                                  border: '1px solid #334155',
                                  color: '#94a3b8'
                                }}>
                                  📱 No mobile screenshot available
                                  <div style={{ fontSize: '0.8rem', marginTop: '5px' }}>
                                    Competitor doesn't have mobile for this page type
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                          
                          {/* Comparison Analysis */}
                          {comparisonData?.loading && (
                            <div style={{ textAlign: 'center', padding: '20px' }}>
                              <div className="seo-loading-spinner" style={{ width: '30px', height: '30px' }}></div>
                              <p style={{ marginTop: '10px', color: '#10b981', fontSize: '0.9rem' }}>
                                🤖 Analyzing mobile screenshots...
                              </p>
                            </div>
                          )}
                          
                          {/* Show analysis results */}
                          {comparisonData && !comparisonData.loading && comparisonData.analysis && (
                            <div style={{ 
                              background: '#1e293b', 
                              padding: '15px', 
                              borderRadius: '8px',
                              marginTop: '15px'
                            }}>
                              <h5 style={{ color: '#f1f5f9', marginBottom: '10px' }}>📱 Mobile AI Analysis</h5>
                              
                              {/* Overall Score */}
                              {comparisonData.analysis.overall_score && (
                                <div style={{ 
                                  display: 'flex', 
                                  justifyContent: 'space-between', 
                                  marginBottom: '15px',
                                  padding: '10px',
                                  background: '#0f172a',
                                  borderRadius: '6px'
                                }}>
                                  <div style={{ textAlign: 'center' }}>
                                    <div style={{ color: '#3b82f6', fontWeight: 'bold', fontSize: '1.2rem' }}>
                                      {comparisonData.analysis.overall_score.client_score}/10
                                    </div>
                                    <div style={{ color: '#94a3b8', fontSize: '0.8rem' }}>Your Mobile Site</div>
                                  </div>
                                  <div style={{ 
                                    color: comparisonData.analysis.overall_score.winner === 'client' ? '#10b981' : '#f59e0b',
                                    fontSize: '1.5rem',
                                    alignSelf: 'center'
                                  }}>
                                    {comparisonData.analysis.overall_score.winner === 'client' ? '🏆' : '📈'}
                                  </div>
                                  <div style={{ textAlign: 'center' }}>
                                    <div style={{ color: '#10b981', fontWeight: 'bold', fontSize: '1.2rem' }}>
                                      {comparisonData.analysis.overall_score.benchmark_score}/10
                                    </div>
                                    <div style={{ color: '#94a3b8', fontSize: '0.8rem' }}>Competitor Mobile</div>
                                  </div>
                                </div>
                              )}
                              
                              {/* Detailed Analysis */}
                              {comparisonData.analysis.detailed_analysis && (
                                <div style={{ marginBottom: '15px' }}>
                                  <p style={{ color: '#e2e8f0', fontSize: '0.9rem', lineHeight: '1.5' }}>
                                    {comparisonData.analysis.detailed_analysis}
                                  </p>
                                </div>
                              )}

                              {/* Key Differences */}
                              {comparisonData.analysis.key_differences && comparisonData.analysis.key_differences.length > 0 && (
                                <div style={{ marginBottom: '15px' }}>
                                  <h5 style={{ color: '#f1f5f9', marginBottom: '12px', fontSize: '1.1rem', fontWeight: '700' }}>📊 Detailed Comparison</h5>
                                  {comparisonData.analysis.key_differences.map((diff, idx) => (
                                    <div key={idx} style={{ 
                                      background: '#0f172a',
                                      padding: '12px',
                                      borderRadius: '6px',
                                      marginBottom: '8px'
                                    }}>
                                      <div style={{ 
                                        display: 'flex', 
                                        justifyContent: 'space-between', 
                                        alignItems: 'center',
                                        marginBottom: '8px'
                                      }}>
                                        <h6 style={{ color: '#f1f5f9', margin: 0, fontSize: '1.1rem', fontWeight: '700' }}>
                                          {diff.aspect}
                                        </h6>
                                        <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                                          <div style={{ textAlign: 'center' }}>
                                            <div style={{ 
                                              color: diff.client_rating >= diff.benchmark_rating ? '#10b981' : '#ef4444',
                                              fontWeight: 'bold',
                                              fontSize: '1rem'
                                            }}>
                                              {diff.client_rating}/10
                                            </div>
                                            <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>You</div>
                                          </div>
                                          <div style={{ color: '#64748b', fontSize: '0.8rem' }}>vs</div>
                                          <div style={{ textAlign: 'center' }}>
                                            <div style={{ 
                                              color: diff.benchmark_rating >= diff.client_rating ? '#10b981' : '#ef4444',
                                              fontWeight: 'bold',
                                              fontSize: '1rem'
                                            }}>
                                              {diff.benchmark_rating}/10
                                            </div>
                                            <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>Competitor</div>
                                          </div>
                                        </div>
                                      </div>
                                      <div style={{ color: '#cbd5e1', fontSize: '0.85rem', lineHeight: '1.4', marginTop: '4px' }}>
                                        {diff.explanation}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}

                              {/* Strengths */}
                              {comparisonData.strengths && (
                                <div style={{ marginBottom: '15px' }}>
                                  <h5 style={{ color: '#f1f5f9', marginBottom: '12px', fontSize: '1.1rem', fontWeight: '700' }}>💪 Competitive Strengths</h5>
                                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                                    {comparisonData.strengths.client_advantages && (
                                      <div>
                                        <div style={{ color: '#3b82f6', fontWeight: '700', fontSize: '0.9rem', marginBottom: '8px' }}>
                                          ✅ Your Advantages
                                        </div>
                                        {comparisonData.strengths.client_advantages.map((advantage, idx) => (
                                          <div key={idx} style={{ 
                                            background: 'rgba(59, 130, 246, 0.1)',
                                            padding: '6px 8px',
                                            borderRadius: '4px',
                                            marginBottom: '4px',
                                            fontSize: '0.75rem',
                                            color: '#e2e8f0',
                                            lineHeight: '1.3'
                                          }}>
                                            • {advantage}
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                    {comparisonData.strengths.benchmark_advantages && (
                                      <div>
                                        <div style={{ color: '#10b981', fontWeight: '700', fontSize: '0.9rem', marginBottom: '8px' }}>
                                          🎯 Learn From Competitor
                                        </div>
                                        {comparisonData.strengths.benchmark_advantages.map((advantage, idx) => (
                                          <div key={idx} style={{ 
                                            background: 'rgba(16, 185, 129, 0.1)',
                                            padding: '6px 8px',
                                            borderRadius: '4px',
                                            marginBottom: '4px',
                                            fontSize: '0.75rem',
                                            color: '#e2e8f0',
                                            lineHeight: '1.3'
                                          }}>
                                            • {advantage}
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}

                              {/* Critical Recommendations */}
                              {comparisonData.recommendations?.critical && comparisonData.recommendations.critical.length > 0 && (
                                <div style={{ marginBottom: '15px' }}>
                                  <h5 style={{ color: '#ef4444', marginBottom: '10px', fontSize: '1.1rem', fontWeight: '700' }}>🚨 Critical Recommendations</h5>
                                  {comparisonData.recommendations.critical.map((rec, idx) => (
                                    <div key={idx} style={{ 
                                      background: '#0f172a',
                                      padding: '10px',
                                      borderRadius: '6px',
                                      marginBottom: '8px',
                                      borderLeft: '3px solid #ef4444'
                                    }}>
                                      <div style={{ fontWeight: 'bold', color: '#f1f5f9', fontSize: '1rem' }}>
                                        #{rec.priority} - {rec.title}
                                      </div>
                                      <div style={{ color: '#cbd5e1', fontSize: '0.85rem', marginTop: '6px', lineHeight: '1.4' }}>
                                        {rec.description}
                                      </div>
                                      <div style={{ 
                                        display: 'flex', 
                                        gap: '12px', 
                                        marginTop: '8px',
                                        fontSize: '0.75rem'
                                      }}>
                                        <span style={{ color: '#ef4444', fontWeight: '600' }}>
                                          Impact: {rec.impact}
                                        </span>
                                        <span style={{ color: '#94a3b8', fontWeight: '600' }}>
                                          Effort: {rec.effort}
                                        </span>
                                        <span style={{ color: '#3b82f6', fontWeight: '600' }}>
                                          {rec.category}
                                        </span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}

                              {/* General Improvements */}
                              {comparisonData.recommendations?.improvements && comparisonData.recommendations.improvements.length > 0 && (
                                <div style={{ marginBottom: '15px' }}>
                                  <h5 style={{ color: '#f59e0b', marginBottom: '10px', fontSize: '1.1rem', fontWeight: '700' }}>💡 General Improvements</h5>
                                  {comparisonData.recommendations.improvements.map((rec, idx) => (
                                    <div key={idx} style={{ 
                                      background: '#0f172a',
                                      padding: '10px',
                                      borderRadius: '6px',
                                      marginBottom: '8px',
                                      borderLeft: '3px solid #f59e0b'
                                    }}>
                                      <div style={{ fontWeight: 'bold', color: '#f1f5f9', fontSize: '1rem' }}>
                                        {rec.title}
                                      </div>
                                      <div style={{ color: '#cbd5e1', fontSize: '0.85rem', marginTop: '6px', lineHeight: '1.4' }}>
                                        {rec.description}
                                      </div>
                                      <div style={{ 
                                        display: 'flex', 
                                        gap: '12px', 
                                        marginTop: '8px',
                                        fontSize: '0.75rem'
                                      }}>
                                        <span style={{ color: '#f59e0b', fontWeight: '600' }}>
                                          Impact: {rec.impact}
                                        </span>
                                        <span style={{ color: '#94a3b8', fontWeight: '600' }}>
                                          Effort: {rec.effort}
                                        </span>
                                        <span style={{ color: '#3b82f6', fontWeight: '600' }}>
                                          {rec.category}
                                        </span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}

                              {/* Summary */}
                              {comparisonData.summary && (
                                <div style={{ 
                                  background: '#0f172a',
                                  padding: '15px',
                                  borderRadius: '8px',
                                  marginTop: '15px',
                                  border: '1px solid #334155'
                                }}>
                                  <h5 style={{ color: '#f1f5f9', marginBottom: '12px', fontSize: '1.1rem', fontWeight: '700' }}>📋 Summary</h5>
                                  
                                  <div style={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-around', 
                                    marginBottom: '15px',
                                    padding: '10px',
                                    background: '#1e293b',
                                    borderRadius: '6px'
                                  }}>
                                    <div style={{ textAlign: 'center' }}>
                                      <div style={{ color: '#3b82f6', fontWeight: 'bold', fontSize: '1.5rem' }}>
                                        {comparisonData.summary.client_wins}
                                      </div>
                                      <div style={{ color: '#94a3b8', fontSize: '0.8rem' }}>You Win</div>
                                    </div>
                                    <div style={{ textAlign: 'center' }}>
                                      <div style={{ color: '#10b981', fontWeight: 'bold', fontSize: '1.5rem' }}>
                                        {comparisonData.summary.benchmark_wins}
                                      </div>
                                      <div style={{ color: '#94a3b8', fontSize: '0.8rem' }}>They Win</div>
                                    </div>
                                    <div style={{ textAlign: 'center' }}>
                                      <div style={{ color: '#f59e0b', fontWeight: 'bold', fontSize: '1.5rem' }}>
                                        {comparisonData.summary.ties}
                                      </div>
                                      <div style={{ color: '#94a3b8', fontSize: '0.8rem' }}>Ties</div>
                                    </div>
                                  </div>
                                  
                                  {comparisonData.summary.overall_recommendation && (
                                    <div>
                                      <div style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '1rem', marginBottom: '8px' }}>
                                        🎯 Overall Recommendation:
                                      </div>
                                      <div style={{ color: '#cbd5e1', fontSize: '0.9rem', lineHeight: '1.5', marginTop: '4px' }}>
                                        {comparisonData.summary.overall_recommendation}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}

                          {/* Error handling */}
                          {comparisonData && comparisonData.error && (
                            <div style={{ 
                              background: comparisonData.error && 
                                          !comparisonData.error.includes('industry_gap') && 
                                          !comparisonData.error.includes('missing') ? '#ef444410' : '#1e293b', 
                              padding: '15px', 
                              borderRadius: '8px',
                              marginTop: '15px',
                              border: comparisonData.error && 
                                      !comparisonData.error.includes('industry_gap') && 
                                      !comparisonData.error.includes('missing') ? '1px solid #ef4444' : 'none'
                            }}>
                              <p style={{ color: '#94a3b8', fontSize: '0.9rem', margin: 0 }}>
                                {comparisonData.error}
                              </p>
                              {comparisonData.recommendation && (
                                <p style={{ color: '#3b82f6', fontSize: '0.8rem', margin: '8px 0 0 0' }}>
                                  💡 {comparisonData.recommendation}
                                </p>
                              )}
                              
                              {/* Show Try Again button for API errors only */}
                              {comparisonData.error && 
                               !comparisonData.error.includes('industry_gap') && 
                               !comparisonData.error.includes('missing') && 
                               !comparisonData.analysis && (
                                <button
                                  className="seo-btn-secondary"
                                  onClick={() => triggerBenchmarkComparisonWithModal(classificationType, 'phone')}
                                  style={{ 
                                    fontSize: '0.8rem', 
                                    padding: '6px 12px',
                                    background: '#475569',
                                    border: 'none',
                                    marginTop: '10px'
                                  }}
                                >
                                  🔄 Try Again
                                </button>
                              )}
                            </div>
                          )}
                          
                          {/* Action Button */}
                          {currentScreenshot && benchmarkScreenshot && !comparisonData && (
                            <button
                              className="seo-btn-secondary"
                              onClick={() => triggerBenchmarkComparison(classificationType, 'phone')}
                              style={{ width: '100%', marginTop: '15px' }}
                            >
                              🔍 Analyze Mobile Comparison
                            </button>
                          )}
                          
                        </div>
                      );
                    })}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </>
        ) : (
          <div className="seo-alert seo-alert-info">
            <p>Select a competitor execution to begin benchmark analysis.</p>
          </div>
        )}
      </>
    );
  };

  const renderPromptManagement = () => {
    if (loadingPrompts) {
      return (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div className="seo-loading-spinner"></div>
          <p style={{ marginTop: '20px', color: '#10b981' }}>
            Loading prompt templates and configurations...
          </p>
        </div>
      );
    }

    if (!selectedTemplate) {
      return (
        <>
          <div className="seo-alert seo-alert-info">
            <h4>Claude Prompt Template Management</h4>
            <p>Select a prompt template below to view and edit its content and linked configurations.</p>
          </div>

          {/* Template Selector */}
          <div className="template-selector">
            {promptTemplates.map(template => (
              <div 
                key={template.id}
                className="template-card"
                onClick={() => fetchTemplateWithConfigs(template.slug)}
              >
                <h4>{template.name}</h4>
                <div className="meta">
                  Slug: {template.slug} • Updated: {new Date(template.updated_at).toLocaleDateString()}
                </div>
                <div className="preview">
                  {template.content.substring(0, 100)}...
                </div>
                <div style={{ marginTop: '10px', fontSize: '0.8rem', color: '#94a3b8' }}>
                  Status: <span style={{ color: template.is_active ? '#10b981' : '#f59e0b' }}>
                    {template.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {promptTemplates.length === 0 && (
            <div className="seo-alert seo-alert-warning">
              <h4>No Templates Found</h4>
              <p>No prompt templates are available. Check if the API endpoint is working correctly.</p>
            </div>
          )}
        </>
      );
    }

    // Main editor view when template is selected
    const templateValidation = validatePromptTemplate(tempTemplateContent);
    const configValidation = selectedConfig ? validateConfiguration(tempConfigData) : { issues: [], parsed: null };

    return (
      <>
        {/* Header with back button */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: '20px',
          paddingBottom: '15px',
          borderBottom: '1px solid #334155'
        }}>
          <div>
            <h3 style={{ margin: 0, color: '#f1f5f9' }}>
              Editing: {selectedTemplate.name}
            </h3>
            <div style={{ fontSize: '0.9rem', color: '#94a3b8', marginTop: '5px' }}>
              Slug: {selectedTemplate.slug} • 
              Status: <span style={{ color: selectedTemplate.is_active ? '#10b981' : '#f59e0b' }}>
                {selectedTemplate.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>
          <button
            className="seo-btn-secondary"
            onClick={() => {
              setSelectedTemplate(null);
              setSelectedConfig(null);
              setEditingTemplate(false);
              setEditingConfig(false);
            }}
          >
            ← Back to Templates
          </button>
        </div>

        
        {/* Show linked configuration info */}
        {selectedTemplate.configurations && selectedTemplate.configurations.length > 0 && (
          <div style={{ 
            background: '#1e293b', 
            padding: '12px', 
            borderRadius: '8px', 
            marginBottom: '20px',
            border: '1px solid #334155'
          }}>
            <div style={{ color: '#10b981', fontWeight: '600', fontSize: '0.9rem' }}>
              📄 Linked Configuration: {selectedTemplate.configurations[0].name}
            </div>
            <div style={{ color: '#94a3b8', fontSize: '0.8rem', marginTop: '4px' }}>
              Slug: {selectedTemplate.configurations[0].slug} • 
              Status: {selectedTemplate.configurations[0].is_active ? 'Active' : 'Inactive'} •
              Type: {(() => {
                const configData = selectedTemplate.configurations[0].config_data;
                const firstIndustry = Object.keys(configData)[0];
                if (firstIndustry && configData[firstIndustry]) {
                  const hasClassifications = configData[firstIndustry].classifications && Array.isArray(configData[firstIndustry].classifications);
                  return hasClassifications ? 'Classification Config' : 'Benchmark Config';
                }
                return 'Unknown';
              })()}
            </div>
          </div>
        )}
        
        {(!selectedTemplate.configurations || selectedTemplate.configurations.length === 0) && (
          <div className="seo-alert seo-alert-warning" style={{ marginBottom: '20px' }}>
            <h5>No Linked Configurations</h5>
            <p>This template has no linked configurations. Variables will need to be provided at runtime.</p>
          </div>
        )}

        {/* Editor Container */}
        <div className="prompt-editor-container">
          {/* Template Editor */}
          <div className="prompt-editor-panel">
            <div className="prompt-editor-header">
              <h4 style={{ margin: 0, color: '#f1f5f9' }}>Prompt Template</h4>
              <div>
                {!editingTemplate ? (
                  <button
                    className="seo-btn-primary"
                    onClick={() => setEditingTemplate(true)}
                    style={{ fontSize: '0.8rem', padding: '6px 12px' }}
                  >
                    ✏️ Edit
                  </button>
                ) : (
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      className="seo-btn-success"
                      onClick={() => {
                        if (templateValidation.issues.filter(i => i.type === 'error').length === 0) {
                          updatePromptTemplate(selectedTemplate.slug, { content: tempTemplateContent });
                        } else {
                          alert('Please fix validation errors before saving');
                        }
                      }}
                      style={{ fontSize: '0.8rem', padding: '6px 12px' }}
                      disabled={templateValidation.issues.filter(i => i.type === 'error').length > 0}
                    >
                      💾 Save
                    </button>
                    <button
                      className="seo-btn-secondary"
                      onClick={() => {
                        setTempTemplateContent(selectedTemplate.content);
                        setEditingTemplate(false);
                      }}
                      style={{ fontSize: '0.8rem', padding: '6px 12px' }}
                    >
                      ❌ Cancel
                    </button>
                  </div>
                )}
              </div>
            </div>

            {editingTemplate ? (
              <textarea
                className="prompt-textarea"
                value={tempTemplateContent}
                onChange={(e) => setTempTemplateContent(e.target.value)}
                placeholder="Enter prompt template content..."
              />
            ) : (
              <div 
                style={{
                  background: '#0f172a',
                  border: '2px solid #334155',
                  borderRadius: '8px',
                  padding: '15px',
                  fontFamily: 'Monaco, Courier New, monospace',
                  fontSize: '0.9rem',
                  lineHeight: '1.5',
                  color: '#e2e8f0',
                  height: '400px',
                  overflowY: 'auto',
                  whiteSpace: 'pre-wrap'
                }}
                dangerouslySetInnerHTML={{
                  __html: highlightTemplateVariables(selectedTemplate.content)
                }}
              />
            )}

            {/* Template Validation */}
            <div className="validation-panel">
              <h5 style={{ margin: '0 0 10px 0', color: '#f1f5f9' }}>Template Validation</h5>
              
              {templateValidation.issues.length > 0 ? (
                <div>
                  {templateValidation.issues.map((issue, index) => (
                    <div key={index} className={`validation-issue ${issue.type}`}>
                      <strong>{issue.type === 'error' ? '❌' : '⚠️'} {issue.type.toUpperCase()}:</strong> {issue.message}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="validation-issue success">
                  ✅ Template syntax is valid
                </div>
              )}

              {/* Variables Found */}
              <div style={{ marginTop: '15px' }}>
                <h6 style={{ margin: '0 0 8px 0', color: '#cbd5e1' }}>
                  Variables ({templateValidation.variables.length})
                </h6>
                <div className="variable-list">
                  {templateValidation.variables.map(variable => (
                    <span key={variable} className="variable-tag">
                      {variable}
                    </span>
                  ))}
                </div>
                {templateValidation.variables.length === 0 && (
                  <div style={{ color: '#94a3b8', fontSize: '0.8rem', fontStyle: 'italic' }}>
                    No variables found in template
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Configuration Editor */}
          <div className="prompt-editor-panel">
            <div className="prompt-editor-header">
              <h4 style={{ margin: 0, color: '#f1f5f9' }}>
                Configuration {selectedConfig ? `(${selectedConfig.name})` : '(None Selected)'}
              </h4>
              <div>
                {selectedConfig && !editingConfig ? (
                  <button
                    className="seo-btn-primary"
                    onClick={() => {
                      console.log('🔍 Edit button clicked!');
                      console.log('📄 Config data:', selectedConfig.config_data);
                      setTempConfigData(JSON.stringify(selectedConfig.config_data, null, 2));
                      setEditingConfig(true);
                      console.log('✅ Edit mode enabled');
                    }}
                    style={{ fontSize: '0.8rem', padding: '6px 12px' }}
                  >
                    ✏️ Edit
                  </button>
                ) : selectedConfig && editingConfig ? (
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      className="seo-btn-success"
                      onClick={() => {
                        if (configValidation.issues.filter(i => i.type === 'error').length === 0) {
                          // Update the configuration
                          fetch(`${API_BASE_URL}/prompts/configs/${selectedConfig.slug}/`, {
                            method: 'PATCH',
                            headers: {
                              'Content-Type': 'application/json',
                              'Authorization': `Token ${getAuthToken()}`
                            },
                            body: JSON.stringify({ 
                              config_data: JSON.parse(tempConfigData) 
                            })
                          })
                          .then(response => response.json())
                          .then(updatedConfig => {
                            console.log('✅ Configuration updated successfully');
                            setSelectedConfig(updatedConfig);
                            setEditingConfig(false);
                            // Refresh the template to get updated linked configs
                            fetchTemplateWithConfigs(selectedTemplate.slug);
                          })
                          .catch(error => {
                            console.error('❌ Error updating configuration:', error);
                            alert('Failed to update configuration: ' + error.message);
                          });
                        } else {
                          alert('Please fix JSON errors before saving');
                        }
                      }}
                      style={{ fontSize: '0.8rem', padding: '6px 12px' }}
                      disabled={configValidation.issues.filter(i => i.type === 'error').length > 0}
                    >
                      💾 Save
                    </button>
                    <button
                      className="seo-btn-secondary"
                      onClick={() => {
                        setTempConfigData(JSON.stringify(selectedConfig.config_data, null, 2));
                        setEditingConfig(false);
                      }}
                      style={{ fontSize: '0.8rem', padding: '6px 12px' }}
                    >
                      ❌ Cancel
                    </button>
                  </div>
                ) : null}
              </div>
            </div>

            {selectedConfig ? (
              <>
                <div 
                  style={{
                    background: '#0f172a',
                    border: editingConfig ? '2px solid #10b981' : '2px solid #334155',
                    borderRadius: '8px',
                    padding: '0',
                    height: '400px',
                    overflow: 'hidden',
                    position: 'relative'
                  }}
                >
                  {editingConfig && (
                    <div style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      background: 'rgba(16, 185, 129, 0.9)',
                      color: 'white',
                      padding: '3px 8px',
                      borderRadius: '4px',
                      fontSize: '0.7rem',
                      fontWeight: 'bold',
                      zIndex: 1000
                    }}>
                      ✏️ EDIT MODE
                    </div>
                  )}
                  <AceEditor
                    mode="json"
                    theme="monokai"
                    value={editingConfig ? tempConfigData : JSON.stringify(selectedConfig.config_data, null, 2)}
                    onChange={editingConfig ? (newValue) => {
                      console.log('📝 JSON being edited:', newValue);
                      setTempConfigData(newValue);
                    } : undefined}
                    name={editingConfig ? "json-config-editor" : "json-config-viewer"}
                    editorProps={{ $blockScrolling: true }}
                    height="400px"
                    width="100%"
                    fontSize={14}
                    showPrintMargin={false}
                    showGutter={true}
                    highlightActiveLine={editingConfig}
                    readOnly={!editingConfig}
                    setOptions={{
                      enableBasicAutocompletion: editingConfig,
                      enableLiveAutocompletion: editingConfig,
                      enableSnippets: false,
                      showLineNumbers: true,
                      tabSize: 2,
                      useWorker: false,
                      highlightSelectedWord: editingConfig,
                      cursorStyle: editingConfig ? 'ace' : 'slim'
                    }}
                    style={{
                      borderRadius: '8px',
                      opacity: editingConfig ? 1 : 0.95
                    }}
                  />
                </div>

                {/* Configuration Validation */}
                <div className="validation-panel">
                  <h5 style={{ margin: '0 0 10px 0', color: '#f1f5f9' }}>Configuration Validation</h5>
                  
                  {configValidation.issues.length > 0 ? (
                    <div>
                      {configValidation.issues.map((issue, index) => (
                        <div key={index} className={`validation-issue ${issue.type}`}>
                          <strong>{issue.type === 'error' ? '❌' : '⚠️'} {issue.type.toUpperCase()}:</strong> {issue.message}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="validation-issue success">
                      ✅ Configuration JSON is valid
                    </div>
                  )}

                  {/* Config Structure Analysis */}
                  {configValidation.parsed && (
                    <div style={{ marginTop: '15px' }}>
                      {/* Handle Schema Audit Config */}
                      {configValidation.parsed.industries ? (
                        Object.keys(configValidation.parsed.industries).map(industry => {
                          const industryData = configValidation.parsed.industries[industry];
                          const pageTypes = Object.keys(industryData.schema_requirements || {});
                          
                          return (
                            <div key={industry} style={{ marginBottom: '15px' }}>
                              <h6 style={{ margin: '0 0 8px 0', color: '#cbd5e1' }}>
                                {industry} (Schema Audit Config)
                              </h6>
                              
                              <div>
                                <div style={{ fontSize: '0.8rem', color: '#94a3b8', marginBottom: '5px' }}>
                                  Page Types with Schema Requirements ({pageTypes.length}):
                                </div>
                                <div className="variable-list">
                                  {pageTypes.map(pageType => (
                                    <span key={pageType} className="variable-tag" style={{ background: 'rgba(168, 85, 247, 0.15)', color: '#a855f7' }}>
                                      {pageType}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        // Handle Classification/Benchmark Configs
                        Object.keys(configValidation.parsed).map(industry => {
                        const industryData = configValidation.parsed[industry];
                        const isClassificationConfig = industryData.classifications && Array.isArray(industryData.classifications);
                        const pageTypes = Object.keys(industryData).filter(key => 
                          typeof industryData[key] === 'object' && 
                          industryData[key] !== null &&
                          !Array.isArray(industryData[key]) &&
                          key !== 'guidelines'
                        );
                        
                        return (
                          <div key={industry} style={{ marginBottom: '15px' }}>
                            <h6 style={{ margin: '0 0 8px 0', color: '#cbd5e1' }}>
                              {industry} ({isClassificationConfig ? 'Classification Config' : 'Benchmark Config'})
                            </h6>
                            
                            {isClassificationConfig ? (
                              <div>
                                <div style={{ fontSize: '0.8rem', color: '#94a3b8', marginBottom: '5px' }}>
                                  Classifications ({industryData.classifications.length}):
                                </div>
                                <div className="variable-list">
                                  {industryData.classifications.map(classification => (
                                    <span key={classification} className="variable-tag" style={{ background: 'rgba(16, 185, 129, 0.15)', color: '#10b981' }}>
                                      {classification}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            ) : (
                              <div>
                                <div style={{ fontSize: '0.8rem', color: '#94a3b8', marginBottom: '5px' }}>
                                  Page Types ({pageTypes.length}):
                                </div>
                                <div className="variable-list">
                                  {pageTypes.map(pageType => (
                                    <span key={pageType} className="variable-tag" style={{ background: 'rgba(59, 130, 246, 0.15)', color: '#3b82f6' }}>
                                      {pageType}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                        })
                      )}
                    </div>
                  )}

                  {/* Validation Reference Guide */}
                  <div style={{ marginTop: '20px', paddingTop: '15px', borderTop: '1px solid #334155' }}>
                    <h6 style={{ margin: '0 0 10px 0', color: '#cbd5e1' }}>Validation Reference</h6>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                      <div>
                        <div style={{ fontSize: '0.8rem', color: '#3b82f6', fontWeight: '600', marginBottom: '5px' }}>
                          ✅ Valid Industries:
                        </div>
                        <div className="variable-list">
                          {validIndustries.map(industry => (
                            <span key={industry} className="variable-tag" style={{ background: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6', fontSize: '0.75rem' }}>
                              {industry}
                            </span>
                          ))}
                        </div>
                      </div>
                      
                      <div>
                        <div style={{ fontSize: '0.8rem', color: '#10b981', fontWeight: '600', marginBottom: '5px' }}>
                          ✅ Available Classifications:
                        </div>
                        <div style={{ 
                          maxHeight: '100px', 
                          overflowY: 'auto',
                          fontSize: '0.75rem',
                          color: '#94a3b8',
                          lineHeight: '1.2'
                        }}>
                          {[...validClassificationTypes].sort().join(', ')}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div style={{ 
                background: '#0f172a',
                border: '2px dashed #334155',
                borderRadius: '8px',
                padding: '40px',
                textAlign: 'center',
                color: '#94a3b8',
                height: '400px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }}>
                <div style={{ fontSize: '2rem', marginBottom: '10px' }}>⚙️</div>
                <div>Select a configuration from the dropdown above to view and edit</div>
              </div>
            )}
          </div>
        </div>

        {/* Variable Mapping Analysis */}
        {selectedTemplate && selectedConfig && (
          <div style={{
            background: '#1e293b',
            border: '1px solid #334155',
            borderRadius: '12px',
            padding: '20px',
            marginTop: '20px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
              <h4 style={{ margin: 0, color: '#f1f5f9' }}>Variable Mapping Analysis</h4>
              <button
                className="seo-btn-secondary"
                onClick={() => {
                  const validation = validatePromptTemplate(tempTemplateContent);
                  const configValidation = selectedConfig ? validateConfiguration(tempConfigData) : { issues: [] };
                  
                  if (validation.issues.filter(i => i.type === 'error').length === 0 && 
                      configValidation.issues.filter(i => i.type === 'error').length === 0) {
                    alert(`✅ Prompt Validation Successful!\n\nTemplate Variables: ${validation.variables.length}\nTemplate Issues: ${validation.issues.length}\nConfig Issues: ${configValidation.issues.length}\n\nAll variables are properly defined and template is ready for use.`);
                  } else {
                    const errorCount = validation.issues.filter(i => i.type === 'error').length + 
                                    configValidation.issues.filter(i => i.type === 'error').length;
                    alert(`❌ Validation Failed!\n\nFound ${errorCount} errors that need to be fixed before the prompt can be used.`);
                  }
                }}
                style={{ fontSize: '0.8rem', padding: '6px 12px' }}
              >
                🧪 Test Prompt
              </button>
            </div>
            
            <div style={{ 
              background: '#0f172a',
              padding: '12px',
              borderRadius: '6px',
              marginBottom: '15px',
              fontSize: '0.85rem',
              color: '#94a3b8'
            }}>
              <strong>Variable Sources:</strong> 
              <span style={{ color: '#3b82f6' }}> API Runtime</span> (provided by classify/benchmark endpoints) •
              <span style={{ color: '#10b981' }}> Config</span> (from linked configurations) •
              <span style={{ color: '#f59e0b' }}> Unknown</span> (need definition)
            </div>
            
            {(() => {
              const templateVars = validatePromptTemplate(tempTemplateContent).variables;
              
              // Define runtime variables for different endpoint types
              const runtimeVariables = new Set([
                // Classification endpoint runtime variables
                'pages', 'pages_data', 'industry', 'industry_type', 'apiKey', 'execution_id',
                
                // Benchmark endpoint runtime variables  
                'client_execution_id', 'benchmark_execution_id', 'classification_type',
                'client_screenshot_url', 'benchmark_screenshot_url',
                'client_url', 'benchmark_url', 'device',
                
                // Schema Audit endpoint runtime variables
                'PAGE_TYPE', 'INDUSTRY', 'URL', 'BUSINESS_NAME', 'CURRENT_SCHEMA', 'EXPECTED_SCHEMAS',
                
                // Config-derived variables (generated from config data in backend)
                'industry_classifications', 'industry_guidelines', 'focus_areas_text',
                'key_elements_text', 'cro_priorities_text', 'specific_analysis_prompt', 
                'focus_areas', 'key_elements', 'cro_priorities', 'specific_prompt'
              ]);
              
              
              // Categorize variables for better display
              const configVars = templateVars.filter(v => 
                ['industry_classifications', 'industry_guidelines', 'focus_areas_text',
                 'key_elements_text', 'cro_priorities_text', 'specific_analysis_prompt', 
                 'focus_areas', 'key_elements', 'cro_priorities', 'specific_prompt'].includes(v));
              const endpointVars = templateVars.filter(v => 
                ['pages', 'pages_data', 'industry', 'industry_type', 'classification_type', 'client_url', 
                 'benchmark_url', 'device', 'apiKey', 'execution_id', 'client_execution_id', 
                 'benchmark_execution_id', 'client_screenshot_url', 'benchmark_screenshot_url',
                 'PAGE_TYPE', 'INDUSTRY', 'URL', 'BUSINESS_NAME', 'CURRENT_SCHEMA', 'EXPECTED_SCHEMAS'].includes(v));
              const unknownVars = templateVars.filter(v => 
                !runtimeVariables.has(v) && !configVars.includes(v));
              
              return (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px' }}>
                  <div>
                    <h5 style={{ color: '#3b82f6', marginBottom: '10px' }}>🔗 API Runtime Variables</h5>
                    <div className="variable-list">
                      {endpointVars.map(variable => (
                        <span key={variable} className="variable-tag" style={{ 
                          background: 'rgba(59, 130, 246, 0.15)', 
                          color: '#3b82f6' 
                        }}>
                          {variable}
                        </span>
                      ))}
                    </div>
                    {endpointVars.length === 0 && (
                      <div style={{ color: '#94a3b8', fontSize: '0.8rem', fontStyle: 'italic' }}>
                        No endpoint variables
                      </div>
                    )}
                    <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '8px' }}>
                      Provided by API calls
                    </div>
                  </div>
                  
                  <div>
                    <h5 style={{ color: '#10b981', marginBottom: '10px' }}>⚙️ Config Variables</h5>
                    <div className="variable-list">
                      {configVars.map(variable => (
                        <span key={variable} className="variable-tag" style={{ 
                          background: selectedConfig ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)', 
                          color: selectedConfig ? '#10b981' : '#ef4444' 
                        }}>
                          {variable}
                        </span>
                      ))}
                    </div>
                    {configVars.length === 0 && (
                      <div style={{ color: '#94a3b8', fontSize: '0.8rem', fontStyle: 'italic' }}>
                        No config variables
                      </div>
                    )}
                    <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '8px' }}>
                      {selectedConfig ? 'Available from linked config' : 'Need linked configuration'}
                    </div>
                  </div>
                  
                  <div>
                    <h5 style={{ color: '#f59e0b', marginBottom: '10px' }}>⚠️ Unknown Variables</h5>
                    <div className="variable-list">
                      {unknownVars.map(variable => (
                        <span key={variable} className="variable-tag missing">
                          {variable}
                        </span>
                      ))}
                    </div>
                    {unknownVars.length === 0 && (
                      <div style={{ color: '#10b981', fontSize: '0.8rem', fontStyle: 'italic' }}>
                        All variables recognized
                      </div>
                    )}
                    <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '8px' }}>
                      Need definition or removal
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* Configuration-to-Template Variable Mapping Guide */}
        {selectedConfig && (
          <div style={{
            background: '#1e293b',
            border: '1px solid #334155',
            borderRadius: '12px',
            padding: '20px',
            marginTop: '20px'
          }}>
            <h4 style={{ margin: '0 0 15px 0', color: '#f1f5f9' }}>🔗 Config-to-Template Variable Mapping</h4>
            
            <div style={{ 
              background: '#0f172a',
              padding: '12px',
              borderRadius: '6px',
              marginBottom: '15px',
              fontSize: '0.85rem',
              color: '#94a3b8'
            }}>
              <strong>How Configuration Data Maps to Template Variables:</strong>
            </div>

            {(() => {
              const configValidation = validateConfiguration(tempConfigData);
              const isSchemaAuditConfig = configValidation.parsed && configValidation.parsed.industries && typeof configValidation.parsed.industries === 'object';
              const isClassificationConfig = configValidation.parsed && Object.values(configValidation.parsed).some(industryData => 
                industryData.classifications && Array.isArray(industryData.classifications)
              );
              const isBenchmarkConfig = configValidation.parsed && Object.values(configValidation.parsed).some(industryData => 
                Object.keys(industryData).some(key => 
                  typeof industryData[key] === 'object' && 
                  industryData[key]?.focus_areas && 
                  industryData[key]?.key_elements
                )
              );

              return (
                <div style={{ display: 'grid', gridTemplateColumns: isSchemaAuditConfig ? '1fr' : '1fr 1fr', gap: '20px' }}>
                  {/* Schema Audit Config Mapping */}
                  {isSchemaAuditConfig && (
                    <div style={{ 
                      background: 'rgba(168, 85, 247, 0.1)', 
                      border: '1px solid rgba(168, 85, 247, 0.3)',
                      borderRadius: '8px', 
                      padding: '15px' 
                    }}>
                      <h5 style={{ color: '#a855f7', margin: '0 0 10px 0' }}>🔍 Schema Audit Config</h5>
                      <div style={{ fontSize: '0.8rem', color: '#e2e8f0', lineHeight: '1.6' }}>
                        <div><strong style={{ color: '#a855f7' }}>{'{EXPECTED_SCHEMAS}'}</strong> ← <code>config.industries.Industry.schema_requirements.PageType.schemas[]</code></div>
                        <div style={{ marginTop: '4px', fontSize: '0.75rem', color: '#94a3b8' }}>
                          Generated from config based on PAGE_TYPE and INDUSTRY
                        </div>
                        <div style={{ marginTop: '8px' }}>
                          <strong style={{ color: '#a855f7' }}>Runtime Variables:</strong>
                        </div>
                        <div><strong style={{ color: '#a855f7' }}>{'{PAGE_TYPE}'}</strong> - Classification result</div>
                        <div><strong style={{ color: '#a855f7' }}>{'{INDUSTRY}'}</strong> - Selected industry</div>
                        <div><strong style={{ color: '#a855f7' }}>{'{URL}'}</strong> - Page address</div>
                        <div><strong style={{ color: '#a855f7' }}>{'{BUSINESS_NAME}'}</strong> - Extracted from schema</div>
                        <div><strong style={{ color: '#a855f7' }}>{'{CURRENT_SCHEMA}'}</strong> - Existing JSON-LD markup</div>
                        <div><strong style={{ color: '#a855f7' }}>{'{{DOMAIN}}'}</strong> - Example placeholder (not a variable)</div>
                        <div style={{ marginTop: '8px', fontSize: '0.75rem', color: '#94a3b8' }}>
                          Used by schema audit endpoint to analyze and optimize structured data
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Classification Config Mapping */}
                  {isClassificationConfig && (
                    <div style={{ 
                      background: 'rgba(16, 185, 129, 0.1)', 
                      border: '1px solid rgba(16, 185, 129, 0.3)',
                      borderRadius: '8px', 
                      padding: '15px' 
                    }}>
                      <h5 style={{ color: '#10b981', margin: '0 0 10px 0' }}>📋 Classification Config</h5>
                      <div style={{ fontSize: '0.8rem', color: '#e2e8f0', lineHeight: '1.6' }}>
                        <div><strong style={{ color: '#10b981' }}>{'{industry_classifications}'}</strong> ← <code>config.Industry.classifications[]</code></div>
                        <div><strong style={{ color: '#10b981' }}>{'{industry_guidelines}'}</strong> ← <code>config.Industry.guidelines{'{}'}</code></div>
                        <div style={{ marginTop: '8px', fontSize: '0.75rem', color: '#94a3b8' }}>
                          Used by classify endpoint to understand page types and their definitions
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Benchmark Config Mapping */}
                  {isBenchmarkConfig && (
                    <div style={{ 
                      background: 'rgba(59, 130, 246, 0.1)', 
                      border: '1px solid rgba(59, 130, 246, 0.3)',
                      borderRadius: '8px', 
                      padding: '15px' 
                    }}>
                      <h5 style={{ color: '#3b82f6', margin: '0 0 10px 0' }}>📊 Benchmark Config</h5>
                      <div style={{ fontSize: '0.8rem', color: '#e2e8f0', lineHeight: '1.6' }}>
                        <div><strong style={{ color: '#3b82f6' }}>{'{focus_areas_text}'}</strong> ← <code>config.Industry.PageType.focus_areas[]</code></div>
                        <div><strong style={{ color: '#3b82f6' }}>{'{key_elements_text}'}</strong> ← <code>config.Industry.PageType.key_elements[]</code></div>
                        <div><strong style={{ color: '#3b82f6' }}>{'{cro_priorities_text}'}</strong> ← <code>config.Industry.PageType.cro_priorities[]</code></div>
                        <div><strong style={{ color: '#3b82f6' }}>{'{specific_analysis_prompt}'}</strong> ← <code>config.Industry.PageType.specific_prompt</code></div>
                        <div style={{ marginTop: '8px', fontSize: '0.75rem', color: '#94a3b8' }}>
                          Used by benchmark endpoint to generate targeted page comparisons
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            <div style={{ 
              background: 'rgba(139, 92, 246, 0.1)', 
              border: '1px solid rgba(139, 92, 246, 0.3)',
              borderRadius: '8px', 
              padding: '15px',
              marginTop: '15px'
            }}>
              <h5 style={{ color: '#8b5cf6', margin: '0 0 10px 0' }}>⚡ Runtime Processing</h5>
              <div style={{ fontSize: '0.8rem', color: '#e2e8f0', lineHeight: '1.6' }}>
                <div style={{ marginBottom: '8px' }}>
                  <strong>Backend automatically converts:</strong>
                </div>
                <div>• Arrays → Comma-separated text (focus_areas[] → focus_areas_text)</div>
                <div>• Objects → Formatted guidelines (guidelines{'{}'} → industry_guidelines)</div>
                <div>• Strings → Direct mapping (specific_prompt → specific_analysis_prompt)</div>
                <div style={{ marginTop: '8px', fontSize: '0.75rem', color: '#94a3b8' }}>
                  No manual conversion needed - just ensure config structure matches requirements
                </div>
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingTimer.current) {
        clearTimeout(pollingTimer.current);
      }
    };
  }, []);

  return (
    <div className="seo-audit-container">
      <div className="seo-audit-header">
        <h1>SEO/CRO Audit System</h1>
      </div>

      <div className="seo-audit-content">
        {/* Control Bar */}
        <form autoComplete="off" onSubmit={(e) => e.preventDefault()}>
        <div className="seo-control-bar">
          <div className="seo-input-group" style={{ position: 'relative' }}>
            <label htmlFor="site-url">Website URL</label>
            <input
              type="text"
              id="site-url"
              name="seo-audit-website-url-field-12345"
              placeholder="https://example.com"
              value={siteUrl}
              onChange={(e) => setSiteUrl(e.target.value)}
              onFocus={(e) => {
                fetchUrlRecommendations();
                setShowUrlRecommendations(true);
              }}
              onBlur={(e) => {
                // Delay hiding to allow click on recommendations
                setTimeout(() => setShowUrlRecommendations(false), 200);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  setShowUrlRecommendations(false);
                }
              }}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
              data-form-type="other"
              data-lpignore="true"
              data-1p-ignore="true"
            />
            
            {/* URL Recommendations Dropdown */}
            {showUrlRecommendations && (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  backgroundColor: '#1e293b',
                  border: '1px solid #334155',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                  zIndex: 1000,
                  maxHeight: '200px',
                  overflowY: 'auto'
                }}
              >
                {loadingUrlRecommendations ? (
                  <div style={{ padding: '12px', color: '#94a3b8', textAlign: 'center' }}>
                    Loading recommendations...
                  </div>
                ) : urlRecommendations.length > 0 ? (
                  <>
                    <div style={{ 
                      padding: '8px 12px', 
                      fontSize: '0.8rem', 
                      color: '#64748b', 
                      borderBottom: '1px solid #334155',
                      backgroundColor: '#0f172a'
                    }}>
                      Previous URLs ({urlRecommendations.length})
                    </div>
                    {urlRecommendations.map((url, index) => (
                      <div
                        key={index}
                        style={{
                          padding: '10px 12px',
                          cursor: 'pointer',
                          borderBottom: index < urlRecommendations.length - 1 ? '1px solid #334155' : 'none',
                          color: '#e2e8f0',
                          fontSize: '0.9rem'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = '#334155';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = 'transparent';
                        }}
                        onClick={() => {
                          setSiteUrl(url);
                          setShowUrlRecommendations(false);
                        }}
                      >
                        {url}
                      </div>
                    ))}
                  </>
                ) : (
                  <div style={{ padding: '12px', color: '#94a3b8', textAlign: 'center' }}>
                    No previous URLs found
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* Previous Executions Selection */}
          {previousExecutions.length > 0 && (
            <div className="seo-input-group">
              <label htmlFor="execution-select">
                Previous Scans ({previousExecutions.length} found)
                {loadingExecutions && <span style={{color: '#f59e0b'}}> - Loading...</span>}
              </label>
              <select
                id="execution-select"
                value={selectedExecution}
                onChange={(e) => setSelectedExecution(e.target.value)}
                disabled={loadingExecutions}
              >
                <option value="new">⚡ Trigger New Scan</option>
                {previousExecutions.map(execution => (
                  <option key={execution.id} value={execution.id.toString()}>
                    📅 {new Date(execution.execution_date).toLocaleDateString()} - 
                    {new Date(execution.execution_date).toLocaleTimeString()} 
                    {execution.industry_type && ` (${execution.industry_type})`}
                    (ID: {execution.id})
                  </option>
                ))}
              </select>
            </div>
          )}
          
          <div className="seo-input-group">
            <label htmlFor="industry-select">Industry Type</label>
            <select
              id="industry-select"
              value={industry}
              onChange={(e) => setIndustry(e.target.value)}
              disabled={industryLocked}
            >
              <option value="">-- Select Industry --</option>
              <option value="Law Firms">Law Firms</option>
              <option value="Plumbing">Plumbing</option>
              <option value="Roofing">Roofing</option>
              <option value="HVAC">HVAC</option>
              <option value="Electricians">Electricians</option>
              <option value="Pest Control">Pest Control</option>
              <option value="Landscaping">Landscaping</option>
              <option value="Dentistry">Dentistry</option>
              <option value="Beauty & Cosmetic School">Beauty & Cosmetic School</option>
              <option value="Doors & Windows">Doors & Windows</option>
            </select>
          </div>
          
          <button 
            className="seo-btn-primary" 
            onClick={triggerScan} 
            disabled={progress.show || loadingExecutions}
          >
            {selectedExecution && selectedExecution !== 'new' ? 'Load Previous Scan' : 'Trigger New Scan'}
          </button>
          
          {/* Reset Button - Always visible */}
          <button 
            className="seo-btn-warning"
            onClick={() => {
              if (window.confirm('Reset all data? This will clear all scan results, classifications, and cached data.')) {
                // Clear all state
                clearStaleData();
                // Clear localStorage completely
                localStorage.removeItem('seoAuditData');
                localStorage.removeItem('selectedClaudeModel');
                // Reset URL and industry fields
                setSiteUrl('');
                setIndustry('');
                setSelectedModel('claude-sonnet-4-20250514');
                // Force reload to ensure clean state
                window.location.reload();
              }
            }}
            style={{ 
              marginLeft: '10px',
              fontSize: '0.85rem',
              padding: '12px 20px'
            }}
          >
            🔄 Reset All
          </button>
        </div>
        </form>


        {/* Status Bar */}
        <div className="seo-status-bar">
          <div className="seo-status-content">
            <div className="seo-status-indicator">
              <span className={`seo-status-dot ${status.type}`}></span>
              <span>{status.message}</span>
            </div>
            <div>
              <span>{lastUpdate}</span>
              {currentExecution?.downloadUrl && currentExecution?.completedAt && totalPages > 0 && (
                <button
                  className="seo-btn-secondary"
                  style={{ marginLeft: '10px' }}
                  onClick={() => window.open(currentExecution.downloadUrl, '_blank')}
                >
                  Download Results
                </button>
              )}
              {totalPages > 0 && (
                <button
                  className="seo-btn-secondary"
                  style={{ marginLeft: '10px', fontSize: '0.85rem' }}
                  onClick={() => {
                    if (window.confirm('Clear all scan data? This will remove current results.')) {
                      clearStaleData();
                      localStorage.removeItem('seoAuditData');
                    }
                  }}
                >
                  Clear Data
                </button>
              )}
            </div>
          </div>
          
          <div className={`seo-progress-bar ${progress.show ? 'active' : ''}`}>
            <div 
              className="seo-progress-fill" 
              style={{ width: `${progress.percent}%` }}
            >
              <span>{progress.message}</span>
            </div>
          </div>
          
          {currentExecution && currentExecution.completedAt && totalPages > 0 && (
            <div className="seo-execution-info active">
              <div>
                <span className="label">Execution ID:</span>
                <span className="value">{currentExecution.executionId || '-'}</span>
              </div>
              <div style={{ marginTop: '10px' }}>
                <span className="label">ARN:</span>
                <span className="value">{currentExecution.executionArn || '-'}</span>
              </div>
              {currentExecution.s3Path && (
                <div style={{ marginTop: '10px' }}>
                  <span className="label">S3 Path:</span>
                  <span className="value">{currentExecution.s3Path}</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Metrics */}
        <div className="seo-metrics-grid">
          <div className="seo-metric-card">
            <div className="seo-metric-value">
              {Object.keys(fileStatus).length > 0 ? totalPages : (totalPages || '-')}
            </div>
            <div className="seo-metric-label">Total Pages</div>
          </div>
          <div className="seo-metric-card">
            <div className="seo-metric-value">
              {totalPages === 0 && Object.keys(fileStatus).length > 0 ? 'No Data' : 
               pageTypes || '-'}
            </div>
            <div className="seo-metric-label">Page Types</div>
          </div>
          <div className="seo-metric-card">
            <div className="seo-metric-value">
              {totalPages === 0 && Object.keys(fileStatus).length > 0 ? 'N/A' : 
               missingPages || '-'}
            </div>
            <div className="seo-metric-label">Missed Opportunities</div>
          </div>
          <div className="seo-metric-card">
            <div className="seo-metric-value">
              {totalPages === 0 && Object.keys(fileStatus).length > 0 ? 'No Data' : 
               seoScore ? `${seoScore}%` : '-'}
            </div>
            <div className="seo-metric-label">SEO Score</div>
          </div>
          <div className="seo-metric-card">
            <div className="seo-metric-value">
              {totalPages === 0 && Object.keys(fileStatus).length > 0 ? 'No Data' : 
               schemaTypes || '-'}
            </div>
            <div className="seo-metric-label">Schema Types</div>
          </div>
          <div className="seo-metric-card">
            <div className="seo-metric-value">
              {totalPages === 0 && Object.keys(fileStatus).length > 0 ? 'No Data' : cms}
            </div>
            <div className="seo-metric-label">CMS Detected</div>
          </div>
        </div>

        {/* Main Content */}
        <div className="seo-main-content">
          {/* Sidebar Navigation */}
          <div className="seo-sidebar">
            <h3 style={{ marginTop: 0, marginBottom: '20px', color: '#f1f5f9' }}>Analysis Results</h3>
            <button
              className={`seo-nav-item ${activeSection === 'overview' ? 'active' : ''}`}
              onClick={() => setActiveSection('overview')}
            >
              Overview
            </button>
            <button
              className={`seo-nav-item ${activeSection === 'structure' ? 'active' : ''}`}
              onClick={() => setActiveSection('structure')}
            >
              Site Structure
            </button>
            <button
              className={`seo-nav-item ${activeSection === 'classification' ? 'active' : ''}`}
              onClick={() => setActiveSection('classification')}
            >
              Page Classification
            </button>
            <button
              className={`seo-nav-item ${activeSection === 'gaps' ? 'active' : ''}`}
              onClick={() => setActiveSection('gaps')}
            >
              Gap Analysis
            </button>
            <button
              className={`seo-nav-item ${activeSection.startsWith('benchmark') ? 'active' : ''}`}
              onClick={() => setActiveSection('benchmark')}
            >
              Benchmark
            </button>
            <button
              className={`seo-nav-item ${activeSection === 'recommendations' ? 'active' : ''}`}
              onClick={() => setActiveSection('recommendations')}
            >
              AI Recommendations
            </button>
            <button
              className={`seo-nav-item ${activeSection === 'schema-audit' ? 'active' : ''}`}
              onClick={() => setActiveSection('schema-audit')}
            >
              Schema Audit
            </button>
            <button
              className={`seo-nav-item ${activeSection === 'prompts' ? 'active' : ''}`}
              onClick={() => setActiveSection('prompts')}
            >
              Prompt Management
            </button>
            <button
              className={`seo-nav-item ${activeSection === 'api-data' ? 'active' : ''}`}
              onClick={() => setActiveSection('api-data')}
            >
              API Data
            </button>
          </div>

          {/* Content Panel */}
          <div className="seo-content-panel">
            {/* Overview Section */}
            <div className={`seo-content-section ${activeSection === 'overview' ? 'active' : ''}`}>
              <h2>Analysis Overview</h2>
              {renderOverview()}
            </div>

            {/* Site Structure Section */}
            <div className={`seo-content-section ${activeSection === 'structure' ? 'active' : ''}`}>
              <h2>Site Structure Analysis</h2>
              {renderStructure()}
            </div>

            {/* Classification Section */}
            <div className={`seo-content-section ${activeSection === 'classification' ? 'active' : ''}`}>
              <h2>Page Classification</h2>
              
              
              {renderClassification()}
            </div>

            {/* Gap Analysis Section */}
            <div className={`seo-content-section ${activeSection === 'gaps' ? 'active' : ''}`}>
              <h2>Gap Analysis</h2>
              {renderGapAnalysis()}
            </div>

            {/* Benchmark Section */}
            <div className={`seo-content-section ${activeSection.startsWith('benchmark') ? 'active' : ''}`}>
              <h2>Benchmark Analysis</h2>
              {renderBenchmark()}
            </div>

            {/* AI Recommendations Section */}
            <div className={`seo-content-section ${activeSection === 'recommendations' ? 'active' : ''}`}>
              <h2>AI-Powered Analysis & Recommendations</h2>
              
              {isAiAnalyzing ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <div className="seo-loading-spinner"></div>
                  <p style={{ marginTop: '20px', color: '#10b981' }}>
                    🤖 AI analysis in progress...
                  </p>
                  <p style={{ color: '#94a3b8', fontSize: '0.9rem', marginTop: '10px' }}>
                    Recommendations will be available once classification is complete
                  </p>
                </div>
              ) : (
                <>
                  {/* Show appropriate message based on data availability */}
                  {totalPages === 0 && Object.keys(fileStatus).length > 0 ? (
                    <div className="seo-alert seo-alert-warning">
                      <h4>No Pages to Analyze for Recommendations</h4>
                      <p>The scan completed but didn't find any pages, so AI recommendations cannot be generated.</p>
                      <p style={{ marginTop: '10px' }}>
                        Please verify the website URL is accessible and run a new scan to get recommendations.
                      </p>
                    </div>
                  ) : totalPages > 0 ? (
                    <div style={{ marginBottom: '20px', padding: '15px', background: '#0f172a', borderRadius: '8px' }}>
                      <p style={{ color: '#94a3b8', marginBottom: '10px' }}>
                        Classification Method Used: 
                          <span
                            style={{
                              fontWeight: 600,
                              color: classificationMethod.includes('AI-Powered') ? '#10b981' : '#f59e0b',
                              marginLeft: '10px'
                            }}
                          >
                            {classificationMethod}
                          </span>
                      </p>
                      <p style={{ color: '#cbd5e1' }}>
                        Analyzed {totalPages} pages and identified {pageTypes} different page types.
                        {classificationMethod.includes('AI-Powered') 
                          ? ' Claude AI provided intelligent classification based on content analysis.'
                          : classificationMethod.includes('failed') ?
                          ' Claude API failed, used enhanced rule-based classification instead.' :
                          ' Used pattern matching for classification. Add Claude API key for more accurate results.'}
                      </p>
                    </div>
                  ) : (
                    <div className="seo-alert seo-alert-info">
                      <p>No analysis data available. Please run a scan first to generate AI recommendations.</p>
                    </div>
                  )}
                  
                  {totalPages > 0 && (
                    <div style={{ marginBottom: '20px' }}>
                      <button 
                        className="seo-btn-primary" 
                        style={{ marginRight: '10px' }}
                        onClick={generateAIRecommendations}
                      >
                        Generate Full SEO Recommendations
                      </button>
                      <button 
                        className="seo-btn-secondary"
                        disabled={isAiAnalyzing}
                        onClick={async () => {
                        console.log('=== RE-CLASSIFY BUTTON CLICKED ===');
                        console.log('Pages state:', pages);
                        console.log('Pages keys:', Object.keys(pages));
                        console.log('Pages length:', Object.keys(pages).length);
                        console.log('Current execution:', currentExecution);
                        
                        if (Object.keys(pages).length > 0) {
                          // Show model selection modal
                          showModelSelectionModal(async (modelToUse) => {
                            console.log('🤖 Using selected model:', modelToUse);
                            
                          // Validate pages have proper data structure
                          const hasValidPages = Object.values(pages).some(page => page.url && page.title);
                          if (!hasValidPages) {
                            alert('Invalid page data detected. Please run a new scan to get fresh data.');
                            console.error('Pages state contains invalid data:', pages);
                            return;
                          }
                          
                          // Get current execution data for Claude API
                          let execForClaude = currentExecution;
                          if (!execForClaude?.executionId && selectedExecution && selectedExecution !== 'new') {
                            const execution = previousExecutions.find(ex => ex.id.toString() === selectedExecution);
                            if (execution) {
                              execForClaude = {
                                executionId: execution.id.toString(),
                                executionArn: '',
                                siteUrl: siteUrl,
                                industry: industry,
                                startTime: execution.execution_date,
                                s3Path: execution.s3_path,
                                completedAt: execution.execution_date
                              };
                            }
                          }
                          
                          console.log('Re-classify using execution:', execForClaude);
                          
                          // For re-classification, always call Claude API (don't load existing)
                            await performFullAnalysisWithPages(pages, execForClaude, modelToUse);
                            
                            // Update the persisted selected model after successful operation
                            setSelectedModel(modelToUse);
                          });
                        } else {
                          alert('No pages to classify. Please run a scan first.');
                        }
                      }}
                    >
                      {isAiAnalyzing ? '🤖 Analyzing...' : 'Re-classify Pages with AI'}
                    </button>
                    </div>
                  )}
                  
                  <div className="seo-alert seo-alert-info">
                    <strong>Note:</strong> AI recommendations require backend integration with Claude API. 
                    This feature will generate comprehensive SEO strategies based on your site analysis.
                  </div>
                </>
              )}
            </div>

            {/* Schema Audit Section */}
            <div className={`seo-content-section ${activeSection === 'schema-audit' ? 'active' : ''}`}>
              <h2>Schema Audit</h2>
              {renderSchemaAudit()}
            </div>

            {/* Prompt Management Section */}
            <div className={`seo-content-section ${activeSection === 'prompts' ? 'active' : ''}`}>
              <h2>Prompt Management</h2>
              {renderPromptManagement()}
            </div>

            {/* API Data Section */}
            <div className={`seo-content-section ${activeSection === 'api-data' ? 'active' : ''}`}>
              <h2>API Data Structure</h2>
              {renderApiData()}
            </div>
          </div>
        </div>
      </div>

      {/* Model Selection Modal */}
      {showModelModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#1e293b',
            padding: '30px',
            borderRadius: '12px',
            maxWidth: '500px',
            width: '90%',
            border: '1px solid #334155'
          }}>
            <h3 style={{ color: '#f8fafc', marginBottom: '20px', fontSize: '1.2rem' }}>
              🤖 Select AI Model
            </h3>
            
            <div style={{ marginBottom: '20px' }}>
              {availableModels.length > 0 ? (
                availableModels.map((model) => (
                  <div
                    key={model.id}
                    onClick={() => handleModelSelection(model.id)}
                    style={{
                      padding: '15px',
                      border: selectedModel === model.id ? '2px solid #3b82f6' : '1px solid #475569',
                      borderRadius: '8px',
                      marginBottom: '10px',
                      cursor: 'pointer',
                      backgroundColor: selectedModel === model.id ? 'rgba(59, 130, 246, 0.1)' : '#0f172a',
                      transition: 'all 0.2s'
                    }}
                  >
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'flex-start',
                      marginBottom: '8px'
                    }}>
                      <div>
                        <h4 style={{ 
                          color: selectedModel === model.id ? '#3b82f6' : '#f8fafc',
                          margin: 0,
                          fontSize: '1rem',
                          fontWeight: '600'
                        }}>
                          {model.name}
                        </h4>
                        <p style={{ 
                          color: '#94a3b8', 
                          margin: '4px 0 0 0',
                          fontSize: '0.85rem'
                        }}>
                          {model.description}
                        </p>
                      </div>
                      {model.cost_tier && (
                        <span style={{
                          padding: '2px 8px',
                          borderRadius: '12px',
                          fontSize: '0.7rem',
                          fontWeight: '500',
                          backgroundColor: model.cost_tier === 'premium' ? '#dc2626' : 
                                          model.cost_tier === 'standard' ? '#059669' : '#ca8a04',
                          color: 'white'
                        }}>
                          {model.cost_tier}
                        </span>
                      )}
                    </div>
                    
                    {model.recommended_for && model.recommended_for.length > 0 && (
                      <div style={{ 
                        display: 'flex', 
                        gap: '6px', 
                        flexWrap: 'wrap',
                        marginTop: '8px'
                      }}>
                        {model.recommended_for.map((use, index) => (
                          <span
                            key={index}
                            style={{
                              padding: '2px 6px',
                              borderRadius: '4px',
                              fontSize: '0.65rem',
                              backgroundColor: '#374151',
                              color: '#d1d5db'
                            }}
                          >
                            {use.replace('_', ' ')}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div style={{ color: '#94a3b8', textAlign: 'center', padding: '20px' }}>
                  Loading available models...
                </div>
              )}
            </div>
            
            <div style={{ 
              display: 'flex', 
              gap: '10px', 
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => setShowModelModal(false)}
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: '1px solid #475569',
                  backgroundColor: 'transparent',
                  color: '#94a3b8',
                  cursor: 'pointer',
                  fontSize: '0.9rem'
                }}
              >
                Cancel
              </button>
            {selectedModel && (
              <button
                onClick={handleContinueWithModel}
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: 'none',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  cursor: 'pointer',
                  fontWeight: '500',
                  fontSize: '0.9rem'
                }}
              >
                Continue
              </button>
            )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SeoAudit;
